import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import 'reactflow/dist/style.css';
import './style.css';
import store from '../../../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ReactFlow, {
  isEdge,
  Background,
  BackgroundVariant,
  MiniMap,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  Controls,
  useReactFlow,
  Panel,
  Position,
  ConnectionMode,
} from 'reactflow';
import CableEdge from './CableEdge';
import OrthogonalEdge from './OrthogonalEdge';

import CustomNode from "../customNode/CustomNode";
import BusNode from "../bus/BusNode";
import TextBoxNode from "../textbox/TextBoxNode";
import BoxNode from "../box/BoxNode";
import StraightLineNode from "../straightLine/StraightLineNode";
import SymbolDrawerNode from "../symbolDrawer/SymbolDrawerNode";
import { setScene } from '../../../../redux/reducers/global/inspector/inspectorSlice';
import { setHighlightHandles, setConnectionAssist, setConnectionAssistHover, clearConnectionAssist, clearSpawnRequest, setLabelPositionEditNodeId, clearSpawnPreview, clearAutoConnectRequest, resetSymbolDrawerTool, setBoxToolAnchor, resetBoxTool, setStraightLineToolAnchor, resetStraightLineTool, addTemplateBlock, selectTemplateBlock, setTemplateBlocks, updateTemplateBlock, updateTemplateBlocks, duplicateTemplateBlock, resetTemplateTextTool, resetTemplateImageTool, TEMPLATE_IMAGE_SCALE_MIN, TEMPLATE_IMAGE_SCALE_MAX, TEMPLATE_IMAGE_SCALE_DEFAULT, TEMPLATE_IMAGE_ALIGNMENTS, TEMPLATE_IMAGE_RATIO_OPTIONS, TEMPLATE_IMAGE_RATIO_DEFAULT, TEMPLATE_TEXT_WIDTH_DEFAULT, TEMPLATE_TEXT_DEFAULT, setLabelFontPx } from '../../../../redux/reducers/global/view/viewSlice';
import { addNode, clearSelection, clearNodeSelection, selectSingleNode, setCurrentNodeId, setNodesActionCreator, setNodeAttributes, setSelectedNodeIds, getNextSuggestedName, normalizeSuggestedName } from '../../../../redux/reducers/global/node/nodeSlice';
import { debounce } from 'lodash';
import { useNodeOperations } from '../../../../hooks/useNodeOperations';
import { useNodeSelectionActions } from '../../../../hooks/useNodeSelectionActions';
import { setTabPanel, search, setForceSearchResults, setSearchSelectedIndex } from '../../../../redux/reducers/global/editor/editorSlice';
import { EDITOR_TAB_PANEL_INSPECTOR, EDITOR_TAB_PANEL_ELEMENTS, VERTICAL_ORIENTATION } from '../../../../utilities/constants/editorConstants';
import { PAGE_SIZE_DIMENSIONS, PAGE_SIZE_A4 } from '../../../../utilities/constants/pageSizeConstants';
import { addHistoryState, performRedo, performUndo, setHistoryBaseline } from '../../../../redux/reducers/global/history/historySlice';
import { useEdgeOperations } from '../../../../hooks/useEdgeOperations';
import { selectSingleEdge, clearSelection as clearEdgeSelection, setSelectedEdgeIds, setEdgesActionCreator } from '../../../../redux/reducers/global/edge/edgeSlice';
import { resolveInspectorForNode, INSPECTOR_IDS } from '../../../../utilities/inspectors/registry';
import { createDiagram, setSelectedDiagram } from '../../../../redux/reducers/global/diagram/diagramSlice';
import useDiagramDataLoader from '../../../../utilities/useDiagramDataLoader';
import Symbols from '../../../../utilities/mappings/symbols/Symbols';
import PageGridOverlay from './PageGridOverlay';
import EdgeCrossPageOverlay from './EdgeCrossPageOverlay';
import { computeCustomHandleAnchors } from './customHandleAnchors';
import { enumerateBusHandles, getBusLayout, getBusHandleAnchor } from '../../../../utilities/busLayout';
import { getSymbolDrawerContentHeight, getUsedSymbolDrawerItems } from '../../../../utilities/symbolDrawerItems';

const normalizeHexColor = (value) => {
  if (typeof value !== 'string') return null;
  let hex = value.trim();
  if (!hex) return null;
  if (!hex.startsWith('#')) {
    hex = `#${hex}`;
  }
  if (/^#([0-9a-fA-F]{3})$/.test(hex)) {
    hex = `#${hex.slice(1)
      .split('')
      .map((ch) => ch + ch)
      .join('')}`;
  }
  if (!/^#([0-9a-fA-F]{6})$/.test(hex)) return null;
  return hex.toLowerCase();
};

const hexToRgb = (hex) => {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;
  const numeric = parseInt(normalized.slice(1), 16);
  return {
    r: (numeric >> 16) & 255,
    g: (numeric >> 8) & 255,
    b: numeric & 255,
  };
};

const isLightHex = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const linear = [rgb.r, rgb.g, rgb.b].map((channel) => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  return luminance > 0.6;
};

const COLOR_PRESETS = [
  { key: 'black', label: 'Negro', hex: '#111827' },
  { key: 'gray', label: 'Gris', hex: '#d3d3d3' },
  { key: 'green', label: 'Verde', hex: '#10B981' },
  { key: 'yellow', label: 'Amarillo', hex: '#FACC15' },
  { key: 'red', label: 'Rojo', hex: '#EF4444' },
  { key: 'violet', label: 'Violeta', hex: '#8B5CF6' },
].map((preset) => {
  const normalized = normalizeHexColor(preset.hex) || '#2cabef';
  return {
    ...preset,
    hex: normalized,
    normalized,
    isLight: isLightHex(normalized),
  };
});

const COLOR_HEX_LOOKUP = new Set(COLOR_PRESETS.map((preset) => preset.normalized));
const DEFAULT_SERVICE_COLOR = normalizeHexColor('#111827');
const OUT_OF_SERVICE_COLOR = COLOR_PRESETS.find((preset) => preset.key === 'gray')?.normalized || normalizeHexColor('#d3d3d3');

const normalizePaletteColor = (value) => {
  const normalized = normalizeHexColor(value);
  if (!normalized) return null;
  return COLOR_HEX_LOOKUP.has(normalized) ? normalized : null;
};

const getOutOfServiceDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const clampAlpha = (value, fallback = 1) => {
  if (value === undefined || value === null) {
    return fallback;
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return fallback;
  }
  if (numeric < 0) return 0;
  if (numeric > 1) return 1;
  return numeric;
};

const TEXTBOX_DEFAULT_BACKGROUND = '#ffffff';
const TEXTBOX_DEFAULT_TEXT = '#111827';

const BOX_SYMBOL_LABEL = 'Box';
const BOX_DEFAULT_WIDTH = 240;
const BOX_DEFAULT_HEIGHT = 160;
const BOX_DEFAULT_STROKE = 3;
const BOX_DEFAULT_STROKE_COLOR = '#111827';
const BOX_DEFAULT_BACKGROUND_COLOR = '#ffffff';
const BOX_DEFAULT_BACKGROUND_ALPHA = 1;
const BOX_DEFAULT_STROKE_ALPHA = 1;
const BOX_DEFAULT_COLUMNS = 1;
const BOX_DEFAULT_ROWS = 1;
const BOX_MAX_DIVISIONS = 24;
const BOX_MIN_SIZE = 1;
const BOX_TABLE_DEFAULT_FONT_SIZE = 10;
const STRAIGHT_LINE_SYMBOL_LABEL = 'StraightLine';
const STRAIGHT_LINE_DEFAULT_COLOR = '#111827';
const STRAIGHT_LINE_DEFAULT_STROKE = 3;
const STRAIGHT_LINE_PADDING = 12;
const STRAIGHT_LINE_MIN_LENGTH = 8;
const SYMBOL_DRAWER_SYMBOL_LABEL = 'SymbolDrawer';
const SYMBOL_DRAWER_DEFAULT_WIDTH = 320;
const EMPTY_BOX_TOOL_STATE = { active: false, anchor: null, preset: null };
const EMPTY_STRAIGHT_LINE_TOOL_STATE = {
  active: false,
  anchor: null,
  color: STRAIGHT_LINE_DEFAULT_COLOR,
  strokeWidth: STRAIGHT_LINE_DEFAULT_STROKE,
};
const EMPTY_SYMBOL_DRAWER_TOOL_STATE = { active: false };
const DESIGN_BOX_Z_INDEX = 0;
const DESIGN_REPORT_Z_INDEX = 4;
const DESIGN_TEXT_Z_INDEX = 7;
const TABLE_DEFAULT_CELL_WIDTH = 120;
const TABLE_DEFAULT_ROW_HEIGHT = 34;
const TABLE_DEFAULT_MIN_WIDTH = 360;
const TABLE_DEFAULT_MIN_HEIGHT = 220;
const TABLE_DEFAULT_MAX_WIDTH = 1200;
const TABLE_DEFAULT_MAX_HEIGHT = 900;

const alphaLabelToNumericString = (label) => {
  if (!label) return '1';
  const cleaned = String(label).replace(/[^a-z]/gi, '').toUpperCase();
  if (!cleaned) return '1';
  let value = 0;
  for (let i = 0; i < cleaned.length; i += 1) {
    value = value * 26 + (cleaned.charCodeAt(i) - 64);
  }
  return String(value || 1);
};

const replaceFooterPagePlaceholder = (text, pageLabel) => {
  if (typeof text !== 'string' || text.length === 0) return '';
  const numericLabel = alphaLabelToNumericString(pageLabel);
  return text.replace(new RegExp('\\{pagina\\}', 'gi'), numericLabel);
};

const isInspectorlessDesignNode = (node) => {
  const symbolLabel = node?.data?.symbolLabel;
  return node?.type === 'boxNode'
    || node?.type === 'straightLineNode'
    || node?.type === 'symbolDrawerNode'
    || symbolLabel === BOX_SYMBOL_LABEL
    || symbolLabel === STRAIGHT_LINE_SYMBOL_LABEL
    || symbolLabel === SYMBOL_DRAWER_SYMBOL_LABEL;
};

const stripHtml = (value = '') => value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

const adjustNoteHtmlForZoom = (html) => html;

const slugifySymbolLabel = (value) => {
  if (typeof value !== 'string') return null;
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return normalized || null;
};

const clampTemplateImageScaleValue = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return TEMPLATE_IMAGE_SCALE_DEFAULT;
  }
  return Math.min(
    TEMPLATE_IMAGE_SCALE_MAX,
    Math.max(TEMPLATE_IMAGE_SCALE_MIN, numeric),
  );
};

const normalizeTemplateImageAlignValue = (value) => (
  TEMPLATE_IMAGE_ALIGNMENTS.includes(value) ? value : 'center'
);

const normalizeTemplateImageRatioValue = (value) => (
  TEMPLATE_IMAGE_RATIO_OPTIONS.includes(value) ? value : TEMPLATE_IMAGE_RATIO_DEFAULT
);

const PX_PER_CM = 37.7952755906;
const TEMPLATE_MARGIN_INSET_CM = 1;
const TEMPLATE_BLEED_INSET_CM = 0.3;
const TEMPLATE_MARGIN_INSET_PX = PX_PER_CM * TEMPLATE_MARGIN_INSET_CM;
const TEMPLATE_BLEED_INSET_PX = PX_PER_CM * TEMPLATE_BLEED_INSET_CM;
const TEMPLATE_SAFE_AREA_INSET_PX = TEMPLATE_MARGIN_INSET_PX + TEMPLATE_BLEED_INSET_PX;
const TEMPLATE_ANCHOR_HORIZONTAL_PADDING = 32;
const TEMPLATE_ANCHOR_VERTICAL_PADDING = 24;
const TEMPLATE_ANCHOR_ROW_GAP = 12;
const TEMPLATE_ANCHOR_COLUMN_GAP = 12;
const TEMPLATE_ANCHOR_MIN_WIDTH = 260;
const TEMPLATE_ANCHOR_MIN_HEIGHT = 160;
const TEMPLATE_ANCHOR_MIN_COLUMN_WIDTH = 120;
const TEMPLATE_TEXT_BLOCK_BASE_WIDTH = 360;
const TEMPLATE_TEXT_BLOCK_BASE_HEIGHT = 140;
const TEMPLATE_MARGIN_ANCHOR_GAP_PX = 10;
const TEMPLATE_FOOTER_PADDING_PX = 12;
const SHAKE_REQUIRED_REVERSALS = 3;
const SHAKE_MIN_DELTA_PX = 24;
const SHAKE_REVERSAL_WINDOW_MS = 800;
const SHAKE_MAX_INTERVAL_MS = 250;
const SHAKE_MAX_TOTAL_MS = 650;
const SHAKE_MIN_TRAVEL_PX = SHAKE_MIN_DELTA_PX * 5;
const CONNECT_CLICK_SUPPRESSION_MS = 180;

const computeTemplateContentBounds = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      width: TEMPLATE_ANCHOR_MIN_WIDTH,
      height: TEMPLATE_ANCHOR_MIN_HEIGHT,
      innerWidth: Math.max(TEMPLATE_ANCHOR_MIN_WIDTH - TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2, 0),
    };
  }

  let largestImageWidth = 0;
  rows.forEach((row) => {
    row.blocks.forEach((block) => {
      if (block.type === 'image') {
        const width = clampTemplateImageScaleValue(block.imageScale);
        largestImageWidth = Math.max(largestImageWidth, width);
      }
    });
  });

  const textBlockWidth = Math.max(
    largestImageWidth || TEMPLATE_TEXT_BLOCK_BASE_WIDTH,
    TEMPLATE_ANCHOR_MIN_WIDTH - TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2,
  );

  let maxRowWidth = 0;
  let totalHeight = 0;
  rows.forEach((row) => {
    let rowWidth = 0;
    let rowHeight = 0;
    row.blocks.forEach((block) => {
      if (block.type === 'image') {
        const width = clampTemplateImageScaleValue(block.imageScale);
        const ratio = normalizeTemplateImageRatioValue(block.imageRatio);
        const height = ratio === 'rectangle' ? width * (3 / 4) : width;
        rowWidth += width;
        rowHeight = Math.max(rowHeight, height);
      } else {
        rowWidth += textBlockWidth;
        rowHeight = Math.max(rowHeight, TEMPLATE_TEXT_BLOCK_BASE_HEIGHT);
      }
    });
    if (row.blocks.length > 1) {
      rowWidth += (row.blocks.length - 1) * TEMPLATE_ANCHOR_COLUMN_GAP;
    }
    maxRowWidth = Math.max(maxRowWidth, rowWidth);
    totalHeight += rowHeight;
  });
  if (rows.length > 1) {
    totalHeight += (rows.length - 1) * TEMPLATE_ANCHOR_ROW_GAP;
  }

  const width = Math.max(maxRowWidth + TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2, TEMPLATE_ANCHOR_MIN_WIDTH);
  const height = Math.max(totalHeight + TEMPLATE_ANCHOR_VERTICAL_PADDING * 2, TEMPLATE_ANCHOR_MIN_HEIGHT);

  const innerWidth = Math.max(width - TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2, TEMPLATE_ANCHOR_MIN_WIDTH - TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2);

  return {
    width,
    height,
    innerWidth,
    textWidth: textBlockWidth,
  };
};

const TemplateAnchorOverlay = ({ transform, isSpacePressedRef, isSpacePressed, selectedBlockIds = [], onTextContextMenu, onImageContextMenu }) => {
  const dispatch = useDispatch();
  const templateSettings = useSelector((state) => state.view.templateSettings) || {};
  const pageOption = templateSettings.pageOption || 'none';
  const footerOption = templateSettings.footerOption || 'none';
  const footerText = templateSettings.footerText || '';
  const blocks = Array.isArray(templateSettings.blocks) ? templateSettings.blocks : [];
  const templateTextTool = useSelector((state) => state.view.templateTextTool) || { active: false };
  const templateImageTool = useSelector((state) => state.view.templateImageTool) || { active: false };
  const marginEnabled = !!templateSettings.marginEnabled;
  const selectedBlockId = templateSettings.selectedBlockId || null;
  const anchor = templateSettings.anchor;
  const [editingTextBlockId, setEditingTextBlockId] = useState(null);
  const pageSize = useSelector((state) => state.view.pageSize);
  const { width, height } = PAGE_SIZE_DIMENSIONS[pageSize] || PAGE_SIZE_DIMENSIONS[PAGE_SIZE_A4];
  const requestTemplateControlsFocus = useCallback(() => {
    if (typeof window !== 'undefined' && window?.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('template-anchor-overlay-focus'));
    }
  }, []);
  const templateBlockInteractionRef = useRef(null);
  const templateImageBlocks = useMemo(() => (
    blocks.filter((block) => block?.type === 'image')
  ), [blocks]);
  const templateTextBlocks = useMemo(() => (
    blocks.filter((block) => block?.type === 'text')
  ), [blocks]);
  const getTemplateBlockInteractionPosition = useCallback((block, pageBounds) => {
    if (!block) return null;
    if (block.type === 'text') {
      const textWidth = Number.isFinite(Number(block.textWidth)) ? Number(block.textWidth) : TEMPLATE_TEXT_WIDTH_DEFAULT;
      const fallbackX = 80;
      return {
        type: 'text',
        x: Number.isFinite(Number(block.textX)) ? Number(block.textX) : Math.min(fallbackX, Math.max((pageBounds?.width || width) - textWidth, 0)),
        y: Number.isFinite(Number(block.textY)) ? Number(block.textY) : 120,
      };
    }
    if (block.type === 'image') {
      const imageWidth = clampTemplateImageScaleValue(block.imageScale);
      return {
        type: 'image',
        x: Number.isFinite(Number(block.imageX)) ? Number(block.imageX) : Math.max((pageBounds?.width || width) - imageWidth - 80, 0),
        y: Number.isFinite(Number(block.imageY)) ? Number(block.imageY) : 80,
      };
    }
    return null;
  }, [width]);
  const selectedTemplateBlock = useMemo(
    () => blocks.find((block) => block?.id === selectedBlockId) || null,
    [blocks, selectedBlockId],
  );
  const templateLayoutBlocks = useMemo(() => (
    blocks.filter((block) => block?.type !== 'image' && block?.type !== 'text')
  ), [blocks]);

  const templateRows = useMemo(() => {
    if (!Array.isArray(templateLayoutBlocks) || templateLayoutBlocks.length === 0) {
      return [];
    }
    const rows = [];
    const rowMap = new Map();
    templateLayoutBlocks.forEach((block, index) => {
      if (!block) return;
      const rowId = typeof block.rowId === 'string' ? block.rowId : block.id;
      let row = rowMap.get(rowId);
      if (!row) {
        row = {
          id: rowId,
          columns: block.rowColumns === 2 ? 2 : 1,
          blocks: [],
          firstIndex: index,
        };
        rowMap.set(rowId, row);
        rows.push(row);
      }
      row.blocks.push(block);
      if (block.rowColumns === 2) {
        row.columns = 2;
      }
      if (index < row.firstIndex) {
        row.firstIndex = index;
      }
    });
    rows.sort((a, b) => a.firstIndex - b.firstIndex);
    return rows.map((row) => ({
      ...row,
      blocks: row.blocks
        .slice()
        .sort((a, b) => (a?.rowIndex ?? 0) - (b?.rowIndex ?? 0)),
    }));
  }, [templateLayoutBlocks]);
  const anchorItemCount = templateRows.reduce((sum, row) => sum + row.blocks.length, 0);
  const hasTwoColumnRows = templateRows.some((row) => row.columns === 2);
  const anchorContentBounds = useMemo(
    () => computeTemplateContentBounds(templateRows),
    [templateRows],
  );

  const [container, setContainer] = useState(null);
  const [templateTextToolCursor, setTemplateTextToolCursor] = useState(null);
  const [templateImageToolCursor, setTemplateImageToolCursor] = useState(null);
  const nodes = useSelector((state) => state.node.nodes) || [];

  useEffect(() => {
    const elem = document.querySelector('.react-flow__viewport');
    if (elem) {
      setContainer(elem);
    }
  }, []);

  const shouldRenderAnchor = pageOption !== 'none' && !!anchor && anchorItemCount > 0;
  const hasFooterSelection = footerOption === 'page-names' || (footerOption === 'custom-text' && footerText.length > 0);
  const shouldRenderFooter = hasFooterSelection;

  const { x = 0, y = 0, zoom = 1 } = transform || {};

  useEffect(() => {
    if (!editingTextBlockId || typeof document === 'undefined') return;
    const editor = document.querySelector(`[data-template-text-editor="${editingTextBlockId}"]`);
    if (!editor) return;
    if (editor.dataset.templateTextEmpty === 'true') {
      editor.innerHTML = '';
    }
    editor.focus();
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    const selection = window.getSelection?.();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [editingTextBlockId]);

  useEffect(() => {
    if (!templateTextTool?.active && !templateImageTool?.active) {
      setTemplateTextToolCursor(null);
      setTemplateImageToolCursor(null);
      return;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        dispatch(resetTemplateTextTool());
        dispatch(resetTemplateImageTool());
        setTemplateTextToolCursor(null);
        setTemplateImageToolCursor(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [dispatch, templateImageTool?.active, templateTextTool?.active]);

  useEffect(() => {
    if (!templateImageTool?.active || typeof window === 'undefined') {
      return undefined;
    }
    const handlePointerMove = (event) => {
      setTemplateImageToolCursor((current) => ({
        ...(current || {}),
        screenX: event.clientX,
        screenY: event.clientY,
      }));
    };
    window.addEventListener('pointermove', handlePointerMove, true);
    return () => window.removeEventListener('pointermove', handlePointerMove, true);
  }, [templateImageTool?.active]);

  useEffect(() => {
    if (!selectedTemplateBlock || selectedTemplateBlock.type !== 'text' || typeof document === 'undefined') {
      return undefined;
    }
    const handlePointerDown = (event) => {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') {
        return;
      }
      if (target.closest('.template-floating-text')) {
        return;
      }
      if (
        target.closest('.template-builder-panel')
        || target.closest('#menu-bar-overlay-root')
        || target.closest('.diagram-node-context-menu')
        || target.closest('.react-flow__pane')
      ) {
        return;
      }
      dispatch(selectTemplateBlock(null));
      setEditingTextBlockId(null);
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [dispatch, selectedTemplateBlock]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const interaction = templateBlockInteractionRef.current;
      if (!interaction) return;
      event.preventDefault();
      const deltaX = (event.clientX - interaction.startClientX) / (zoom || 1);
      const deltaY = (event.clientY - interaction.startClientY) / (zoom || 1);
      if (
        interaction.mode === 'move'
        && Array.isArray(interaction.blockOrigins)
        && interaction.blockOrigins.length > 0
      ) {
        const updates = interaction.blockOrigins.map((origin) => {
          if (origin.type === 'text') {
            return {
              id: origin.id,
              changes: {
                textX: Math.max(0, origin.x + deltaX),
                textY: Math.max(0, origin.y + deltaY),
              },
            };
          }
          if (origin.type === 'image') {
            return {
              id: origin.id,
              changes: {
                imageX: Math.max(0, origin.x + deltaX),
                imageY: Math.max(0, origin.y + deltaY),
              },
            };
          }
          return null;
        }).filter(Boolean);
        if (updates.length > 0) {
          dispatch(updateTemplateBlocks(updates));
        }
        return;
      }
      if (interaction.kind === 'image' && interaction.mode === 'move') {
        dispatch(updateTemplateBlock({
          id: interaction.blockId,
          changes: {
            imageX: Math.max(0, interaction.startX + deltaX),
            imageY: Math.max(0, interaction.startY + deltaY),
          },
        }));
        return;
      }
      if (interaction.kind === 'image' && interaction.mode === 'resize') {
        const nextWidth = Math.min(
          TEMPLATE_IMAGE_SCALE_MAX,
          Math.max(TEMPLATE_IMAGE_SCALE_MIN, interaction.startWidth + Math.max(deltaX, deltaY)),
        );
        dispatch(updateTemplateBlock({
          id: interaction.blockId,
          changes: { imageScale: nextWidth },
        }));
        return;
      }
      if (interaction.kind === 'text' && interaction.mode === 'move') {
        dispatch(updateTemplateBlock({
          id: interaction.blockId,
          changes: {
            textX: Math.max(0, interaction.startX + deltaX),
            textY: Math.max(0, interaction.startY + deltaY),
          },
        }));
        return;
      }
      if (interaction.kind === 'text' && interaction.mode === 'resize') {
        const nextWidth = Math.max(80, interaction.startWidth + deltaX);
        dispatch(updateTemplateBlock({
          id: interaction.blockId,
          changes: { textWidth: nextWidth },
        }));
      }
    };
    const handlePointerUp = () => {
      templateBlockInteractionRef.current = null;
    };
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [dispatch, zoom]);

  const beginTemplateImageInteraction = useCallback((event, block, pageBounds, mode) => {
    if (isSpacePressedRef?.current) {
      event.preventDefault();
      event.stopPropagation();
      dispatch(selectTemplateBlock(null));
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    requestTemplateControlsFocus();
    const imageWidth = clampTemplateImageScaleValue(block.imageScale);
    const imageRatio = normalizeTemplateImageRatioValue(block.imageRatio);
    const imageHeight = imageRatio === 'rectangle' ? imageWidth * (3 / 4) : imageWidth;
    const fallbackX = Math.max(pageBounds.width - imageWidth - 80, 0);
    const fallbackY = 80;
    const selectedIdSet = new Set(Array.isArray(selectedBlockIds) ? selectedBlockIds : []);
    const selectedMoveBlocks = mode === 'move' && selectedIdSet.has(block.id)
      ? blocks.filter((item) => item?.id && selectedIdSet.has(item.id) && (item.type === 'text' || item.type === 'image'))
      : [];
    dispatch(selectTemplateBlock(block.id));
    templateBlockInteractionRef.current = {
      kind: 'image',
      mode,
      blockId: block.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: Number.isFinite(Number(block.imageX)) ? Number(block.imageX) : fallbackX,
      startY: Number.isFinite(Number(block.imageY)) ? Number(block.imageY) : fallbackY,
      startWidth: imageWidth,
      startHeight: imageHeight,
      blockOrigins: selectedMoveBlocks
        .map((item) => {
          const position = getTemplateBlockInteractionPosition(item, pageBounds);
          return position ? { id: item.id, ...position } : null;
        })
        .filter(Boolean),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [blocks, dispatch, getTemplateBlockInteractionPosition, isSpacePressedRef, requestTemplateControlsFocus, selectedBlockIds]);

  const beginTemplateTextInteraction = useCallback((event, block, pageBounds, mode) => {
    if (isSpacePressedRef?.current) return;
    event.preventDefault();
    event.stopPropagation();
    requestTemplateControlsFocus();
    const textWidth = Number.isFinite(Number(block.textWidth)) ? Number(block.textWidth) : TEMPLATE_TEXT_WIDTH_DEFAULT;
    const fallbackX = 80;
    const fallbackY = 120;
    const selectedIdSet = new Set(Array.isArray(selectedBlockIds) ? selectedBlockIds : []);
    const selectedMoveBlocks = mode === 'move' && selectedIdSet.has(block.id)
      ? blocks.filter((item) => item?.id && selectedIdSet.has(item.id) && (item.type === 'text' || item.type === 'image'))
      : [];
    dispatch(selectTemplateBlock(block.id));
    templateBlockInteractionRef.current = {
      kind: 'text',
      mode,
      blockId: block.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: Number.isFinite(Number(block.textX)) ? Number(block.textX) : Math.min(fallbackX, Math.max(pageBounds.width - textWidth, 0)),
      startY: Number.isFinite(Number(block.textY)) ? Number(block.textY) : fallbackY,
      startWidth: textWidth,
      blockOrigins: selectedMoveBlocks
        .map((item) => {
          const position = getTemplateBlockInteractionPosition(item, pageBounds);
          return position ? { id: item.id, ...position } : null;
        })
        .filter(Boolean),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [blocks, dispatch, getTemplateBlockInteractionPosition, isSpacePressedRef, requestTemplateControlsFocus, selectedBlockIds]);

  const handleTemplateTextContextMenu = useCallback((event, block) => {
    event.preventDefault();
    event.stopPropagation();
    requestTemplateControlsFocus();
    dispatch(selectTemplateBlock(block.id));
    onTextContextMenu?.(event, block);
  }, [dispatch, onTextContextMenu, requestTemplateControlsFocus]);

  const handleTemplateImageContextMenu = useCallback((event, block) => {
    event.preventDefault();
    event.stopPropagation();
    requestTemplateControlsFocus();
    dispatch(selectTemplateBlock(block.id));
    onImageContextMenu?.(event, block);
  }, [dispatch, onImageContextMenu, requestTemplateControlsFocus]);

  const handleTemplateTextDoubleClick = useCallback((event, block) => {
    event.preventDefault();
    event.stopPropagation();
    requestTemplateControlsFocus();
    dispatch(selectTemplateBlock(block.id));
    setEditingTextBlockId(block.id);
  }, [dispatch, requestTemplateControlsFocus]);

  const handleTemplateTextBlur = useCallback((event, block) => {
    const nextHtml = event.currentTarget.innerHTML
      .replace(/\u200b/g, '')
      .replace(/<(strong|b)>\s*<\/\1>/gi, '');
    dispatch(updateTemplateBlock({
      id: block.id,
      changes: { text: stripHtml(nextHtml).length > 0 ? nextHtml : '' },
    }));
    setEditingTextBlockId(null);
  }, [dispatch]);

  const tilesFromNodes = useMemo(() => {
    const tiles = new Set();
    nodes.forEach((node) => {
      const nx = node?.position?.x ?? 0;
      const ny = node?.position?.y ?? 0;
      const col = Math.floor(nx / width);
      const row = Math.floor(ny / height);
      tiles.add(`${col},${row}`);
    });
    if (tiles.size === 0) {
      tiles.add('0,0');
    }
    return Array.from(tiles).map((tile) => {
      const [col, row] = tile.split(',').map(Number);
      return { col, row };
    });
  }, [nodes, width, height]);

  const anchorTileOffsets = useMemo(() => {
    if (pageOption === 'all-pages') {
      return tilesFromNodes;
    }
    return [{ col: 0, row: 0 }];
  }, [pageOption, tilesFromNodes]);

  const footerTileOffsets = useMemo(() => {
    if (hasFooterSelection) {
      return tilesFromNodes;
    }
    return anchorTileOffsets;
  }, [hasFooterSelection, tilesFromNodes, anchorTileOffsets]);

  const anchorTileKeys = useMemo(() => new Set(anchorTileOffsets.map((tile) => `${tile.col},${tile.row}`)), [anchorTileOffsets]);
  const footerTileKeys = useMemo(() => new Set(footerTileOffsets.map((tile) => `${tile.col},${tile.row}`)), [footerTileOffsets]);

  const orderedTiles = useMemo(() => {
    const combined = new Map();
    [...anchorTileOffsets, ...footerTileOffsets].forEach((tile) => {
      const key = `${tile.col},${tile.row}`;
      if (!combined.has(key)) {
        combined.set(key, tile);
      }
    });
    return Array.from(combined.values()).sort((a, b) => {
      if (a.row !== b.row) return a.row - b.row;
      return a.col - b.col;
    });
  }, [anchorTileOffsets, footerTileOffsets]);

  const toAlpha = (n) => {
    const A = 'A'.charCodeAt(0);
    let s = '';
    let value = Math.max(0, n);
    do {
      s = String.fromCharCode(A + (value % 26)) + s;
      value = Math.floor(value / 26) - 1;
    } while (value >= 0);
    return s;
  };

  const moveFooterToLeft = false;
  const marginInsetValue = marginEnabled ? TEMPLATE_SAFE_AREA_INSET_PX : 0;
  const marginAnchorGap = marginEnabled ? TEMPLATE_MARGIN_ANCHOR_GAP_PX : 0;
  const totalMarginPadding = marginInsetValue * 2 + marginAnchorGap * 2;
  const anchorAvailableWidth = Math.max(width - totalMarginPadding, 0);
  const anchorAvailableHeight = Math.max(height - totalMarginPadding, 0);
  const resolvedAnchorWidth = Math.max(
    Math.min(anchorContentBounds.width, anchorAvailableWidth || anchorContentBounds.width),
    TEMPLATE_ANCHOR_MIN_WIDTH,
  );
  const resolvedAnchorHeight = Math.max(
    Math.min(anchorContentBounds.height, anchorAvailableHeight || anchorContentBounds.height),
    TEMPLATE_ANCHOR_MIN_HEIGHT,
  );
  const anchorInnerWidth = Math.max(resolvedAnchorWidth - TEMPLATE_ANCHOR_HORIZONTAL_PADDING * 2, 0);
  const preferredTextBlockWidth = Math.max(
    Math.min(
      anchorContentBounds.textWidth || TEMPLATE_TEXT_BLOCK_BASE_WIDTH,
      anchorInnerWidth || (anchorContentBounds.textWidth || TEMPLATE_TEXT_BLOCK_BASE_WIDTH),
    ),
    80,
  );

  const overlays = orderedTiles.map((tile, idx) => {
    const tileLeft = tile.col * width;
    const tileTop = tile.row * height;
    const leftAlignedLeft = tileLeft + marginInsetValue + marginAnchorGap;
    const rightAlignedLeft = tileLeft + width - marginInsetValue - marginAnchorGap - resolvedAnchorWidth;
    const anchorLeft = anchor === 'right'
      ? Math.max(leftAlignedLeft, rightAlignedLeft)
      : leftAlignedLeft;
    const anchorTop = tileTop + marginInsetValue + marginAnchorGap;
    const left = anchorLeft;
    const top = anchorTop;
    const footerInset = marginInsetValue + marginAnchorGap + TEMPLATE_FOOTER_PADDING_PX;
    const footerMaxWidth = Math.max(width - footerInset * 2, 0);
    const footerLeft = moveFooterToLeft
      ? tileLeft + footerInset
      : tileLeft + width - footerInset;
    const footerTop = tileTop + height - footerInset;
    const footerTransform = moveFooterToLeft ? 'translate(0, -100%)' : 'translate(-100%, -100%)';
    const footerAlign = moveFooterToLeft ? 'left' : 'right';

    const pageLabel = toAlpha(idx);
    const key = `${tile.col},${tile.row}`;
    const showAnchor = anchorTileKeys.has(key);
    const showFooter = footerTileKeys.has(key);
    const displayPageNumber = alphaLabelToNumericString(pageLabel);
    const footerLabel = footerOption === 'page-names'
      ? `Página ${displayPageNumber}`
      : replaceFooterPagePlaceholder(footerText, pageLabel);

    const anchorStyle = {
      left,
      top,
      width: resolvedAnchorWidth,
      height: resolvedAnchorHeight,
    };

    return {
      key,
      showAnchor,
      showFooter,
      anchorStyle,
      innerWidth: anchorInnerWidth,
      footerStyle: {
        left: footerLeft,
        top: footerTop,
        transform: footerTransform,
        textAlign: footerAlign,
        maxWidth: footerMaxWidth,
      },
      footerLabel,
      pageBounds: {
        left: tileLeft,
        top: tileTop,
        width,
        height,
      },
    };
  });

  const shouldRenderTemplateImages = pageOption !== 'none' && templateImageBlocks.length > 0;
  const shouldRenderTemplateTexts = pageOption !== 'none' && templateTextBlocks.length > 0;
  const shouldRenderTextPlacement = pageOption !== 'none' && !!templateTextTool?.active;
  const shouldRenderImagePlacement = pageOption !== 'none' && !!templateImageTool?.active;

  const resolveTemplateTextPlacementPoint = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const effectiveZoom = Number.isFinite(Number(zoom)) && Number(zoom) > 0 ? Number(zoom) : 1;
    return {
      x: Math.max(0, (event.clientX - rect.left) / effectiveZoom),
      y: Math.max(0, (event.clientY - rect.top) / effectiveZoom),
    };
  }, [zoom]);

  const handleTemplateTextPlacementMove = useCallback((event, item) => {
    const point = resolveTemplateTextPlacementPoint(event);
    setTemplateTextToolCursor({
      x: point.x,
      y: point.y,
      pageKey: item.key,
    });
  }, [resolveTemplateTextPlacementPoint]);

  const handleTemplateTextPlacementLeave = useCallback(() => {
    setTemplateTextToolCursor(null);
  }, []);

  const handleTemplateTextPlacement = useCallback((event, pageBounds) => {
    if (!templateTextTool?.active) return;
    event.preventDefault();
    event.stopPropagation();
    requestTemplateControlsFocus();
    const point = resolveTemplateTextPlacementPoint(event);
    const nextX = Math.max(0, Math.min(point.x, Math.max(pageBounds.width - TEMPLATE_TEXT_WIDTH_DEFAULT, 0)));
    const nextY = Math.max(0, Math.min(point.y, pageBounds.height - 40));
    dispatch(addTemplateBlock({
      type: 'text',
      rowColumns: 1,
      rowIndex: 0,
      text: TEMPLATE_TEXT_DEFAULT,
      textX: nextX,
      textY: nextY,
      textWidth: TEMPLATE_TEXT_WIDTH_DEFAULT,
    }));
    dispatch(resetTemplateTextTool());
    setTemplateTextToolCursor(null);
  }, [dispatch, requestTemplateControlsFocus, resolveTemplateTextPlacementPoint, templateTextTool?.active]);

  const handleTemplateImagePlacementMove = useCallback((event, item) => {
    const point = resolveTemplateTextPlacementPoint(event);
    setTemplateImageToolCursor({
      x: point.x,
      y: point.y,
      screenX: event.clientX,
      screenY: event.clientY,
      pageKey: item.key,
    });
  }, [resolveTemplateTextPlacementPoint]);

  const handleTemplateImagePlacementLeave = useCallback(() => {
    setTemplateImageToolCursor((current) => (
      current
        ? { screenX: current.screenX, screenY: current.screenY }
        : null
    ));
  }, []);

  const handleTemplateImagePlacement = useCallback((event, pageBounds) => {
    if (!templateImageTool?.active) return;
    event.preventDefault();
    event.stopPropagation();
    requestTemplateControlsFocus();
    const pendingImage = templateImageTool?.image || {
      src: '/assets/lorem-img.jpg',
      name: 'lorem-img.jpg',
    };
    const point = resolveTemplateTextPlacementPoint(event);
    const blockId = `tmpl_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
    const imageWidth = TEMPLATE_IMAGE_SCALE_DEFAULT;
    const imageHeight = TEMPLATE_IMAGE_RATIO_DEFAULT === 'rectangle'
      ? imageWidth * (3 / 4)
      : imageWidth;
    const nextX = Math.max(0, Math.min(point.x, Math.max(pageBounds.width - imageWidth, 0)));
    const nextY = Math.max(0, Math.min(point.y, Math.max(pageBounds.height - imageHeight, 0)));
    dispatch(addTemplateBlock({
      id: blockId,
      type: 'image',
      rowColumns: 1,
      rowIndex: 0,
      image: pendingImage,
      imageScale: imageWidth,
      imageRatio: TEMPLATE_IMAGE_RATIO_DEFAULT,
      imageX: nextX,
      imageY: nextY,
    }));
    dispatch(resetTemplateImageTool());
    setTemplateImageToolCursor(null);
  }, [dispatch, requestTemplateControlsFocus, resolveTemplateTextPlacementPoint, templateImageTool?.active, templateImageTool?.image]);

  const imagePlacementCursorPortal =
    shouldRenderImagePlacement
    && templateImageToolCursor
    && Number.isFinite(Number(templateImageToolCursor.screenX))
    && Number.isFinite(Number(templateImageToolCursor.screenY))
      ? createPortal(
        <span
          className="template-image-placement-cursor template-image-placement-cursor--fixed"
          style={{
            left: templateImageToolCursor.screenX,
            top: templateImageToolCursor.screenY,
          }}
          aria-hidden="true"
        >
          <img
            className="template-image-placement-cursor__icon"
            src="/assets/ico-createimage.svg"
            alt=""
            draggable="false"
          />
        </span>,
        document.body,
      )
      : null;

  if (!container || (!shouldRenderAnchor && !shouldRenderTemplateImages && !shouldRenderTemplateTexts && !shouldRenderFooter && !marginEnabled && !shouldRenderTextPlacement && !shouldRenderImagePlacement)) return null;

  return createPortal(
    <>
      {marginEnabled
        ? overlays.map((item) => {
            const borderInset = TEMPLATE_MARGIN_INSET_PX;
            const adjustedWidth = Math.max(item.pageBounds.width - borderInset * 2, 0);
            const adjustedHeight = Math.max(item.pageBounds.height - borderInset * 2, 0);
            return (
              <div
                key={`template-margin-${item.key}`}
                className="template-margin-overlay ux-only export-visible"
                style={{
                  left: item.pageBounds.left + borderInset,
                  top: item.pageBounds.top + borderInset,
                  width: adjustedWidth,
                  height: adjustedHeight,
                }}
              />
            );
          })
        : null}
      {shouldRenderAnchor
        ? overlays.filter((item) => item.showAnchor).map((item) => {
            const hasContent = anchorItemCount > 0;
            const anchorContentClassNames = ['template-anchor-content'];
            anchorContentClassNames.push(hasTwoColumnRows ? 'has-two-columns' : 'has-single-column');
            if (!hasContent) {
              anchorContentClassNames.push('is-empty');
            }
            return (
              <div
                key={`template-anchor-${item.key}`}
                className="template-anchor-overlay ux-only export-visible"
                style={item.anchorStyle}
                onMouseDownCapture={requestTemplateControlsFocus}
                onTouchStartCapture={requestTemplateControlsFocus}
              >
                <div className={anchorContentClassNames.join(' ')}>
                  {hasContent ? (
                    <div className={`template-anchor-grid template-anchor-grid--${hasTwoColumnRows ? 'two' : 'one'}`}>
                      {templateRows.map((row, rowIndex) => (
                        <div key={`${item.key}-row-${row.id}-${rowIndex}`} className="template-anchor-row">
                          {(() => {
                            const rowColumnCount = row.columns === 2 ? 2 : 1;
                            const fallbackInnerWidth = item.innerWidth && item.innerWidth > 0
                              ? item.innerWidth
                              : preferredTextBlockWidth * rowColumnCount;
                            const rowGap = rowColumnCount > 1 ? TEMPLATE_ANCHOR_COLUMN_GAP : 0;
                            const rowAvailableWidth = Math.max(fallbackInnerWidth - rowGap, 0);
                            const rowColumnWidth = rowColumnCount > 1 && rowAvailableWidth > 0
                              ? Math.max(rowAvailableWidth / rowColumnCount, TEMPLATE_ANCHOR_MIN_COLUMN_WIDTH)
                              : null;
                            return row.blocks.map((block) => {
                              const isSelected = block.id === selectedBlockId;
                              const handleSelect = (event) => {
                                event.stopPropagation();
                                dispatch(selectTemplateBlock(block.id));
                              };
                              if (block.type === 'image') {
                                const imageAlign = normalizeTemplateImageAlignValue(block.imageAlign);
                                const rawImageWidth = clampTemplateImageScaleValue(block.imageScale);
                              const imageRatio = normalizeTemplateImageRatioValue(block.imageRatio);
                              const aspectRatio = imageRatio === 'rectangle' ? '16 / 9' : '1 / 1';
                                const constrainedColumnWidth = rowColumnWidth || (item.innerWidth > 0 ? item.innerWidth : rawImageWidth);
                                const imageWidthPx = Math.min(rawImageWidth, constrainedColumnWidth);
                                const buttonStyle = rowColumnWidth
                                  ? { width: `${rowColumnWidth}px`, maxWidth: `${rowColumnWidth}px` }
                                  : undefined;
                                return (
                                  <button
                                    type="button"
                                    key={block.id}
                                    className={`template-anchor-block template-anchor-block--image template-anchor-block--align-${imageAlign} template-anchor-block--ratio-${imageRatio}${isSelected ? ' is-selected' : ''}`}
                                    onClick={handleSelect}
                                    style={buttonStyle}
                                  >
                                    {block.image?.src ? (
                                      <img
                                        src={block.image.src}
                                        alt="Imagen de plantilla"
                                      style={{
                                        width: `${imageWidthPx}px`,
                                        maxWidth: '100%',
                                        aspectRatio,
                                      }}
                                    />
                                  ) : (
                                    <span className="template-anchor-block-placeholder">Imagen</span>
                                  )}
                                </button>
                              );
                            }
                                const textContent = stripHtml(block.text || '').length > 0
                                  ? block.text
                                  : 'Editar texto';
                                const textWidthPx = rowColumnWidth || preferredTextBlockWidth;
                                const textStyle = rowColumnWidth
                                  ? { width: `${textWidthPx}px`, maxWidth: `${textWidthPx}px` }
                                  : { width: `${textWidthPx}px`, maxWidth: '100%' };
                                return (
                                  <button
                                    type="button"
                                    key={block.id}
                                    className={`template-anchor-block template-anchor-block--text${isSelected ? ' is-selected' : ''}`}
                                    onClick={handleSelect}
                                    style={textStyle}
                                  >
                                    <span
                                      className="template-anchor-block-text"
                                      dangerouslySetInnerHTML={{ __html: adjustNoteHtmlForZoom(textContent) }}
                                    />
                                  </button>
                                );
                              });
                          })()}
                          {row.columns === 2 && row.blocks.length === 1 ? (
                            <div className="template-anchor-block template-anchor-block--ghost" aria-hidden="true" />
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="template-anchor-placeholder">
                      Agrega bloques desde el creador de plantillas
                    </div>
                  )}
                </div>
              </div>
            );
          })
        : null}
      {shouldRenderTextPlacement
        ? overlays.filter((item) => item.showAnchor).map((item) => {
            const showCursor = templateTextToolCursor?.pageKey === item.key;
            return (
              <div
                key={`template-text-placement-${item.key}`}
                className="template-text-placement-layer ux-only"
                style={{
                  left: item.pageBounds.left,
                  top: item.pageBounds.top,
                  width: item.pageBounds.width,
                  height: item.pageBounds.height,
                }}
                onPointerMove={(event) => handleTemplateTextPlacementMove(event, item)}
                onPointerLeave={handleTemplateTextPlacementLeave}
                onPointerDown={(event) => handleTemplateTextPlacement(event, item.pageBounds)}
              >
                {showCursor ? (
                  <span
                    className="template-text-placement-cursor"
                    style={{
                      left: templateTextToolCursor.x,
                      top: templateTextToolCursor.y,
                    }}
                    aria-hidden="true"
                  >
                    T
                  </span>
                ) : null}
              </div>
            );
          })
        : null}
      {shouldRenderImagePlacement
        ? overlays.filter((item) => item.showAnchor).map((item) => {
            return (
              <div
                key={`template-image-placement-${item.key}`}
                className="template-text-placement-layer template-image-placement-layer ux-only"
                style={{
                  left: item.pageBounds.left,
                  top: item.pageBounds.top,
                  width: item.pageBounds.width,
                  height: item.pageBounds.height,
                }}
                onPointerMove={(event) => handleTemplateImagePlacementMove(event, item)}
                onPointerLeave={handleTemplateImagePlacementLeave}
                onPointerDown={(event) => handleTemplateImagePlacement(event, item.pageBounds)}
              />
            );
          })
        : null}
      {imagePlacementCursorPortal}
      {shouldRenderTemplateImages
        ? overlays.filter((item) => item.showAnchor).flatMap((item) => (
          templateImageBlocks.map((block) => {
            const isSelected = block.id === selectedBlockId || selectedBlockIds.includes(block.id);
            const imageWidth = clampTemplateImageScaleValue(block.imageScale);
            const imageRatio = normalizeTemplateImageRatioValue(block.imageRatio);
            const imageHeight = imageRatio === 'rectangle' ? imageWidth * (3 / 4) : imageWidth;
            const fallbackX = Math.max(item.pageBounds.width - imageWidth - 80, 0);
            const fallbackY = 80;
            const imageX = Number.isFinite(Number(block.imageX)) ? Number(block.imageX) : fallbackX;
            const imageY = Number.isFinite(Number(block.imageY)) ? Number(block.imageY) : fallbackY;
            return (
              <div
                key={`template-image-${item.key}-${block.id}`}
                className={`template-floating-image${isSelected ? ' is-selected' : ''}${isSpacePressed ? ' is-space-disabled' : ''}`}
                data-template-block-id={block.id}
                style={{
                  left: item.pageBounds.left + imageX,
                  top: item.pageBounds.top + imageY,
                  width: imageWidth,
                  height: imageHeight,
                }}
                onMouseDownCapture={requestTemplateControlsFocus}
                onTouchStartCapture={requestTemplateControlsFocus}
                onPointerDown={(event) => beginTemplateImageInteraction(event, block, item.pageBounds, 'move')}
                onContextMenu={(event) => handleTemplateImageContextMenu(event, block)}
              >
                {block.image?.src ? (
                  <img src={block.image.src} alt="Imagen de plantilla" draggable={false} />
                ) : null}
                {isSelected ? (
                  <button
                    type="button"
                    className="template-floating-image__resize"
                    aria-label="Cambiar tamaño de imagen"
                    onPointerDown={(event) => beginTemplateImageInteraction(event, block, item.pageBounds, 'resize')}
                  />
                ) : null}
              </div>
            );
          })
        ))
        : null}
      {shouldRenderTemplateTexts
        ? overlays.filter((item) => item.showAnchor).flatMap((item) => (
          templateTextBlocks.map((block) => {
            const isSelected = block.id === selectedBlockId || selectedBlockIds.includes(block.id);
            const isEditing = editingTextBlockId === block.id;
            const textWidth = Number.isFinite(Number(block.textWidth)) ? Number(block.textWidth) : TEMPLATE_TEXT_WIDTH_DEFAULT;
            const fallbackX = 80;
            const fallbackY = 120;
            const textX = Number.isFinite(Number(block.textX)) ? Number(block.textX) : fallbackX;
            const textY = Number.isFinite(Number(block.textY)) ? Number(block.textY) : fallbackY;
            const hasTextContent = stripHtml(block.text || '').length > 0;
            const textContent = hasTextContent
              ? block.text
              : 'Editar texto';
            return (
              <div
                key={`template-text-${item.key}-${block.id}`}
                className={`template-floating-text${isSelected ? ' is-selected' : ''}${isEditing ? ' is-editing' : ''}${isSpacePressed ? ' is-space-disabled' : ''}`}
                data-template-block-id={block.id}
                style={{
                  left: item.pageBounds.left + textX,
                  top: item.pageBounds.top + textY,
                  width: textWidth,
                }}
                onMouseDownCapture={requestTemplateControlsFocus}
                onTouchStartCapture={requestTemplateControlsFocus}
                onPointerDown={(event) => {
                  if (isEditing) return;
                  beginTemplateTextInteraction(event, block, item.pageBounds, 'move');
                }}
                onDoubleClick={(event) => handleTemplateTextDoubleClick(event, block)}
                onContextMenu={(event) => handleTemplateTextContextMenu(event, block)}
              >
                <span
                  className="template-floating-text__content"
                  contentEditable={isEditing}
                  suppressContentEditableWarning
                  data-template-text-editor={block.id}
                  data-template-text-empty={hasTextContent ? 'false' : 'true'}
                  onBlur={(event) => handleTemplateTextBlur(event, block)}
                  onKeyDown={(event) => event.stopPropagation()}
                  dangerouslySetInnerHTML={{ __html: adjustNoteHtmlForZoom(textContent) }}
                />
                {isSelected && !isEditing ? (
                  <button
                    type="button"
                    className="template-floating-text__resize template-floating-text__resize-handle"
                    aria-label="Cambiar ancho del texto"
                    onPointerDown={(event) => beginTemplateTextInteraction(event, block, item.pageBounds, 'resize')}
                  />
                ) : null}
              </div>
            );
          })
        ))
        : null}
      {shouldRenderFooter
        ? overlays.filter((item) => item.showFooter).map((item) => (
            <div
              key={`template-footer-${item.key}`}
              className="template-footer-overlay ux-only export-visible"
              style={item.footerStyle}
            >
              {item.footerLabel}
            </div>
          ))
        : null}
    </>,
    container
  );
};

const flowKey = 'example-flow';

const initialNodes = [];

let id = 0;
const getId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getNodeId = () => `randomnode_${+new Date()}`;
const nodeTypes = {
  customNode: CustomNode,
  busNode: BusNode,
  textBoxNode: TextBoxNode,
  boxNode: BoxNode,
  straightLineNode: StraightLineNode,
  symbolDrawerNode: SymbolDrawerNode,
};

const ICON_SIZE_PRESETS = [
  {
    key: 'small',
    label: 'Chico',
    scaleCm: 100 / 37.7952755906,
    iconVariant: 'small',
  },
];
const DEFAULT_ICON_SIZE_PRESET = ICON_SIZE_PRESETS[0];

const TEXT_SIZE_PRESETS = [
  { key: 'text-small', label: 'Texto chico', sizeIcon: 12, sizePx: 28 },
  { key: 'text-medium', label: 'Texto mediano', sizeIcon: 16, sizePx: 38 },
  { key: 'text-large', label: 'Texto grande', sizeIcon: 20, sizePx: 50 },
];

const ROTATE_ICON_SRC = '/assets/ico-rotar.png';
const CLONE_ICON_SRC = '/assets/ico-clonar.png';
const MOVE_TEXT_ICON_SRC = '/assets/ico-moverTexto.png';
const OUT_SERVICE_ICON_SRC = '/assets/ico-outservice.png';

const commonIconProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const AlignHorizontalIcon = () => (
  <svg {...commonIconProps}>
    <path d="M5 4v16" strokeWidth="1.2" strokeOpacity="0.45" />
    <path d="M19 4v16" strokeWidth="1.2" strokeOpacity="0.45" />
    <rect x="7.5" y="6.5" width="5.5" height="5" rx="1.2" fill="none" />
    <rect x="11" y="12.5" width="5.5" height="5" rx="1.2" fill="none" />
  </svg>
);

const AlignVerticalIcon = () => (
  <svg {...commonIconProps}>
    <path d="M4 5h16" strokeWidth="1.2" strokeOpacity="0.45" />
    <path d="M4 19h16" strokeWidth="1.2" strokeOpacity="0.45" />
    <rect x="6.5" y="7.5" width="5" height="5.5" rx="1.2" fill="none" />
    <rect x="12.5" y="11" width="5" height="5.5" rx="1.2" fill="none" />
  </svg>
);

const LockIcon = ({ locked = false }) => (
  <svg {...commonIconProps}>
    <rect x="5.5" y="10.5" width="13" height="9" rx="2" />
    {locked ? (
      <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
    ) : (
      <path d="M8.5 10.5V8a3.5 3.5 0 0 1 6.2-2.2" />
    )}
    <path d="M12 14v2" />
  </svg>
);

const BoldIcon = () => (
  <svg {...commonIconProps}>
    <path d="M8 5h4.8a3.1 3.1 0 0 1 0 6.2H8z" />
    <path d="M8 11.2h5.4a3.4 3.4 0 0 1 0 6.8H8z" />
    <path d="M8 5v13" />
  </svg>
);

const ActionIconImage = ({ src, tint = false }) => {
  if (!tint) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="node-selection-overlay__circle-btn-image"
        draggable="false"
      />
    );
  }

  return (
    <span
      className="node-selection-overlay__circle-btn-image node-selection-overlay__circle-btn-image--mask"
      style={{
        backgroundColor: 'currentColor',
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
      }}
      aria-hidden="true"
    />
  );
};

const edgeTypes = {
  cable: CableEdge,
  orthogonalEdge: OrthogonalEdge,
};

let editorTemplateIdCounter = 0;
const createEditorTemplateElementId = (prefix = 'tmpl') => {
  editorTemplateIdCounter += 1;
  const randomPart = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 12);
  return `${prefix}_${Date.now()}_${editorTemplateIdCounter}_${randomPart}`;
};

const deepCloneTemplateValue = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

const getTemplateBlockPosition = (block) => {
  if (block?.type === 'image') {
    return {
      x: Number.isFinite(Number(block.imageX)) ? Number(block.imageX) : 0,
      y: Number.isFinite(Number(block.imageY)) ? Number(block.imageY) : 0,
    };
  }
  return {
    x: Number.isFinite(Number(block?.textX)) ? Number(block.textX) : 0,
    y: Number.isFinite(Number(block?.textY)) ? Number(block.textY) : 0,
  };
};

const getTemplateBlockSize = (block) => {
  if (block?.type === 'image') {
    const width = clampTemplateImageScaleValue(block.imageScale);
    const ratio = normalizeTemplateImageRatioValue(block.imageRatio);
    return {
      width,
      height: ratio === 'rectangle' ? width * (3 / 4) : width,
    };
  }
  return {
    width: Number.isFinite(Number(block?.textWidth)) ? Number(block.textWidth) : TEMPLATE_TEXT_WIDTH_DEFAULT,
    height: 80,
  };
};

export const Diagram = ({ onDirty, registerMarkSaved, registerSelectAll, registerCloneSelection, registerInsertTemplate, onCreateTemplate }) => {

  
  const dispatch = useDispatch();
  const reactFlowInstance = useReactFlow();
  const reactFlowWrapper = useRef(null);
  // Using React Flow's built-in connect-on-click preview workflow

  // Search params fallback
  const [searchParams] = useSearchParams();
  const diagramIdFromQuery = searchParams.get('diagramId');

  // Redux selectors
  const selectedDiagramId = useSelector((state) => state.diagram.selectedId);
  const currentNodes = useSelector((state) => state.node.nodes) || [];
  const currentEdges = useSelector((state) => state.edge.edges) || [];
  const templateSettings = useSelector((state) => state.view.templateSettings) || {};
  const selectedTemplateBlockId = templateSettings.selectedBlockId || null;
  const pageSize = useSelector((state) => state.view.pageSize);
  const { width: templatePageWidth, height: templatePageHeight } = PAGE_SIZE_DIMENSIONS[pageSize] || PAGE_SIZE_DIMENSIONS[PAGE_SIZE_A4];
  const gridGapPx = useSelector((state) => state.view.gridGapPx) || 25;
  const gridSnapEnabled = useSelector((state) => state.view.gridSnapEnabled);
  const currentSelectedNodes = useSelector((state) => state.node.selectedNodeIds) || [];
  const currentSelectedEdges = useSelector((state) => state.edge.selectedEdgeIds) || [];
  const edgeSquelchUntil = useSelector((state) => state.edge.squelchUntil) || 0;
  const diagramLoading = useSelector((state) => state.diagram.diagramLoading);
  const historyApplying = useSelector((state) => state.history.applying);
  const globalLabelFontPx = useSelector((state) => state.view.labelFontPx) || 38;
  const symbolLabelOverrides = useSelector((state) => state.view.symbolLabelOverrides) || {};
  const labelPositionEditNodeId = useSelector((state) => state.view.labelPositionEditNodeId);
  const handlesHidden = useSelector((state) => state.view.handlesHidden);
  const autoConnectRequest = useSelector((state) => state.view.autoConnectRequest);
  const symbolDrawerTool = useSelector((state) => state.view.symbolDrawerTool) || EMPTY_SYMBOL_DRAWER_TOOL_STATE;
  const boxTool = useSelector((state) => state.view.boxTool) || EMPTY_BOX_TOOL_STATE;
  const boxToolRef = useRef(boxTool);
  const boxPointerInteractionRef = useRef(null);
  const suppressBoxPaneClickUntilRef = useRef(0);
  const straightLineTool = useSelector((state) => state.view.straightLineTool) || EMPTY_STRAIGHT_LINE_TOOL_STATE;
  const straightLineToolRef = useRef(straightLineTool);
  const straightLinePointerInteractionRef = useRef(null);
  const suppressStraightLinePaneClickUntilRef = useRef(0);
  const straightLineResizeStateRef = useRef(null);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const isSpacePressedRef = useRef(false);
  const modalIsOpen = useSelector((state) => !!state.modal?.isOpen);
  const lastLoadedDiagramIdRef = useRef(null);
  const pendingHistoryBaselineDiagramIdRef = useRef(null);

  // Node and edge operations
  const { nodes: rfNodes, setNodes, handleNodesChange, cloneNode } = useNodeOperations();
  const { setEdges, handleEdgesChange, clearLocalEdgeSelection } = useEdgeOperations();
  useEffect(() => {
    boxToolRef.current = boxTool;
  }, [boxTool]);

  useEffect(() => {
    straightLineToolRef.current = straightLineTool;
  }, [straightLineTool]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === 'Space' || event.key === ' ') {
        isSpacePressedRef.current = true;
        setIsSpacePressed(true);
      }
    };
    const handleKeyUp = (event) => {
      if (event.code === 'Space' || event.key === ' ') {
        isSpacePressedRef.current = false;
        setIsSpacePressed(false);
      }
    };
    const handleBlur = () => {
      isSpacePressedRef.current = false;
      setIsSpacePressed(false);
    };
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(currentNodes) || currentNodes.length === 0) {
      return;
    }
    setNodes((nodes) => {
      let mutated = false;
      const nextNodes = nodes.map((node) => {
        const attrs = node?.data?.attributes;
        if (!attrs || typeof attrs !== 'object') {
          return node;
        }
        const inspectorInfo = resolveInspectorForNode(node.data || {}, symbolLabelOverrides);
        const templateAttrs = inspectorInfo.attributes || {};
        let nextAttributes = null;
        Object.entries(templateAttrs).forEach(([attrKey, templateAttr]) => {
          const existingAttr = attrs[attrKey];
          if (!existingAttr) {
            return;
          }
          const nextLabel = templateAttr?.label || existingAttr.label;
          if (nextLabel !== existingAttr.label) {
            if (!nextAttributes) {
              nextAttributes = { ...attrs };
            }
            nextAttributes[attrKey] = {
              ...existingAttr,
              label: nextLabel,
            };
          }
        });
        if (!nextAttributes) {
          return node;
        }
        mutated = true;
        return {
          ...node,
          data: {
            ...(node.data || {}),
            attributes: nextAttributes,
          },
        };
      });
      return mutated ? nextNodes : nodes;
    });
  }, [currentNodes, setNodes, symbolLabelOverrides]);
  const enforceEdgeDeselection = useCallback(() => {
    const deselect = (list) => {
      if (!Array.isArray(list)) return list;
      let changed = false;
      const next = list.map((edge) => {
        if (edge?.selected) {
          changed = true;
          return { ...edge, selected: false };
        }
        return edge;
      });
      return changed ? next : list;
    };
    const clearEdges = () => {
      try {
        if (reactFlowInstance?.setEdges) {
          reactFlowInstance.setEdges((eds) => deselect(eds));
        }
      } catch {}
      setEdges((eds) => deselect(eds));
    };
    clearEdges();
    const schedule = typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame
      : (cb) => setTimeout(cb, 16);
    schedule(() => clearEdges());
  }, [reactFlowInstance, setEdges]);
  const loadDiagramData = useDiagramDataLoader();
  const {
    cloneSelection,
    rotateSingleNode,
    rotateSelectedNodes,
    selectedNodeIds: toolbarSelectedNodeIds,
    currentNode: toolbarCurrentNode,
  } = useNodeSelectionActions({ setNodes, cloneNode });
  const nodeSelectionCount = Array.isArray(toolbarSelectedNodeIds) ? toolbarSelectedNodeIds.length : 0;
  const edgeSelectionCount = Array.isArray(currentSelectedEdges) ? currentSelectedEdges.length : 0;
  const hasNodeSelection = nodeSelectionCount > 0;
  const hasEdgeSelection = edgeSelectionCount > 0;
  const hasEdgeSelectionOnly = hasEdgeSelection && !hasNodeSelection;
  const overlayIsMulti = (hasNodeSelection && nodeSelectionCount > 1) || (hasEdgeSelection && edgeSelectionCount > 1);
  const showNodeToolbar = !diagramLoading && (hasNodeSelection || hasEdgeSelection);
  const canRotateToolbarNode = !!toolbarCurrentNode?.data?.isRotable;
  const isTextPositionEditing = Boolean(
    labelPositionEditNodeId &&
    toolbarCurrentNode &&
    labelPositionEditNodeId === toolbarCurrentNode.id &&
    showNodeToolbar
  );
  const selectedNodes = useMemo(() => {
    if (!Array.isArray(toolbarSelectedNodeIds) || toolbarSelectedNodeIds.length === 0) {
      return [];
    }
    return currentNodes.filter((node) => toolbarSelectedNodeIds.includes(node.id));
  }, [currentNodes, toolbarSelectedNodeIds]);
  const toolbarSelectionNode = useMemo(() => {
    if (overlayIsMulti || selectedNodes.length !== 1) {
      return toolbarCurrentNode;
    }
    return selectedNodes[0] || toolbarCurrentNode;
  }, [overlayIsMulti, selectedNodes, toolbarCurrentNode]);
  const hasInspectorlessDesignSelection = useMemo(
    () => selectedNodes.length > 0 && selectedNodes.every(isInspectorlessDesignNode),
    [selectedNodes],
  );
  const selectedEdges = useMemo(() => {
    if (!Array.isArray(currentSelectedEdges) || currentSelectedEdges.length === 0) {
      return [];
    }
    return currentEdges.filter((edge) => currentSelectedEdges.includes(edge.id));
  }, [currentEdges, currentSelectedEdges]);
  const textSizeEligibleNodes = useMemo(
    () =>
      selectedNodes.filter((node) => {
        const symbol = node?.data?.symbolLabel;
        return symbol !== BOX_SYMBOL_LABEL
          && symbol !== STRAIGHT_LINE_SYMBOL_LABEL
          && symbol !== SYMBOL_DRAWER_SYMBOL_LABEL;
      }),
    [selectedNodes],
  );
  const supportsTextSize = textSizeEligibleNodes.length > 0;
  const activeTextSizeKey = useMemo(() => {
    if (!supportsTextSize) return null;
    const sizes = new Set();
    textSizeEligibleNodes.forEach((node) => {
      const sizeValue =
        typeof node?.data?.textSizePx === 'number' && node.data.textSizePx > 0
          ? node.data.textSizePx
          : globalLabelFontPx;
      sizes.add(sizeValue);
    });
    if (sizes.size !== 1) return null;
    const [sizePx] = Array.from(sizes);
    const preset = TEXT_SIZE_PRESETS.find((item) => item.sizePx === sizePx);
    return preset?.key || null;
  }, [supportsTextSize, textSizeEligibleNodes, globalLabelFontPx]);

  const canRotateSelection = useMemo(
    () => selectedNodes.some((node) => !!node?.data?.isRotable),
    [selectedNodes],
  );
  const canStartTextPositionEdit = useMemo(() => {
    if (overlayIsMulti) return false;
    if (!toolbarSelectionNode?.id) return false;
    const symbol = toolbarSelectionNode?.data?.symbolLabel;
    return symbol !== BOX_SYMBOL_LABEL
      && symbol !== STRAIGHT_LINE_SYMBOL_LABEL
      && symbol !== SYMBOL_DRAWER_SYMBOL_LABEL;
  }, [overlayIsMulti, toolbarSelectionNode]);

  const [isDragging, setDragging] = useState(false);
  const [hideDesignResizeHandles, setHideDesignResizeHandles] = useState(false);
  const [isMarqueeSelecting, setIsMarqueeSelecting] = useState(false);
  const isDraggingRef = useRef(false);
  const [nodeContextMenu, setNodeContextMenu] = useState(null);
  const [selectedTemplateBlockIds, setSelectedTemplateBlockIds] = useState([]);
  const selectedTemplateBlocks = useMemo(() => {
    if (!Array.isArray(selectedTemplateBlockIds) || selectedTemplateBlockIds.length === 0) {
      return [];
    }
    const ids = new Set(selectedTemplateBlockIds);
    return (Array.isArray(templateSettings.blocks) ? templateSettings.blocks : [])
      .filter((block) => block?.id && ids.has(block.id));
  }, [selectedTemplateBlockIds, templateSettings.blocks]);
  const lastSelectionRef = useRef({ nodeIds: [], edgeIds: [] });
  const skipSelectionOnceRef = useRef(false);
  const ignoreSelectionUntilRef = useRef(0);
  const selectionSquelchUntilRef = useRef(0);
  const suppressNodeClickUntilRef = useRef(0);
  const lastHashRef = useRef(null);
  const lastSavedHashRef = useRef(null);
  const justSelectedRef = useRef(false);
  const justSelectedUntilRef = useRef(0);
  const lastInspectorNodeRef = useRef(null);
  const paneClearedRef = useRef(false);
  const deselectTimerRef = useRef(null);
  const lastFitForPageRef = useRef(null);
  const busDragAutoConnectSuppressedRef = useRef(false);
  const nodeDragLockRef = useRef({});
  const templateSelectionDragRef = useRef(null);
  const nodeShakeStateRef = useRef({});
  const selectionDragLockRef = useRef(null);
  const nodeDragSelectionRef = useRef(null);
  const busCascadeDragRef = useRef(null);

  const computeSelectionDelta = useCallback((initialPositions, currentPositions) => {
    if (!initialPositions || !currentPositions) return null;
    const EPS = 0.00001;
    for (const [id, originPos] of initialPositions.entries()) {
      if (!originPos) continue;
      const current = currentPositions.get(id);
      if (!current) continue;
      const dx = current.x - originPos.x;
      const dy = current.y - originPos.y;
      if (Math.abs(dx) > EPS || Math.abs(dy) > EPS) {
        return { dx, dy };
      }
    }
    return { dx: 0, dy: 0 };
  }, []);

  const captureSelectionSnapshot = useCallback(
    (pointerFlow = null, nodeIdOverride = null, edgeIdOverride = null) => {
      const selectedIds = Array.isArray(nodeIdOverride)
        ? nodeIdOverride
        : (Array.isArray(currentSelectedNodes) ? currentSelectedNodes : []);
      if (selectedIds.length === 0) {
        selectionDragLockRef.current = null;
        return null;
      }
      const rf = reactFlowInstance;
      if (!rf?.getNode) return null;

      const origin = new Map();
      selectedIds.forEach((id) => {
        const rfNode = rf.getNode(id);
        if (rfNode && rfNode.position) {
          origin.set(id, { x: rfNode.position.x, y: rfNode.position.y });
        }
      });
      if (origin.size === 0) {
        selectionDragLockRef.current = null;
        return null;
      }

      const selectedNodeSet = new Set(selectedIds);
      const selectedEdgeIds = Array.isArray(edgeIdOverride)
        ? edgeIdOverride
        : (Array.isArray(currentSelectedEdges) ? currentSelectedEdges : []);
      const selectedEdgeSet = new Set(selectedEdgeIds);
      const edgeWaypoints = new Map();
      const rfEdges = rf?.getEdges?.() || [];
      if (rfEdges.length > 0) {
        rfEdges.forEach((edge) => {
          if (!edge || !edge.id) return;
          const includeEdge =
            selectedEdgeSet.has(edge.id) ||
            (selectedNodeSet.has(edge.source) && selectedNodeSet.has(edge.target));
          if (!includeEdge) return;
          const wps = Array.isArray(edge?.data?.waypoints) ? edge.data.waypoints : null;
          if (!wps || wps.length === 0) return;
          edgeWaypoints.set(
            edge.id,
            wps.map((pt) => ({ x: pt.x, y: pt.y }))
          );
        });
      }
      const lock = {
        axis: null,
        origin,
        shiftOrigin: null,
        shiftPointer: pointerFlow,
        initialPointer: pointerFlow,
        initialNodePositions: new Map(origin),
        edgeWaypoints,
        lastEdgeShift: { dx: 0, dy: 0 },
      };
      selectionDragLockRef.current = lock;
      return lock;
    },
    [currentSelectedNodes, currentSelectedEdges, reactFlowInstance]
  );

  const getTemplateBlockDragPosition = useCallback((block) => {
    if (!block) return null;
    if (block.type === 'text') {
      return {
        x: Number.isFinite(Number(block.textX)) ? Number(block.textX) : 80,
        y: Number.isFinite(Number(block.textY)) ? Number(block.textY) : 120,
      };
    }
    if (block.type === 'image') {
      const imageWidth = clampTemplateImageScaleValue(block.imageScale);
      const imageRatio = normalizeTemplateImageRatioValue(block.imageRatio);
      const imageHeight = imageRatio === 'rectangle' ? imageWidth * (3 / 4) : imageWidth;
      return {
        x: Number.isFinite(Number(block.imageX)) ? Number(block.imageX) : Math.max(templatePageWidth - imageWidth - 80, 0),
        y: Number.isFinite(Number(block.imageY)) ? Number(block.imageY) : 80,
        width: imageWidth,
        height: imageHeight,
      };
    }
    return null;
  }, [templatePageWidth]);

  const applySelectedTemplateBlockDrag = useCallback((node, nextPosition) => {
    if (!node?.id || !nextPosition || !Array.isArray(selectedTemplateBlockIds) || selectedTemplateBlockIds.length === 0) {
      templateSelectionDragRef.current = null;
      return;
    }
    const selectedNodeIds = Array.isArray(currentSelectedNodesRef.current) ? currentSelectedNodesRef.current : [];
    if (selectedNodeIds.length > 0 && !selectedNodeIds.includes(node.id)) {
      templateSelectionDragRef.current = null;
      return;
    }
    const blocks = Array.isArray(templateSettings.blocks) ? templateSettings.blocks : [];
    const selectedIdSet = new Set(selectedTemplateBlockIds);
    const selectedBlocks = blocks.filter((block) => (
      block?.id
      && selectedIdSet.has(block.id)
      && (block.type === 'text' || block.type === 'image')
    ));
    if (selectedBlocks.length === 0) {
      templateSelectionDragRef.current = null;
      return;
    }

    let snapshot = templateSelectionDragRef.current;
    if (!snapshot || snapshot.nodeId !== node.id) {
      const originNodePosition = { x: Number(node.position?.x) || 0, y: Number(node.position?.y) || 0 };
      const blockOrigins = new Map();
      selectedBlocks.forEach((block) => {
        const position = getTemplateBlockDragPosition(block);
        if (position) {
          blockOrigins.set(block.id, position);
        }
      });
      snapshot = {
        nodeId: node.id,
        originNodePosition,
        blockOrigins,
        lastDelta: { dx: 0, dy: 0 },
      };
      templateSelectionDragRef.current = snapshot;
    }

    const dx = nextPosition.x - snapshot.originNodePosition.x;
    const dy = nextPosition.y - snapshot.originNodePosition.y;
    const EPS = 0.0001;
    if (
      Math.abs(dx - (snapshot.lastDelta?.dx || 0)) < EPS
      && Math.abs(dy - (snapshot.lastDelta?.dy || 0)) < EPS
    ) {
      return;
    }
    const updates = selectedBlocks.map((block) => {
      const origin = snapshot.blockOrigins.get(block.id);
      if (!origin) return null;
      if (block.type === 'text') {
        return {
          id: block.id,
          changes: {
            textX: origin.x + dx,
            textY: origin.y + dy,
          },
        };
      }
      if (block.type === 'image') {
        return {
          id: block.id,
          changes: {
            imageX: origin.x + dx,
            imageY: origin.y + dy,
          },
        };
      }
      return null;
    }).filter(Boolean);
    if (updates.length > 0) {
      dispatch(updateTemplateBlocks(updates));
    }
    snapshot.lastDelta = { dx, dy };
  }, [dispatch, getTemplateBlockDragPosition, selectedTemplateBlockIds, templateSettings.blocks]);

  const applySelectedTemplateBlockTranslation = useCallback((dx, dy) => {
    if (!Array.isArray(selectedTemplateBlockIds) || selectedTemplateBlockIds.length === 0) {
      return;
    }
    const blocks = Array.isArray(templateSettings.blocks) ? templateSettings.blocks : [];
    const selectedIdSet = new Set(selectedTemplateBlockIds);
    const selectedBlocks = blocks.filter((block) => (
      block?.id
      && selectedIdSet.has(block.id)
      && (block.type === 'text' || block.type === 'image')
    ));
    if (selectedBlocks.length === 0) {
      return;
    }

    let snapshot = templateSelectionDragRef.current;
    if (!snapshot || snapshot.nodeId !== '__selection__') {
      const blockOrigins = new Map();
      selectedBlocks.forEach((block) => {
        const position = getTemplateBlockDragPosition(block);
        if (position) {
          blockOrigins.set(block.id, position);
        }
      });
      snapshot = {
        nodeId: '__selection__',
        blockOrigins,
        lastDelta: { dx: 0, dy: 0 },
      };
      templateSelectionDragRef.current = snapshot;
    }

    const EPS = 0.0001;
    if (
      Math.abs(dx - (snapshot.lastDelta?.dx || 0)) < EPS
      && Math.abs(dy - (snapshot.lastDelta?.dy || 0)) < EPS
    ) {
      return;
    }
    const updates = selectedBlocks.map((block) => {
      const origin = snapshot.blockOrigins.get(block.id);
      if (!origin) return null;
      if (block.type === 'text') {
        return {
          id: block.id,
          changes: {
            textX: origin.x + dx,
            textY: origin.y + dy,
          },
        };
      }
      if (block.type === 'image') {
        return {
          id: block.id,
          changes: {
            imageX: origin.x + dx,
            imageY: origin.y + dy,
          },
        };
      }
      return null;
    }).filter(Boolean);
    if (updates.length > 0) {
      dispatch(updateTemplateBlocks(updates));
    }
    snapshot.lastDelta = { dx, dy };
  }, [dispatch, getTemplateBlockDragPosition, selectedTemplateBlockIds, templateSettings.blocks]);

  const applyEdgeTranslation = useCallback(
    (lock, dx, dy) => {
      if (!lock || !lock.edgeWaypoints || lock.edgeWaypoints.size === 0) {
        return;
      }
      const shift = { dx: Number.isFinite(dx) ? dx : 0, dy: Number.isFinite(dy) ? dy : 0 };
      const prev = lock.lastEdgeShift || { dx: 0, dy: 0 };
      const EPS = 0.0001;
      if (Math.abs(prev.dx - shift.dx) < EPS && Math.abs(prev.dy - shift.dy) < EPS) {
        return;
      }
      const translateList = (edgesList = []) => {
        if (!Array.isArray(edgesList) || edgesList.length === 0) return edgesList;
        let changed = false;
        const next = edgesList.map((edge) => {
          const origin = lock.edgeWaypoints.get(edge.id);
          if (!origin) return edge;
          const translated = origin.map((pt) => ({
            x: pt.x + shift.dx,
            y: pt.y + shift.dy,
          }));
          const prevWaypoints = Array.isArray(edge?.data?.waypoints) ? edge.data.waypoints : [];
          let identical = prevWaypoints.length === translated.length;
          if (identical) {
            for (let i = 0; i < translated.length; i += 1) {
              const prevPt = prevWaypoints[i];
              const nextPt = translated[i];
              if (!prevPt || Math.abs(prevPt.x - nextPt.x) > EPS || Math.abs(prevPt.y - nextPt.y) > EPS) {
                identical = false;
                break;
              }
            }
          }
          if (identical) return edge;
          changed = true;
          const data = { ...(edge.data || {}), waypoints: translated };
          return { ...edge, data };
        });
        return changed ? next : edgesList;
      };
      setEdges((eds) => translateList(eds));
      if (reactFlowInstance?.setEdges) {
        try {
          reactFlowInstance.setEdges((eds) => translateList(eds));
        } catch {}
      }
      lock.lastEdgeShift = shift;
    },
    [setEdges, reactFlowInstance]
  );

  const computeHash = (nodes, edges, templateSettingsSnapshot) =>
    JSON.stringify({ nodes, edges, templateSettings: templateSettingsSnapshot }).split('').reduce((hash, chr) => {
      return ((hash << 5) - hash + chr.charCodeAt(0)) | 0;
    }, 0);

  const sanitizeNodesForHistory = useCallback((nodes = []) => {
    if (!Array.isArray(nodes)) return [];
    return nodes.map((node) => {
      if (!node || typeof node !== 'object') return node;
      const clone = JSON.parse(JSON.stringify(node));
      delete clone.selected;
      delete clone.dragging;
      delete clone.positionAbsolute;
      delete clone.style;
      return clone;
    });
  }, []);

  const sanitizeEdgesForHistory = useCallback((edges = []) => {
    if (!Array.isArray(edges)) return [];
    return edges.map((edge) => {
      if (!edge || typeof edge !== 'object') return edge;
      const clone = JSON.parse(JSON.stringify(edge));
      delete clone.selected;
      delete clone.dragging;
      return clone;
    });
  }, []);

  const saveCurrentState = useCallback((overrides = {}) => {
    if (historyApplying || !reactFlowInstance) return;
    if (pendingHistoryBaselineDiagramIdRef.current) return;
    const currentState = reactFlowInstance.toObject();
    const templateSettingsSource = Object.prototype.hasOwnProperty.call(overrides, 'templateSettings')
      ? overrides.templateSettings
      : templateSettings;
    const templateSettingsSnapshot = JSON.parse(JSON.stringify(templateSettingsSource || {}));
    const snapshotNodes = overrides.nodes
      ? sanitizeNodesForHistory(overrides.nodes)
      : sanitizeNodesForHistory(currentState.nodes);
    const snapshotEdges = overrides.edges
      ? sanitizeEdgesForHistory(overrides.edges)
      : sanitizeEdgesForHistory(currentState.edges);
    const snapshot = {
      ...currentState,
      nodes: snapshotNodes,
      edges: snapshotEdges,
      templateSettings: templateSettingsSnapshot,
    };
    const currentHash = computeHash(snapshot.nodes, snapshot.edges, templateSettingsSnapshot);
    const historyState = (() => {
      try {
        return store.getState()?.history;
      } catch (_) {
        return null;
      }
    })();
    const hasBaseline = !!(historyState && historyState.present);
    const shouldRecord = !hasBaseline || (lastHashRef.current !== currentHash && !isDraggingRef.current);

    if (shouldRecord) {
      dispatch(addHistoryState(JSON.parse(JSON.stringify(snapshot))));
      lastHashRef.current = currentHash;
    }

    if (currentHash !== lastSavedHashRef.current) {
      onDirty && onDirty(true);
    }
  }, [reactFlowInstance, dispatch, historyApplying, onDirty, templateSettings, sanitizeNodesForHistory, sanitizeEdgesForHistory]);

  const stripTransientEdgeFields = useCallback((edges = []) => edges.map((edge) => {
    if (!edge || typeof edge !== 'object') return edge;
    const { selected, dragging, ...rest } = edge;
    return rest;
  }), []);

  const handleDeleteSelected = useCallback((nodeIdsOverride = null, edgeIdsOverride = null, templateBlockIdsOverride = null) => {
    const nodeIdsSource = Array.isArray(nodeIdsOverride) ? nodeIdsOverride : toolbarSelectedNodeIds;
    const edgeIdsSource = Array.isArray(edgeIdsOverride) ? edgeIdsOverride : currentSelectedEdges;
    const templateBlockIdsSource = Array.isArray(templateBlockIdsOverride) ? templateBlockIdsOverride : selectedTemplateBlockIds;
    const nodeIds = Array.isArray(nodeIdsSource) ? nodeIdsSource : [];
    const edgeIds = Array.isArray(edgeIdsSource) ? edgeIdsSource : [];
    const templateBlockIds = Array.isArray(templateBlockIdsSource) ? templateBlockIdsSource : [];
    if (nodeIds.length === 0 && edgeIds.length === 0 && templateBlockIds.length === 0) {
      return;
    }
    let edgesWereMutated = false;
    let nextEdges = Array.isArray(currentEdges) ? [...currentEdges] : [];
    let nextNodesForHistory = null;
    let nextEdgesForHistory = null;
    let nextTemplateSettingsForHistory = null;
    if (nodeIds.length > 0) {
      const nodeSet = new Set(nodeIds);
      const sourceNodes = reactFlowInstance?.getNodes?.() || currentNodes || [];
      const nextNodes = Array.isArray(sourceNodes)
        ? sourceNodes.filter((node) => !nodeSet.has(node.id))
        : [];
      setNodes(nextNodes);
      if (reactFlowInstance && typeof reactFlowInstance.setNodes === 'function') {
        try {
          reactFlowInstance.setNodes(nextNodes);
        } catch {}
      }
      dispatch(setNodesActionCreator({ nodes: nextNodes }));
      nextEdges = nextEdges.filter((edge) => !nodeSet.has(edge.source) && !nodeSet.has(edge.target));
      nextNodesForHistory = nextNodes;
      edgesWereMutated = true;
    }
    if (edgeIds.length > 0) {
      const edgeSet = new Set(edgeIds);
      nextEdges = nextEdges.filter((edge) => !edgeSet.has(edge.id));
      edgesWereMutated = true;
    }
    if (edgesWereMutated) {
      const resetEdges = nextEdges.map((edge) => ({
        ...edge,
        selected: false,
      }));
      setEdges(resetEdges);
      if (reactFlowInstance && typeof reactFlowInstance.setEdges === 'function') {
        try {
          reactFlowInstance.setEdges(resetEdges);
        } catch {}
      }
      dispatch(setEdgesActionCreator({ edges: stripTransientEdgeFields(resetEdges) }));
      nextEdgesForHistory = resetEdges;
    }
    if (templateBlockIds.length > 0) {
      const blockSet = new Set(templateBlockIds);
      const nextBlocks = (Array.isArray(templateSettings.blocks) ? templateSettings.blocks : [])
        .filter((block) => !blockSet.has(block?.id));
      dispatch(setTemplateBlocks(nextBlocks));
      setSelectedTemplateBlockIds([]);
      dispatch(selectTemplateBlock(null));
      nextTemplateSettingsForHistory = {
        ...(templateSettings || {}),
        blocks: nextBlocks,
        selectedBlockId: null,
      };
    }
    dispatch(clearSelection());
    dispatch(clearEdgeSelection());
    dispatch(setSelectedNodeIds([]));
    dispatch(setSelectedEdgeIds([]));
    lastSelectionRef.current = { nodeIds: [], edgeIds: [] };
    dispatch(setCurrentNodeId(null));
    dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
    skipSelectionOnceRef.current = true;
    ignoreSelectionUntilRef.current = Date.now() + 200;
    selectionSquelchUntilRef.current = Date.now() + 120;
    saveCurrentState({
      ...(nextNodesForHistory ? { nodes: nextNodesForHistory } : {}),
      ...(nextEdgesForHistory ? { edges: nextEdgesForHistory } : {}),
      ...(nextTemplateSettingsForHistory ? { templateSettings: nextTemplateSettingsForHistory } : {}),
    });
  }, [
    toolbarSelectedNodeIds,
    currentSelectedEdges,
    selectedTemplateBlockIds,
    currentNodes,
    currentEdges,
    templateSettings,
    setNodes,
    setEdges,
    dispatch,
    reactFlowInstance,
    saveCurrentState,
    stripTransientEdgeFields,
  ]);

  const currentSelectedNodesRef = useRef(currentSelectedNodes || []);
  useEffect(() => {
    currentSelectedNodesRef.current = currentSelectedNodes || [];
  }, [currentSelectedNodes]);

  const currentSelectedEdgesRef = useRef(currentSelectedEdges || []);
  useEffect(() => {
    currentSelectedEdgesRef.current = currentSelectedEdges || [];
  }, [currentSelectedEdges]);
  const activeColorHex = useMemo(() => {
    if (selectedNodes.length === 0 && selectedEdges.length === 0) {
      return null;
    }
    const paletteValues = new Set();
    let hasAppliedColor = false;

    selectedNodes.forEach((node) => {
      let recorded = false;
      const tint = normalizePaletteColor(node?.data?.tintColor);
      if (tint) {
        paletteValues.add(tint);
        hasAppliedColor = true;
        recorded = true;
      }
      if (!recorded && node?.data?.symbolLabel === BOX_SYMBOL_LABEL) {
        const attrColor = normalizePaletteColor(node?.data?.attributes?.strokeColor?.value);
        if (attrColor) {
          paletteValues.add(attrColor);
          hasAppliedColor = true;
        }
      }
    });
    selectedEdges.forEach((edge) => {
      const strokeColor = normalizePaletteColor(edge?.data?.options?.stroke);
      if (strokeColor) {
        paletteValues.add(strokeColor);
        hasAppliedColor = true;
      }
    });

    if (!hasAppliedColor || paletteValues.size !== 1) {
      return null;
    }
    return paletteValues.values().next().value || null;
  }, [selectedNodes, selectedEdges]);
  const singleSelectionLabel = useMemo(() => {
    if (!hasNodeSelection) return '';
    if (!toolbarSelectionNode) return '';
    const data = toolbarSelectionNode.data || {};

    const symbolLabel = (() => {
      const symbolType = data.symbolLabel;
      if (!symbolType) return '';
      for (const group of Object.values(Symbols)) {
        const items = Array.isArray(group) ? group : [];
        const found = items.find((item) => item?.type === symbolType);
        if (found) {
          return typeof found.label === 'string' && found.label.trim()
            ? found.label.trim()
            : symbolType;
        }
      }
      return symbolType;
    })();

    const directCandidates = [
      data.name,
      data.label,
      data.displayName,
      data.title,
    ];
    for (const candidate of directCandidates) {
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed) return trimmed;
      }
    }

    const attrs = data.attributes;
    if (attrs && typeof attrs === 'object') {
      const primaryKeys = ['nombre', 'name', 'label'];
      for (const key of primaryKeys) {
        const attr = attrs[key];
        if (attr && typeof attr.value === 'string') {
          const trimmed = attr.value.trim();
          if (trimmed) return trimmed;
        }
      }

      for (const attr of Object.values(attrs)) {
        if (!attr) continue;
        const value = typeof attr.value === 'string' ? attr.value.trim() : '';
        if (!value) continue;
        const label = (attr.label || attr.name || '').toString().toLowerCase();
        if (label.includes('nombre') || label.includes('name')) {
          return value;
        }
      }
    }

    return symbolLabel || 'Elemento';
  }, [hasNodeSelection, toolbarSelectionNode]);
  const overlayLabel = (() => {
    if (hasNodeSelection) {
      if (overlayIsMulti) return 'Varios';
      return singleSelectionLabel || 'Elemento';
    }
    if (hasEdgeSelection) {
      return edgeSelectionCount > 1 ? 'Líneas' : 'Línea';
    }
    return '';
  })();
  const overlaySymbolLabel = useMemo(() => {
    if (!hasNodeSelection || overlayIsMulti) return null;
    const label = toolbarSelectionNode?.data?.symbolLabel;
    if (typeof label !== 'string') return null;
    const trimmed = label.trim();
    return trimmed || null;
  }, [hasNodeSelection, overlayIsMulti, toolbarSelectionNode]);
  const overlaySymbolSlug = useMemo(
    () => (overlaySymbolLabel ? slugifySymbolLabel(overlaySymbolLabel) : null),
    [overlaySymbolLabel],
  );
  const isTextBoxToolbar =
    hasNodeSelection &&
    !overlayIsMulti &&
    overlaySymbolLabel === 'TextBox';
  const isBoxToolbar =
    hasNodeSelection &&
    !overlayIsMulti &&
    (overlaySymbolLabel === BOX_SYMBOL_LABEL || toolbarSelectionNode?.type === 'boxNode');
  const isStraightLineToolbar =
    hasNodeSelection &&
    !overlayIsMulti &&
    (overlaySymbolLabel === STRAIGHT_LINE_SYMBOL_LABEL || toolbarSelectionNode?.type === 'straightLineNode');
  const isBoxLocked = isBoxToolbar && toolbarSelectionNode?.data?.locked === true;
  const isBusToolbar =
    hasNodeSelection &&
    !overlayIsMulti &&
    overlaySymbolLabel === 'Bus';
  const selectedSymbolDrawerNode = useMemo(() => {
    if (overlayIsMulti || selectedNodes.length !== 1) return null;
    const node = selectedNodes[0];
    return node?.data?.symbolLabel === SYMBOL_DRAWER_SYMBOL_LABEL || node?.type === 'symbolDrawerNode'
      ? node
      : null;
  }, [overlayIsMulti, selectedNodes]);
  const isSymbolDrawerToolbar = !!selectedSymbolDrawerNode;
  const textBoxAttributes = useMemo(() => {
    if (!isTextBoxToolbar) return null;
    return toolbarSelectionNode?.data?.attributes || null;
  }, [isTextBoxToolbar, toolbarSelectionNode]);
  const boxAttributes = useMemo(() => {
    if (!isBoxToolbar) return null;
    return toolbarSelectionNode?.data?.attributes || null;
  }, [isBoxToolbar, toolbarSelectionNode]);
  const textBoxBackgroundHex = useMemo(() => {
    if (!textBoxAttributes) return TEXTBOX_DEFAULT_BACKGROUND;
    const raw = textBoxAttributes.backgroundColor?.value;
    return normalizeHexColor(raw) || TEXTBOX_DEFAULT_BACKGROUND;
  }, [textBoxAttributes]);
  const textBoxTextHex = useMemo(() => {
    if (!textBoxAttributes) return TEXTBOX_DEFAULT_TEXT;
    const raw = textBoxAttributes.textColor?.value;
    return normalizeHexColor(raw) || TEXTBOX_DEFAULT_TEXT;
  }, [textBoxAttributes]);
  const textBoxBackgroundAlpha = useMemo(() => {
    if (!textBoxAttributes) return 1;
    return clampAlpha(textBoxAttributes.backgroundAlpha?.value, 1);
  }, [textBoxAttributes]);
  const textBoxAlphaPercent = Math.round(textBoxBackgroundAlpha * 100);
  const textBoxAlign = useMemo(() => {
    if (!textBoxAttributes) return 'center';
    const raw = typeof textBoxAttributes.textAlign?.value === 'string'
      ? textBoxAttributes.textAlign.value.trim().toLowerCase()
      : '';
    if (raw === 'left' || raw === 'center' || raw === 'right') {
      return raw;
    }
    return 'center';
  }, [textBoxAttributes]);
  const textBoxBold = useMemo(() => {
    if (!textBoxAttributes) return false;
    const raw = textBoxAttributes.textBold?.value;
    return raw === true || raw === 'true';
  }, [textBoxAttributes]);
  const isTextBoxBackgroundTransparent = textBoxAlphaPercent <= 0;

  const handleAlignVertical = useCallback(() => {
    if (!overlayIsMulti || !Array.isArray(toolbarSelectedNodeIds) || toolbarSelectedNodeIds.length === 0) {
      return;
    }
    const selected = currentNodes.filter((n) => toolbarSelectedNodeIds.includes(n.id));
    if (selected.length === 0) return;
    const rfNodes = reactFlowInstance?.getNodes ? reactFlowInstance.getNodes() : [];
    const sizeById = new Map(rfNodes.map((n) => [n.id, { w: n.width || 0, h: n.height || 0 }]));
    const first = selected[0];
    const baseWidth = sizeById.get(first.id)?.w || 0;
    const baseCenterX = (first?.position?.x || 0) + baseWidth / 2;
    setNodes((nodes) =>
      nodes.map((node) => {
        if (!toolbarSelectedNodeIds.includes(node.id)) return node;
        const width = sizeById.get(node.id)?.w || 0;
        const newX = Math.round(baseCenterX - width / 2);
        return { ...node, position: { ...node.position, x: newX } };
      }),
    );
  }, [overlayIsMulti, toolbarSelectedNodeIds, currentNodes, reactFlowInstance, setNodes]);

  const handleAlignHorizontal = useCallback(() => {
    if (!overlayIsMulti || !Array.isArray(toolbarSelectedNodeIds) || toolbarSelectedNodeIds.length === 0) {
      return;
    }
    const selected = currentNodes.filter((n) => toolbarSelectedNodeIds.includes(n.id));
    if (selected.length === 0) return;
    const rfNodes = reactFlowInstance?.getNodes ? reactFlowInstance.getNodes() : [];
    const sizeById = new Map(rfNodes.map((n) => [n.id, { h: n.height || 0 }]));
    const first = selected[0];
    const baseHeight = sizeById.get(first.id)?.h || 0;
    const baseCenterY = (first?.position?.y || 0) + baseHeight / 2;
    setNodes((nodes) =>
      nodes.map((node) => {
        if (!toolbarSelectedNodeIds.includes(node.id)) return node;
        const height = sizeById.get(node.id)?.h || 0;
        const newY = Math.round(baseCenterY - height / 2);
        return { ...node, position: { ...node.position, y: newY } };
      }),
    );
  }, [overlayIsMulti, toolbarSelectedNodeIds, currentNodes, reactFlowInstance, setNodes]);

  useEffect(() => {
    if (!historyApplying && reactFlowInstance) {
      if (pendingHistoryBaselineDiagramIdRef.current) return;
      const currentState = reactFlowInstance.toObject();
      const templateSettingsSnapshot = JSON.parse(JSON.stringify(templateSettings || {}));
      const currentHash = computeHash(currentState.nodes, currentState.edges, templateSettingsSnapshot);
      lastHashRef.current = currentHash;
      if (lastSavedHashRef.current === null) {
        lastSavedHashRef.current = currentHash;
      }
    }
  }, [historyApplying, reactFlowInstance, templateSettings]);

  useEffect(() => {
    const diagramIdToLoad = selectedDiagramId || diagramIdFromQuery;
    if (!diagramIdToLoad) return;

    if (!selectedDiagramId && diagramIdFromQuery) {
      dispatch(setSelectedDiagram(diagramIdFromQuery));
    }

    if (lastLoadedDiagramIdRef.current === diagramIdToLoad) {
      return;
    }

    lastLoadedDiagramIdRef.current = diagramIdToLoad;
    pendingHistoryBaselineDiagramIdRef.current = diagramIdToLoad;
    dispatch(setHistoryBaseline(null));

    loadDiagramData({
      diagramId: diagramIdToLoad,
      setNodes,
      setEdges,
    }).then((loadedData) => {
      if (lastLoadedDiagramIdRef.current !== diagramIdToLoad) {
        return;
      }

      const loadedNodes = Array.isArray(loadedData?.nodes) ? loadedData.nodes : [];
      const loadedEdges = Array.isArray(loadedData?.edges) ? loadedData.edges : [];
      const templateSettingsSnapshot = JSON.parse(JSON.stringify(loadedData?.templateSettings || {}));
      const currentState = reactFlowInstance?.toObject ? reactFlowInstance.toObject() : {};
      const baseline = {
        ...currentState,
        nodes: sanitizeNodesForHistory(loadedNodes),
        edges: sanitizeEdgesForHistory(loadedEdges),
        templateSettings: templateSettingsSnapshot,
      };
      const baselineHash = computeHash(baseline.nodes, baseline.edges, templateSettingsSnapshot);

      dispatch(setHistoryBaseline(baseline));
      lastHashRef.current = baselineHash;
      lastSavedHashRef.current = baselineHash;
      pendingHistoryBaselineDiagramIdRef.current = null;
      onDirty && onDirty(false);
    });
  }, [
    selectedDiagramId,
    diagramIdFromQuery,
    setNodes,
    setEdges,
    loadDiagramData,
    dispatch,
    reactFlowInstance,
    sanitizeNodesForHistory,
    sanitizeEdgesForHistory,
    onDirty,
  ]);

  const applyInspectorForNode = useCallback((node) => {
    if (!node) {
      return;
    }
    if (isInspectorlessDesignNode(node)) {
      dispatch(setCurrentNodeId(null));
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
      return;
    }
    const inspectorInfo = resolveInspectorForNode(node.data || {}, symbolLabelOverrides);
    const templateAttributes = inspectorInfo.attributes || {};
    const existingAttributes = node.data?.attributes || {};
    const missingAttributes = Object.entries(templateAttributes).reduce((acc, [key, tmplAttr]) => {
      const current = existingAttributes[key];
      if (!current) {
        acc[key] = tmplAttr;
      } else if (tmplAttr && tmplAttr.label && !current.label) {
        acc[key] = { ...current, label: tmplAttr.label };
      }
      return acc;
    }, {});

    dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_INSPECTOR }));
    dispatch(setCurrentNodeId(node.id));
    if (Object.keys(missingAttributes).length > 0) {
      dispatch(setNodeAttributes({
        nodeId: node.id,
        attributes: missingAttributes,
        replace: false,
      }));
    }
    dispatch(setScene({ scene: inspectorInfo.inspectorId || INSPECTOR_IDS.DEFAULT }));
  }, [dispatch, symbolLabelOverrides]);

  // Update inspector when selection changes (avoid loops)
  useEffect(() => {
    if (!currentSelectedNodes || currentSelectedNodes.length === 0) {
      lastInspectorNodeRef.current = null;
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
      return;
    }
    if (currentSelectedNodes.length > 1) {
      lastInspectorNodeRef.current = null;
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
      return;
    }
    const firstId = currentSelectedNodes[0];
    if (lastInspectorNodeRef.current === firstId) return;
    const selected = currentNodes.find((n) => n.id === firstId);
    if (selected) {
      if (isInspectorlessDesignNode(selected)) {
        lastInspectorNodeRef.current = firstId;
        dispatch(setCurrentNodeId(null));
        dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
        return;
      }
      applyInspectorForNode(selected);
      lastInspectorNodeRef.current = firstId;
    }
  }, [applyInspectorForNode, currentSelectedNodes, currentNodes, dispatch]);
  // Custom connection drawing state
  const [drawConn, setDrawConn] = useState({ active: false, src: null, waypoints: [], mouse: null, hover: null });
  const [connectOnClickEnabled, setConnectOnClickEnabled] = useState(true);
  const drawConnRef = useRef(drawConn);
  const validHoverHandleRef = useRef(null);
  const suppressConnectStartUntilRef = useRef(0);
  const spawnDragPayloadRef = useRef(null);
  const spawnPlacementInFlightRef = useRef(false);
  const waypointsRef = useRef([]);
  const boxResizeStateRef = useRef(null);
  const lastElbowTsRef = useRef(0);
  useEffect(() => { drawConnRef.current = drawConn; }, [drawConn]);
  const clearValidHoverHandle = useCallback(() => {
    if (validHoverHandleRef.current) {
      validHoverHandleRef.current.classList.remove('connection-assist-valid');
      validHoverHandleRef.current = null;
    }
    dispatch(setConnectionAssistHover({ hoveredNodeId: null, hoveredHandleId: null }));
  }, [dispatch]);
  const isSpawnPlacementActive = useCallback(() => {
    if (spawnDragPayloadRef.current) return true;
    try {
      return !!store.getState?.()?.view?.spawnPreview?.data;
    } catch {
      return false;
    }
  }, []);
  const getActiveSpawnPlacementPayload = useCallback(() => {
    try {
      return store.getState?.()?.view?.spawnPreview?.data || spawnDragPayloadRef.current || null;
    } catch {
      return spawnDragPayloadRef.current || null;
    }
  }, []);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [viewTransform, setViewTransform] = useState({ x: 0, y: 0, zoom: 1 });
  const userMovedRef = useRef(false);
  const prevEmptyRef = useRef(null);
  const initialViewSetRef = useRef(false);
  const initialContentFitRef = useRef(false);

  const selectAllElements = useCallback(() => {
    try {
      const rf = reactFlowInstance;
      const rfNodes = (rf && rf.getNodes) ? rf.getNodes() || [] : [];
      const rfEdges = (rf && rf.getEdges) ? rf.getEdges() || [] : [];

      let nodeIds = rfNodes.map((n) => n.id);
      let edgeIds = rfEdges.map((e) => e.id);

      if ((!nodeIds || nodeIds.length === 0) || (!edgeIds || edgeIds.length === 0)) {
        try {
          const state = store.getState ? store.getState() : null;
          if (state) {
            if (!nodeIds || nodeIds.length === 0) {
              const reduxNodes = state.node?.nodes || [];
              nodeIds = reduxNodes.map((n) => n.id);
            }
            if (!edgeIds || edgeIds.length === 0) {
              const reduxEdges = state.edge?.edges || [];
              edgeIds = reduxEdges.map((e) => e.id);
            }
          }
        } catch {}
      }

      const hasNodes = Array.isArray(nodeIds) && nodeIds.length > 0;
      const hasEdges = Array.isArray(edgeIds) && edgeIds.length > 0;
      const templateBlockIds = (Array.isArray(templateSettings.blocks) ? templateSettings.blocks : [])
        .filter((block) => block?.id && (block.type === 'text' || block.type === 'image'))
        .map((block) => block.id);
      const hasTemplateBlocks = templateBlockIds.length > 0;
      if (!hasNodes && !hasEdges && !hasTemplateBlocks) {
        return;
      }

      selectionSquelchUntilRef.current = Date.now() + 150;
      ignoreSelectionUntilRef.current = Date.now() + 150;
      skipSelectionOnceRef.current = true;

      if (hasNodes) {
        setNodes((nds) => (Array.isArray(nds)
          ? nds.map((n) => (n?.selected ? n : { ...n, selected: true }))
          : nds));
        if (rf && typeof rf.setNodes === 'function') {
          rf.setNodes((nds) => (Array.isArray(nds)
            ? nds.map((n) => ({ ...n, selected: true }))
            : nds));
        }
      }

      if (hasEdges) {
        setEdges((eds) => (Array.isArray(eds)
          ? eds.map((e) => (e?.selected ? e : { ...e, selected: true }))
          : eds));
        if (rf && typeof rf.setEdges === 'function') {
          rf.setEdges((eds) => (Array.isArray(eds)
            ? eds.map((e) => ({ ...e, selected: true }))
            : eds));
        }
      }

      lastSelectionRef.current = {
        nodeIds: hasNodes ? [...nodeIds] : [],
        edgeIds: hasEdges ? [...edgeIds] : [],
      };

      dispatch(setSelectedNodeIds(hasNodes ? nodeIds : []));
      dispatch(setSelectedEdgeIds(hasEdges ? edgeIds : []));
      setSelectedTemplateBlockIds(templateBlockIds);
      dispatch(selectTemplateBlock(templateBlockIds[0] || null));
      dispatch(setCurrentNodeId(null));
      captureSelectionSnapshot(null, nodeIds, edgeIds);
    } catch (err) {
      console.error('[diagram] Failed to select all', err);
    }
  }, [reactFlowInstance, setNodes, setEdges, dispatch, templateSettings.blocks, captureSelectionSnapshot]);

  // Selection clearing now handled directly; legacy blink workaround removed

  // Grid snapping (align to Background id=1 gap)
  const GRID_GAP = Math.max(5, Math.min(200, Number(gridGapPx) || 25));
  const snapToGrid = useCallback((pos) => ({
    x: Math.round((pos?.x || 0) / GRID_GAP) * GRID_GAP,
    y: Math.round((pos?.y || 0) / GRID_GAP) * GRID_GAP,
  }), [GRID_GAP]);

  const normalizeBoxPoint = useCallback((pos) => {
    if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') {
      return null;
    }
    if (!gridSnapEnabled) {
      return { x: pos.x, y: pos.y };
    }
    const snapped = snapToGrid(pos);
    return { x: snapped.x, y: snapped.y };
  }, [gridSnapEnabled, snapToGrid]);

  const resolveBoxPlacement = useCallback(
    (anchorPoint, pointerPoint, forceSquare) => {
      const anchor = normalizeBoxPoint(anchorPoint);
      const corner = normalizeBoxPoint(pointerPoint);
      if (!anchor || !corner) return null;

      let endX = corner.x;
      let endY = corner.y;
      const deltaX = endX - anchor.x;
      const deltaY = endY - anchor.y;
      if (forceSquare) {
        const span = Math.max(Math.abs(deltaX), Math.abs(deltaY), BOX_MIN_SIZE);
        const signX = deltaX >= 0 ? 1 : -1;
        const signY = deltaY >= 0 ? 1 : -1;
        endX = anchor.x + span * signX;
        endY = anchor.y + span * signY;
      }

      const ensureMinimum = (start, finish, minSize) => {
        const span = Math.abs(finish - start);
        if (span >= minSize) {
          return finish;
        }
        const direction = finish >= start ? 1 : -1;
        return start + direction * minSize;
      };

      let finalEndX = ensureMinimum(anchor.x, endX, BOX_MIN_SIZE);
      let finalEndY = ensureMinimum(anchor.y, endY, BOX_MIN_SIZE);

      let width = Math.abs(finalEndX - anchor.x);
      let height = Math.abs(finalEndY - anchor.y);

      if (width < BOX_MIN_SIZE && height < BOX_MIN_SIZE) {
        finalEndX = anchor.x + BOX_DEFAULT_WIDTH;
        finalEndY = anchor.y + (forceSquare ? BOX_DEFAULT_WIDTH : BOX_DEFAULT_HEIGHT);
        width = Math.abs(finalEndX - anchor.x);
        height = Math.abs(finalEndY - anchor.y);
      }

      const position = {
        x: Math.min(anchor.x, finalEndX),
        y: Math.min(anchor.y, finalEndY),
      };

      return {
        anchor,
        corner: { x: finalEndX, y: finalEndY },
        position,
        size: {
          width: Math.max(BOX_MIN_SIZE, width),
          height: Math.max(BOX_MIN_SIZE, height),
        },
      };
    },
    [normalizeBoxPoint],
  );

  const resolveBoxPresetPlacement = useCallback((pointerPoint, preset) => {
    const anchor = normalizeBoxPoint(pointerPoint) || pointerPoint;
    if (!anchor || typeof anchor.x !== 'number' || typeof anchor.y !== 'number') {
      return null;
    }
    const table = preset?.table || null;
    const columns = Array.isArray(table?.headers) && table.headers.length > 0
      ? table.headers.length
      : Number(preset?.attributes?.columns?.value || BOX_DEFAULT_COLUMNS);
    const rows = Array.isArray(table?.rows)
      ? table.rows.length + 1
      : Number(preset?.attributes?.rows?.value || BOX_DEFAULT_ROWS);
    const width = Math.min(
      TABLE_DEFAULT_MAX_WIDTH,
      Math.max(TABLE_DEFAULT_MIN_WIDTH, Math.round(columns * TABLE_DEFAULT_CELL_WIDTH)),
    );
    const height = Math.min(
      TABLE_DEFAULT_MAX_HEIGHT,
      Math.max(TABLE_DEFAULT_MIN_HEIGHT, Math.round(rows * TABLE_DEFAULT_ROW_HEIGHT)),
    );
    return {
      position: { x: anchor.x, y: anchor.y },
      size: { width, height },
    };
  }, [normalizeBoxPoint]);

  const normalizeStraightLinePoint = useCallback((pos) => {
    if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') {
      return null;
    }
    if (!gridSnapEnabled) {
      return { x: pos.x, y: pos.y };
    }
    const snapped = snapToGrid(pos);
    return { x: snapped.x, y: snapped.y };
  }, [gridSnapEnabled, snapToGrid]);

  const lockPointToDominantAxis = useCallback((anchorPoint, pointerPoint) => {
    if (!anchorPoint || !pointerPoint) return pointerPoint;
    const dx = pointerPoint.x - anchorPoint.x;
    const dy = pointerPoint.y - anchorPoint.y;
    if (Math.abs(dx) >= Math.abs(dy)) {
      return { x: pointerPoint.x, y: anchorPoint.y };
    }
    return { x: anchorPoint.x, y: pointerPoint.y };
  }, []);

  const resolveStraightLinePlacement = useCallback((anchorPoint, pointerPoint, forceAxis = false) => {
    const anchor = normalizeStraightLinePoint(anchorPoint);
    let end = normalizeStraightLinePoint(pointerPoint);
    if (!anchor || !end) return null;
    if (forceAxis) {
      end = lockPointToDominantAxis(anchor, end);
    }

    const dx = end.x - anchor.x;
    const dy = end.y - anchor.y;
    if (Math.hypot(dx, dy) < STRAIGHT_LINE_MIN_LENGTH) {
      end = { x: anchor.x + 160, y: anchor.y };
    }

    const minX = Math.min(anchor.x, end.x);
    const minY = Math.min(anchor.y, end.y);
    const maxX = Math.max(anchor.x, end.x);
    const maxY = Math.max(anchor.y, end.y);
    const padding = STRAIGHT_LINE_PADDING;
    const width = Math.max(1, Math.round(maxX - minX + padding * 2));
    const height = Math.max(1, Math.round(maxY - minY + padding * 2));
    const position = {
      x: minX - padding,
      y: minY - padding,
    };

    return {
      anchor,
      end,
      position,
      size: { width, height },
      start: {
        x: anchor.x - position.x,
        y: anchor.y - position.y,
      },
      finish: {
        x: end.x - position.x,
        y: end.y - position.y,
      },
    };
  }, [lockPointToDominantAxis, normalizeStraightLinePoint]);

  const alignNodeByHandle = useCallback((node) => {
    if (!node || !node.position) return node?.position || { x: 0, y: 0 };

    const measuredNode = reactFlowInstance?.getNode?.(node.id) || node;
    const isBusNode = (node?.type === 'busNode') || (node?.data?.symbolLabel === 'Bus');
    if (isBusNode) {
      const snap = (value) => {
        if (!Number.isFinite(value)) return 0;
        return Math.round(value / GRID_GAP) * GRID_GAP;
      };
      return {
        x: snap(node.position.x),
        y: snap(node.position.y),
      };
    }
    const width = typeof measuredNode?.width === 'number' && measuredNode.width > 0
      ? measuredNode.width
      : typeof measuredNode?.__rf?.width === 'number' && measuredNode.__rf.width > 0
        ? measuredNode.__rf.width
        : typeof node.width === 'number' && node.width > 0
          ? node.width
          : 0;
    const height = typeof measuredNode?.height === 'number' && measuredNode.height > 0
      ? measuredNode.height
      : typeof measuredNode?.__rf?.height === 'number' && measuredNode.__rf.height > 0
        ? measuredNode.__rf.height
        : typeof node.height === 'number' && node.height > 0
          ? node.height
          : 0;

    const bounds = measuredNode?.handleBounds || measuredNode?.__rf?.handleBounds;
    const sourceHandles = [];
    const targetHandles = [];
    const collect = (arr, list) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((handle) => {
        if (!handle) return;
        const offsetX = (handle.x ?? 0) + (handle.width ?? 0) / 2;
        const offsetY = (handle.y ?? 0) + (handle.height ?? 0) / 2;
        if (Number.isFinite(offsetX) && Number.isFinite(offsetY)) {
          list.push({ offsetX, offsetY });
        }
      });
    };
    collect(bounds?.source, sourceHandles);
    collect(bounds?.target, targetHandles);

    const primaryHandles = sourceHandles.length > 0 ? sourceHandles : targetHandles;
    const secondaryHandles = sourceHandles.length > 0 ? targetHandles : [];

    const toOffsets = (handles) => ({
      x: handles.filter((h) => Number.isFinite(h.offsetX)).map((h) => h.offsetX),
      y: handles.filter((h) => Number.isFinite(h.offsetY)).map((h) => h.offsetY),
    });

    const primaryOffsets = toOffsets(primaryHandles);
    const secondaryOffsets = toOffsets(secondaryHandles);

    const fallbackX = width > 0 ? width / 2 : 0;
    const fallbackY = height > 0 ? height / 2 : 0;

    const buildAxisCandidates = (current, offsets, fallbackOffset) => {
      const base = offsets.length > 0 ? offsets : [fallbackOffset];
      const candidateSet = new Set();
      base.forEach((offset) => {
        if (!Number.isFinite(offset)) return;
        const value = (current ?? 0) + offset;
        const ratio = value / GRID_GAP;
        [Math.round(ratio), Math.floor(ratio), Math.ceil(ratio)].forEach((mult) => {
          const snapped = mult * GRID_GAP;
          if (Number.isFinite(snapped)) candidateSet.add(snapped - offset);
        });
      });
      if (candidateSet.size === 0) {
        const fallbackValue = Math.round(((current ?? 0) + fallbackOffset) / GRID_GAP) * GRID_GAP;
        candidateSet.add(fallbackValue - fallbackOffset);
      }
      return Array.from(candidateSet);
    };

    const xCandidates = buildAxisCandidates(node.position.x, primaryOffsets.x, fallbackX);
    const yCandidates = buildAxisCandidates(node.position.y, primaryOffsets.y, fallbackY);

    const scoreHandles = (candidateX, candidateY, handles, weight) => {
      let score = 0;
      handles.forEach(({ offsetX, offsetY }) => {
        const absX = candidateX + offsetX;
        const absY = candidateY + offsetY;
        const snappedX = Math.round(absX / GRID_GAP) * GRID_GAP;
        const snappedY = Math.round(absY / GRID_GAP) * GRID_GAP;
        const residueX = Math.abs(absX - snappedX);
        const residueY = Math.abs(absY - snappedY);
        score += (residueX + residueY) * weight;
      });
      return score;
    };

    const evaluateCombo = (candidateX, candidateY) => {
      let score = 0;
      if (primaryHandles.length > 0) {
        score += scoreHandles(candidateX, candidateY, primaryHandles, 2);
      } else {
        const absX = candidateX + fallbackX;
        const absY = candidateY + fallbackY;
        const snappedX = Math.round(absX / GRID_GAP) * GRID_GAP;
        const snappedY = Math.round(absY / GRID_GAP) * GRID_GAP;
        score += Math.abs(absX - snappedX) + Math.abs(absY - snappedY);
      }
      if (secondaryHandles.length > 0) {
        score += scoreHandles(candidateX, candidateY, secondaryHandles, 1);
      }
      return score;
    };

    let bestX = node.position.x ?? 0;
    let bestY = node.position.y ?? 0;
    let bestScore = Number.POSITIVE_INFINITY;
    let bestDelta = Number.POSITIVE_INFINITY;

    xCandidates.forEach((candidateX) => {
      yCandidates.forEach((candidateY) => {
        const score = evaluateCombo(candidateX, candidateY);
        const deltaX = Math.abs(candidateX - (node.position?.x ?? 0));
        const deltaY = Math.abs(candidateY - (node.position?.y ?? 0));
        const delta = deltaX + deltaY;
        if (score < bestScore - 1e-6 || (Math.abs(score - bestScore) <= 1e-6 && delta < bestDelta)) {
          bestScore = score;
          bestDelta = delta;
          bestX = candidateX;
          bestY = candidateY;
        }
      });
    });

    return {
      x: bestX,
      y: bestY,
    };
  }, [GRID_GAP, reactFlowInstance]);

  const handleStartTextPositionEdit = useCallback(() => {
    if (overlayIsMulti || !toolbarCurrentNode?.id) {
      return;
    }
    dispatch(setLabelPositionEditNodeId(toolbarCurrentNode.id));
  }, [dispatch, overlayIsMulti, toolbarCurrentNode]);

  const handleStartTextPositionEditForNode = useCallback((node) => {
    if (overlayIsMulti || !node?.id) {
      return;
    }
    dispatch(setLabelPositionEditNodeId(node.id));
  }, [dispatch, overlayIsMulti]);

  useEffect(() => {
    if (!labelPositionEditNodeId || modalIsOpen) {
      return;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        dispatch(setLabelPositionEditNodeId(null));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [labelPositionEditNodeId, dispatch, modalIsOpen]);

  useEffect(() => {
    if (!labelPositionEditNodeId) {
      return;
    }
    const isSameNode = toolbarCurrentNode?.id === labelPositionEditNodeId;
    if (!showNodeToolbar || overlayIsMulti || !isSameNode) {
      dispatch(setLabelPositionEditNodeId(null));
    }
  }, [labelPositionEditNodeId, toolbarCurrentNode, showNodeToolbar, overlayIsMulti, dispatch]);

  const handleApplyTextSizePreset = useCallback(
    (presetKey) => {
      if (!Array.isArray(toolbarSelectedNodeIds) || toolbarSelectedNodeIds.length === 0) {
        return;
      }
      const preset = TEXT_SIZE_PRESETS.find((item) => item.key === presetKey);
      if (!preset) return;
      dispatch(setLabelFontPx(preset.sizePx));
      if (!supportsTextSize) {
        return;
      }
      const targetIds = new Set(toolbarSelectedNodeIds);
      let didChange = false;
      setNodes((nodes) => {
        let mutated = false;
        const updated = nodes.map((node) => {
          if (!targetIds.has(node.id)) {
            return node;
          }
          const symbol = node?.data?.symbolLabel;
          if (symbol === BOX_SYMBOL_LABEL) {
            return node;
          }
          const currentSize =
            typeof node?.data?.textSizePx === 'number' && node.data.textSizePx > 0
              ? node.data.textSizePx
              : globalLabelFontPx;
          if (currentSize === preset.sizePx) {
            return node;
          }
          mutated = true;
          return {
            ...node,
            data: {
              ...(node.data || {}),
              textSizePx: preset.sizePx,
            },
          };
        });
        if (mutated) {
          didChange = true;
          return updated;
        }
        return nodes;
      });
      if (didChange) {
        saveCurrentState();
      }
    },
    [supportsTextSize, toolbarSelectedNodeIds, setNodes, globalLabelFontPx, saveCurrentState, dispatch],
  );

  const handleApplyAccentColor = useCallback(
    (colorHex, { suppressHistory = false } = {}) => {
      const normalized = normalizePaletteColor(colorHex);
      if (!normalized && colorHex) {
        return;
      }

      const currentColor = activeColorHex;
      const selectedNodesAlreadyOutOfService =
        Array.isArray(selectedNodes) &&
        selectedNodes.length > 0 &&
        selectedNodes.every((node) => node?.data?.outOfService === true);
      const shouldKeepGreyForServiceState =
        normalized === OUT_OF_SERVICE_COLOR &&
        normalized === currentColor &&
        !selectedNodesAlreadyOutOfService;
      const nextColor = normalized && normalized === currentColor && !shouldKeepGreyForServiceState ? null : normalized;
      const shouldSetOutOfService = nextColor === OUT_OF_SERVICE_COLOR;
      const shouldClearOutOfService = !!nextColor && nextColor !== OUT_OF_SERVICE_COLOR;
      const outOfServiceDate = shouldSetOutOfService ? getOutOfServiceDate() : null;

	      let nodesChanged = false;
	      let nextNodesForHistory = null;
	      if (Array.isArray(toolbarSelectedNodeIds) && toolbarSelectedNodeIds.length > 0) {
	        const sourceNodes = reactFlowInstance?.getNodes?.() || currentNodes || [];
	        const nextNodes = sourceNodes.map((node) => {
	          if (!toolbarSelectedNodeIds.includes(node.id)) {
	            return node;
	          }
            const symbolLabel = node?.data?.symbolLabel;
            const attributes = node?.data?.attributes || {};
            const existingTint = normalizePaletteColor(node?.data?.tintColor);
            const existingStrokeAttr = symbolLabel === BOX_SYMBOL_LABEL
              ? normalizePaletteColor(attributes?.strokeColor?.value)
              : null;
            const servicePatch = (() => {
              if (shouldSetOutOfService) {
                return {
                  outOfService: true,
                  outOfServiceDate: node?.data?.outOfServiceDate || outOfServiceDate,
                };
              }
              if (shouldClearOutOfService && node?.data?.outOfService === true) {
                return {
                  outOfService: false,
                  outOfServiceDate: null,
                };
              }
              return {};
            })();
            const serviceChanged = Object.keys(servicePatch).length > 0;
            if (nextColor) {
              if (
                existingTint === nextColor ||
                (symbolLabel === BOX_SYMBOL_LABEL && existingStrokeAttr === nextColor)
              ) {
                if (!serviceChanged) {
                  return node;
                }
                nodesChanged = true;
                return {
                  ...node,
                  data: {
                    ...(node.data || {}),
                    ...servicePatch,
                  },
                };
              }
              nodesChanged = true;
              if (symbolLabel === BOX_SYMBOL_LABEL) {
                const strokeAttr = attributes.strokeColor || {};
                return {
                  ...node,
                  data: {
                    ...(node.data || {}),
                    ...servicePatch,
                    tintColor: nextColor,
                    attributes: {
                      ...attributes,
                      strokeColor: {
                        ...strokeAttr,
                        value: nextColor,
                        label: strokeAttr.label || 'Color de borde',
                        isVisible: strokeAttr.isVisible ?? true,
                        systemDefined: true,
                      },
                    },
                  },
                };
              }
              return {
                ...node,
                data: {
                  ...(node.data || {}),
                  ...servicePatch,
                  tintColor: nextColor,
                },
              };
            }
            if (!existingTint) {
              if (symbolLabel !== BOX_SYMBOL_LABEL || existingStrokeAttr == null || existingStrokeAttr === BOX_DEFAULT_STROKE_COLOR) {
                return node;
              }
            }
            nodesChanged = true;
            const nextData = { ...(node.data || {}) };
            if (symbolLabel === BOX_SYMBOL_LABEL) {
              const strokeAttr = attributes.strokeColor || {};
              nextData.attributes = {
                ...attributes,
                strokeColor: {
                  ...strokeAttr,
                  value: null,
                  label: strokeAttr.label || 'Color de borde',
                  isVisible: strokeAttr.isVisible ?? true,
                  systemDefined: true,
                },
              };
            }
            delete nextData.tintColor;
            if (node?.data?.outOfService === true) {
              nextData.outOfService = false;
              nextData.outOfServiceDate = null;
            }
	          return {
	            ...node,
	            data: nextData,
	          };
	        });
	        if (nodesChanged) {
	          nextNodesForHistory = nextNodes;
	          setNodes(nextNodes);
	          try {
	            reactFlowInstance?.setNodes?.(nextNodes);
	          } catch {}
	          dispatch(setNodesActionCreator({ nodes: sanitizeNodesForHistory(nextNodes) }));
	        }
	      }

	      let edgesChanged = false;
	      let nextEdgesForHistory = null;
	      if (Array.isArray(currentSelectedEdges) && currentSelectedEdges.length > 0) {
	        const sourceEdges = reactFlowInstance?.getEdges?.() || currentEdges || [];
	        const nextEdges = sourceEdges.map((edge) => {
	          if (!currentSelectedEdges.includes(edge.id)) {
	            return edge;
	          }
            const existingData = edge.data || {};
            const existingOptions = existingData.options || {};
            const existingStroke = normalizePaletteColor(existingOptions.stroke);
            if (nextColor) {
              if (existingStroke === nextColor) {
                return edge;
              }
              edgesChanged = true;
              return {
                ...edge,
                data: {
                  ...existingData,
                  options: {
                    ...existingOptions,
                    stroke: nextColor,
                  },
                },
              };
            }
            if (!existingStroke) {
              return edge;
            }
            const { stroke: _removedStroke, ...restOptions } = existingOptions;
            const cleanedOptions = Object.keys(restOptions).length > 0 ? restOptions : undefined;
            const nextData = { ...existingData };
            if (cleanedOptions) {
              nextData.options = cleanedOptions;
            } else if (nextData.options) {
              delete nextData.options;
            }
            edgesChanged = true;
	          return {
	            ...edge,
	            data: nextData,
	          };
	        });
	        if (edgesChanged) {
	          nextEdgesForHistory = nextEdges;
	          setEdges(nextEdges);
	          try {
	            reactFlowInstance?.setEdges?.(nextEdges);
	          } catch {}
	          dispatch(setEdgesActionCreator({ edges: sanitizeEdgesForHistory(nextEdges) }));
	        }
	      }

	      if ((nodesChanged || edgesChanged) && !suppressHistory) {
	        saveCurrentState({
	          nodes: nextNodesForHistory || currentNodes,
	          edges: nextEdgesForHistory || currentEdges,
	        });
	      }
	    },
	    [
	      activeColorHex,
	      selectedNodes,
	      toolbarSelectedNodeIds,
	      currentSelectedEdges,
	      setNodes,
	      setEdges,
	      saveCurrentState,
	      reactFlowInstance,
	      currentNodes,
	      currentEdges,
	      dispatch,
	      sanitizeNodesForHistory,
	      sanitizeEdgesForHistory,
	    ]
	  );

  const emulateActiveColorClick = useCallback(() => {
    if (!activeColorHex) return;
    handleApplyAccentColor(activeColorHex, { suppressHistory: true });
  }, [activeColorHex, handleApplyAccentColor]);

  const selectedNodesOutOfService = useMemo(() => {
    if (!Array.isArray(selectedNodes) || selectedNodes.length === 0) {
      return false;
    }
    return selectedNodes.every((node) => node?.data?.outOfService === true);
  }, [selectedNodes]);

  const handleToggleOutOfService = useCallback((forceState = null) => {
    if (!Array.isArray(toolbarSelectedNodeIds) || toolbarSelectedNodeIds.length === 0) {
      return;
    }
    const targetIds = new Set(toolbarSelectedNodeIds);
    const nextOutOfService = typeof forceState === 'boolean' ? forceState : !selectedNodesOutOfService;
    const nextColor = nextOutOfService ? OUT_OF_SERVICE_COLOR : DEFAULT_SERVICE_COLOR;
    const outOfServiceDate = nextOutOfService ? getOutOfServiceDate() : null;
    const willChange = selectedNodes.some((node) => {
      if (!targetIds.has(node.id)) {
        return false;
      }
      const symbolLabel = node?.data?.symbolLabel;
      const currentData = node?.data || {};
      const attributes = currentData.attributes || {};
      const currentTint = normalizeHexColor(currentData.tintColor);
      const currentOutOfService = currentData.outOfService === true;
      const currentBoxStroke = symbolLabel === BOX_SYMBOL_LABEL
        ? normalizeHexColor(attributes?.strokeColor?.value)
        : null;
      const colorAlreadyApplied = symbolLabel === BOX_SYMBOL_LABEL
        ? currentTint === nextColor && currentBoxStroke === nextColor
        : currentTint === nextColor;
      const existingDate = currentData.outOfServiceDate;
      const dateMissing = nextOutOfService && (typeof existingDate !== 'string' || existingDate.trim() === '');
      return currentOutOfService !== nextOutOfService || dateMissing || !colorAlreadyApplied;
    });

    setNodes((nodes) => {
      let mutated = false;
      const updated = nodes.map((node) => {
        if (!targetIds.has(node.id)) {
          return node;
        }
        const symbolLabel = node?.data?.symbolLabel;
        const currentData = node.data || {};
        const attributes = currentData.attributes || {};
        const currentTint = normalizeHexColor(currentData.tintColor);
        const currentOutOfService = currentData.outOfService === true;
        const strokeColorAttr = attributes.strokeColor || {};
        const currentBoxStroke = symbolLabel === BOX_SYMBOL_LABEL
          ? normalizeHexColor(strokeColorAttr.value)
          : null;
        const colorAlreadyApplied = symbolLabel === BOX_SYMBOL_LABEL
          ? currentTint === nextColor && currentBoxStroke === nextColor
          : currentTint === nextColor;
        const existingDate = currentData.outOfServiceDate;
        const dateMissing = nextOutOfService && (typeof existingDate !== 'string' || existingDate.trim() === '');

        if (currentOutOfService === nextOutOfService && !dateMissing && colorAlreadyApplied) {
          return node;
        }

        mutated = true;
        const nextData = {
          ...currentData,
          outOfService: nextOutOfService,
          outOfServiceDate: nextOutOfService
            ? (typeof currentData.outOfServiceDate === 'string' && currentData.outOfServiceDate.trim()
                ? currentData.outOfServiceDate
                : outOfServiceDate)
            : null,
          tintColor: nextColor,
        };

        if (symbolLabel === BOX_SYMBOL_LABEL) {
          nextData.attributes = {
            ...attributes,
            strokeColor: {
              ...strokeColorAttr,
              value: nextColor,
              label: strokeColorAttr.label || 'Color de borde',
              isVisible: strokeColorAttr.isVisible ?? true,
              systemDefined: true,
            },
          };
        }

        return {
          ...node,
          data: nextData,
        };
      });

      if (mutated) {
        return updated;
      }
      return nodes;
    });

    if (willChange) {
      requestAnimationFrame(() => saveCurrentState());
    }
  }, [toolbarSelectedNodeIds, selectedNodesOutOfService, selectedNodes, setNodes, saveCurrentState]);

  const setNodesOutOfServiceById = useCallback((nodeIds, nextOutOfService = true) => {
    if (!Array.isArray(nodeIds) || nodeIds.length === 0) {
      return;
    }
    const targetIds = new Set(nodeIds);
    const nextColor = nextOutOfService ? OUT_OF_SERVICE_COLOR : DEFAULT_SERVICE_COLOR;
    const outOfServiceDate = nextOutOfService ? getOutOfServiceDate() : null;

    setNodes((nodes) => {
      let mutated = false;
      const updated = nodes.map((node) => {
        if (!targetIds.has(node.id)) {
          return node;
        }
        const symbolLabel = node?.data?.symbolLabel;
        const currentData = node.data || {};
        const attributes = currentData.attributes || {};
        const strokeColorAttr = attributes.strokeColor || {};
        const currentTint = normalizeHexColor(currentData.tintColor);
        const currentBoxStroke = symbolLabel === BOX_SYMBOL_LABEL
          ? normalizeHexColor(strokeColorAttr.value)
          : null;
        const colorAlreadyApplied = symbolLabel === BOX_SYMBOL_LABEL
          ? currentTint === nextColor && currentBoxStroke === nextColor
          : currentTint === nextColor;
        const existingDate = currentData.outOfServiceDate;
        const dateMissing = nextOutOfService && (typeof existingDate !== 'string' || existingDate.trim() === '');

        if (currentData.outOfService === nextOutOfService && !dateMissing && colorAlreadyApplied) {
          return node;
        }

        mutated = true;
        const nextData = {
          ...currentData,
          outOfService: nextOutOfService,
          outOfServiceDate: nextOutOfService
            ? (typeof currentData.outOfServiceDate === 'string' && currentData.outOfServiceDate.trim()
                ? currentData.outOfServiceDate
                : outOfServiceDate)
            : null,
          tintColor: nextColor,
        };

        if (symbolLabel === BOX_SYMBOL_LABEL) {
          nextData.attributes = {
            ...attributes,
            strokeColor: {
              ...strokeColorAttr,
              value: nextColor,
              label: strokeColorAttr.label || 'Color de borde',
              isVisible: strokeColorAttr.isVisible ?? true,
              systemDefined: true,
            },
          };
        }

        return {
          ...node,
          data: nextData,
        };
      });
      return mutated ? updated : nodes;
    });

    requestAnimationFrame(() => saveCurrentState());
  }, [setNodes, saveCurrentState]);

  const setTextBoxAttributeValue = useCallback(
    (attributeName, nextValue, options = {}) => {
      if (!isTextBoxToolbar || !Array.isArray(toolbarSelectedNodeIds) || toolbarSelectedNodeIds.length === 0) {
        return;
      }
      const targetIds = new Set(toolbarSelectedNodeIds);
      let didChange = false;
      setNodes((nodes) => {
        let mutated = false;
        const updated = nodes.map((node) => {
          if (!targetIds.has(node.id) || node?.data?.symbolLabel !== 'TextBox') {
            return node;
          }
          const attributes = node.data?.attributes || {};
          const prevAttr = attributes[attributeName] || {};
          const prevValue = prevAttr.value;
          const sameValue =
            typeof nextValue === 'object'
              ? JSON.stringify(prevValue) === JSON.stringify(nextValue)
              : prevValue === nextValue;
          if (sameValue) {
            return node;
          }
          mutated = true;
          return {
            ...node,
            data: {
              ...node.data,
              attributes: {
                ...attributes,
                [attributeName]: {
                  ...prevAttr,
                  ...options,
                  isVisible: options.isVisible ?? (prevAttr.isVisible ?? true),
                  label: options.label || prevAttr.label || attributeName,
                  systemDefined: options.systemDefined ?? prevAttr.systemDefined,
                  value: nextValue,
                },
              },
            },
          };
        });
        if (mutated) {
          didChange = true;
          return updated;
        }
        return nodes;
      });
      if (didChange) {
        saveCurrentState();
      }
    },
    [isTextBoxToolbar, toolbarSelectedNodeIds, setNodes, saveCurrentState]
  );

  const setBoxAttributeValue = useCallback(
    (attributeName, nextValue, options = {}) => {
      if (!isBoxToolbar || !Array.isArray(toolbarSelectedNodeIds) || toolbarSelectedNodeIds.length === 0) {
        return;
      }
      const { skipHistory = false, ...attributeOptions } = options;
      const targetIds = new Set(toolbarSelectedNodeIds);
      let didChange = false;
      const mutatedIds = new Set();
      setNodes((nodes) => {
        let mutated = false;
        const updated = nodes.map((node) => {
          if (!targetIds.has(node.id) || node?.data?.symbolLabel !== BOX_SYMBOL_LABEL) {
            return node;
          }
          const attributes = node.data?.attributes || {};
          const prevAttr = attributes[attributeName] || {};
          let sanitizedValue = nextValue;
          let requestedWidth = attributeName === 'width' ? sanitizedValue : undefined;
          let requestedHeight = attributeName === 'height' ? sanitizedValue : undefined;
          if (attributeName === 'width' || attributeName === 'height') {
            const numeric = Number(nextValue);
            sanitizedValue = Number.isFinite(numeric) ? numeric : prevAttr.value;
            if (attributeName === 'width') {
              requestedWidth = sanitizedValue;
            } else if (attributeName === 'height') {
              requestedHeight = sanitizedValue;
            }
          }
          if (attributeName === 'backgroundAlpha' || attributeName === 'strokeAlpha') {
            sanitizedValue = clampAlpha(
              nextValue,
              attributeName === 'strokeAlpha' ? BOX_DEFAULT_STROKE_ALPHA : BOX_DEFAULT_BACKGROUND_ALPHA,
            );
          }
          if (attributeName === 'columns' || attributeName === 'rows') {
            const numeric = Number(nextValue);
            sanitizedValue = Number.isFinite(numeric)
              ? Math.min(BOX_MAX_DIVISIONS, Math.max(1, Math.round(numeric)))
              : (prevAttr.value ?? 1);
          }
          if (prevAttr.value === sanitizedValue) {
            return node;
          }
          mutated = true;
          mutatedIds.add(node.id);
          const bufferedAttributes = {
            ...attributes,
            [attributeName]: {
              ...prevAttr,
              ...attributeOptions,
              value: sanitizedValue,
              label: attributeOptions.label || prevAttr.label || attributeName,
              isVisible: attributeOptions.isVisible ?? (prevAttr.isVisible ?? true),
            },
          };

          const rawWidth = attributeName === 'width' ? requestedWidth : bufferedAttributes.width?.value;
          const rawHeight = attributeName === 'height' ? requestedHeight : bufferedAttributes.height?.value;
          const fallbackWidth = typeof node.width === 'number' && node.width > 0 ? node.width : BOX_DEFAULT_WIDTH;
          const fallbackHeight = typeof node.height === 'number' && node.height > 0 ? node.height : BOX_DEFAULT_HEIGHT;
          const normalizedWidth = Math.max(
            BOX_MIN_SIZE,
            Number.isFinite(Number(rawWidth)) ? Number(rawWidth) : fallbackWidth
          );
          const normalizedHeight = Math.max(
            BOX_MIN_SIZE,
            Number.isFinite(Number(rawHeight)) ? Number(rawHeight) : fallbackHeight
          );

          if (bufferedAttributes.width) {
            bufferedAttributes.width = {
              ...bufferedAttributes.width,
              value: normalizedWidth,
            };
            if (attributeName === 'width') {
              sanitizedValue = normalizedWidth;
            }
          }
          if (bufferedAttributes.height) {
            bufferedAttributes.height = {
              ...bufferedAttributes.height,
              value: normalizedHeight,
            };
            if (attributeName === 'height') {
              sanitizedValue = normalizedHeight;
            }
          }

          const nextData = {
            ...(node.data || {}),
            attributes: bufferedAttributes,
          };

          if (attributeName === 'strokeColor') {
            nextData.tintColor = typeof sanitizedValue === 'string' ? sanitizedValue : nextData.tintColor;
          }

          return {
            ...node,
            data: nextData,
            width: normalizedWidth,
            height: normalizedHeight,
            style: {
              ...(node.style || {}),
              width: `${normalizedWidth}px`,
              height: `${normalizedHeight}px`,
            },
          };
        });
        if (mutated) {
          didChange = true;
          return updated;
        }
        return nodes;
      });
      if (didChange) {
        mutatedIds.forEach((id) => {
          try {
            reactFlowInstance?.updateNodeInternals?.(id);
          } catch {}
        });
        if (!skipHistory) {
          saveCurrentState();
        }
      }
    },
    [isBoxToolbar, toolbarSelectedNodeIds, setNodes, saveCurrentState, reactFlowInstance]
  );

  const handleTextBoxAlphaChange = useCallback(
    (percentValue) => {
      if (!isTextBoxToolbar) return;
      const numericPercent = typeof percentValue === 'number' ? percentValue : Number(percentValue);
      if (Number.isNaN(numericPercent)) {
        return;
      }
      const normalizedAlpha = clampAlpha(numericPercent / 100, 1);
      setTextBoxAttributeValue('backgroundAlpha', normalizedAlpha, {
        label: 'Transparencia de fondo',
        isVisible: false,
        systemDefined: true,
      });
    },
    [isTextBoxToolbar, setTextBoxAttributeValue]
  );

  const handleTextBoxColorInput = useCallback(
    (attributeName, colorHex) => {
      if (!isTextBoxToolbar) return;
      if (attributeName !== 'backgroundColor' && attributeName !== 'textColor') {
        return;
      }
      const normalized = normalizeHexColor(colorHex);
      if (!normalized) {
        return;
      }
      setTextBoxAttributeValue(attributeName, normalized);
      if (attributeName === 'backgroundColor' && textBoxAlphaPercent <= 0) {
        handleTextBoxAlphaChange(100);
      }
    },
    [isTextBoxToolbar, setTextBoxAttributeValue, handleTextBoxAlphaChange, textBoxAlphaPercent]
  );

  const handleToggleTextBoxBackground = useCallback(() => {
    const nextPercent = textBoxAlphaPercent <= 0 ? 100 : 0;
    handleTextBoxAlphaChange(nextPercent);
  }, [textBoxAlphaPercent, handleTextBoxAlphaChange]);

  const handleTextBoxAlignChange = useCallback(
    (alignValue) => {
      if (!isTextBoxToolbar) return;
      const next = typeof alignValue === 'string' ? alignValue.toLowerCase() : '';
      if (!['left', 'center', 'right'].includes(next)) return;
      setTextBoxAttributeValue('textAlign', next, {
        label: 'Alineación',
        isVisible: false,
        systemDefined: true,
      });
    },
    [isTextBoxToolbar, setTextBoxAttributeValue]
  );

  const handleToggleTextBoxBold = useCallback(() => {
    if (!isTextBoxToolbar) return;
    setTextBoxAttributeValue('textBold', !textBoxBold, {
      label: 'Bold',
      isVisible: false,
      systemDefined: true,
    });
  }, [isTextBoxToolbar, setTextBoxAttributeValue, textBoxBold]);

  const handleToggleBoxLocked = useCallback(() => {
    if (!isBoxToolbar || !toolbarSelectionNode?.id) return;
    const targetId = toolbarSelectionNode.id;
    const nextLocked = !isBoxLocked;
    setNodes((nodes) => {
      let didChange = false;
      const updated = nodes.map((node) => {
        if (node.id !== targetId || node?.data?.symbolLabel !== BOX_SYMBOL_LABEL) {
          return node;
        }
        didChange = true;
        return {
          ...node,
          draggable: !nextLocked,
          data: {
            ...(node.data || {}),
            locked: nextLocked,
          },
        };
      });
      return didChange ? updated : nodes;
    });
    requestAnimationFrame(() => saveCurrentState());
  }, [isBoxToolbar, toolbarSelectionNode, isBoxLocked, setNodes, saveCurrentState]);

  const handleBoxResizePointerDown = useCallback(
    (event) => {
      if (!isBoxToolbar || !toolbarSelectionNode || !reactFlowInstance) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rfNode = reactFlowInstance.getNode(toolbarSelectionNode.id);
      if (!rfNode) {
        return;
      }
      const target = event.currentTarget;
      const pointerId = event.pointerId;
      try {
        target.setPointerCapture?.(pointerId);
      } catch {}
      skipSelectionOnceRef.current = true;
      setHideDesignResizeHandles(true);
      const originX = rfNode.positionAbsolute?.x ?? rfNode.position?.x ?? 0;
      const originY = rfNode.positionAbsolute?.y ?? rfNode.position?.y ?? 0;
      const attrWidth = Number(boxAttributes?.width?.value);
      const attrHeight = Number(boxAttributes?.height?.value);
      const originWidth = Math.max(
        BOX_MIN_SIZE,
        Number.isFinite(attrWidth)
          ? attrWidth
          : typeof rfNode.width === 'number' && rfNode.width > 0
          ? rfNode.width
          : BOX_DEFAULT_WIDTH
      );
      const originHeight = Math.max(
        BOX_MIN_SIZE,
        Number.isFinite(attrHeight)
          ? attrHeight
          : typeof rfNode.height === 'number' && rfNode.height > 0
          ? rfNode.height
          : BOX_DEFAULT_HEIGHT
      );
      boxResizeStateRef.current = {
        originX,
        originY,
        originWidth,
        originHeight,
        changed: false,
      };
      const handlePointerMove = (moveEvent) => {
        if (!boxResizeStateRef.current) return;
        moveEvent.preventDefault();
        const flow = reactFlowInstance.screenToFlowPosition({
          x: moveEvent.clientX,
          y: moveEvent.clientY,
        });
        const nextWidth = Math.max(
          BOX_MIN_SIZE,
          Math.round(flow.x - boxResizeStateRef.current.originX)
        );
        const nextHeight = Math.max(
          BOX_MIN_SIZE,
          Math.round(flow.y - boxResizeStateRef.current.originY)
        );
        setBoxAttributeValue('width', nextWidth, { label: 'Ancho (px)', skipHistory: true });
        setBoxAttributeValue('height', nextHeight, { label: 'Alto (px)', skipHistory: true });
        boxResizeStateRef.current.changed =
          boxResizeStateRef.current.changed ||
          nextWidth !== boxResizeStateRef.current.originWidth ||
          nextHeight !== boxResizeStateRef.current.originHeight;
      };
      const handlePointerUp = () => {
        const state = boxResizeStateRef.current;
        window.removeEventListener('pointermove', handlePointerMove, true);
        window.removeEventListener('pointerup', handlePointerUp, true);
        window.removeEventListener('pointercancel', handlePointerUp, true);
        try {
          target.releasePointerCapture?.(pointerId);
        } catch {}
        if (state?.changed) {
          saveCurrentState();
        }
        boxResizeStateRef.current = null;
        setHideDesignResizeHandles(false);
      };
      window.addEventListener('pointermove', handlePointerMove, true);
      window.addEventListener('pointerup', handlePointerUp, true);
      window.addEventListener('pointercancel', handlePointerUp, true);
    },
    [isBoxToolbar, toolbarSelectionNode, reactFlowInstance, boxAttributes, setBoxAttributeValue]
  );

  const setSymbolDrawerSize = useCallback(
    (nodeId, width) => {
      if (!nodeId) return;
      const normalizedWidth = Math.max(180, Math.round(Number(width) || SYMBOL_DRAWER_DEFAULT_WIDTH));
      setNodes((nodes) => (
        Array.isArray(nodes)
          ? nodes.map((node) => {
              if (node.id !== nodeId || node?.data?.symbolLabel !== SYMBOL_DRAWER_SYMBOL_LABEL) {
                return node;
              }
              const normalizedHeight = getSymbolDrawerContentHeight(
                getUsedSymbolDrawerItems(nodes).length,
                normalizedWidth,
              );
              return {
                ...node,
                width: normalizedWidth,
                height: normalizedHeight,
                data: {
                  ...(node.data || {}),
                  drawer: {
                    ...(node.data?.drawer || {}),
                    width: normalizedWidth,
                    height: normalizedHeight,
                  },
                },
                style: {
                  ...(node.style || {}),
                  width: `${normalizedWidth}px`,
                  height: `${normalizedHeight}px`,
                },
              };
            })
          : nodes
      ));
      try {
        reactFlowInstance?.updateNodeInternals?.(nodeId);
      } catch {}
    },
    [setNodes, reactFlowInstance]
  );

  useEffect(() => {
    if (!Array.isArray(currentNodes) || currentNodes.length === 0) {
      return;
    }
    const itemCount = getUsedSymbolDrawerItems(currentNodes).length;
    const zoom = Number(viewTransform?.zoom) || 1;
    setNodes((nodes) => {
      if (!Array.isArray(nodes)) return nodes;
      let changed = false;
      const nextNodes = nodes.map((node) => {
        if (node?.data?.symbolLabel !== SYMBOL_DRAWER_SYMBOL_LABEL && node?.type !== 'symbolDrawerNode') {
          return node;
        }
        const width = Math.max(
          180,
          Number(node?.data?.drawer?.width)
            || (typeof node.width === 'number' && node.width > 0 ? node.width : SYMBOL_DRAWER_DEFAULT_WIDTH),
        );
        const renderedDrawer = typeof document !== 'undefined'
          ? Array.from(document.querySelectorAll('.symbol-drawer-node'))
            .find((element) => element?.dataset?.symbolDrawerNodeId === node.id)
          : null;
        const measuredHeight = renderedDrawer
          ? Math.ceil(renderedDrawer.getBoundingClientRect().height / zoom)
          : null;
        const height = measuredHeight || getSymbolDrawerContentHeight(itemCount, width);
        const currentHeight = Number(node.height || node?.data?.drawer?.height);
        if (Math.round(currentHeight) === Math.round(height) && node.style?.height === `${height}px`) {
          return node;
        }
        changed = true;
        return {
          ...node,
          height,
          data: {
            ...(node.data || {}),
            drawer: {
              ...(node.data?.drawer || {}),
              width,
              height,
            },
          },
          style: {
            ...(node.style || {}),
            width: `${width}px`,
            height: `${height}px`,
          },
        };
      });
      return changed ? nextNodes : nodes;
    });
  }, [currentNodes, setNodes, viewTransform?.zoom]);

  const handleSymbolDrawerResizePointerDown = useCallback(
    (event) => {
      if (!isSymbolDrawerToolbar || !selectedSymbolDrawerNode || !reactFlowInstance) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rfNode = reactFlowInstance.getNode(selectedSymbolDrawerNode.id);
      if (!rfNode) {
        return;
      }
      const target = event.currentTarget;
      const pointerId = event.pointerId;
      try {
        target.setPointerCapture?.(pointerId);
      } catch {}
      skipSelectionOnceRef.current = true;
      setHideDesignResizeHandles(true);
      const originX = rfNode.positionAbsolute?.x ?? rfNode.position?.x ?? 0;
      const originWidth = Math.max(
        180,
        Number(selectedSymbolDrawerNode?.data?.drawer?.width)
          || (typeof rfNode.width === 'number' && rfNode.width > 0 ? rfNode.width : SYMBOL_DRAWER_DEFAULT_WIDTH)
      );
      boxResizeStateRef.current = {
        originX,
        originWidth,
        changed: false,
      };
      const handlePointerMove = (moveEvent) => {
        if (!boxResizeStateRef.current) return;
        moveEvent.preventDefault();
        const flow = reactFlowInstance.screenToFlowPosition({
          x: moveEvent.clientX,
          y: moveEvent.clientY,
        });
        const nextWidth = Math.max(180, Math.round(flow.x - boxResizeStateRef.current.originX));
        setSymbolDrawerSize(selectedSymbolDrawerNode.id, nextWidth);
        boxResizeStateRef.current.changed =
          boxResizeStateRef.current.changed ||
          nextWidth !== boxResizeStateRef.current.originWidth;
      };
      const handlePointerUp = () => {
        const state = boxResizeStateRef.current;
        window.removeEventListener('pointermove', handlePointerMove, true);
        window.removeEventListener('pointerup', handlePointerUp, true);
        window.removeEventListener('pointercancel', handlePointerUp, true);
        try {
          target.releasePointerCapture?.(pointerId);
        } catch {}
        if (state?.changed) {
          saveCurrentState();
        }
        boxResizeStateRef.current = null;
        setHideDesignResizeHandles(false);
      };
      window.addEventListener('pointermove', handlePointerMove, true);
      window.addEventListener('pointerup', handlePointerUp, true);
      window.addEventListener('pointercancel', handlePointerUp, true);
    },
    [isSymbolDrawerToolbar, selectedSymbolDrawerNode, reactFlowInstance, setSymbolDrawerSize, saveCurrentState]
  );

  const updateStraightLineGeometry = useCallback((nodeId, fixedAbsolute, movingAbsolute) => {
    if (!nodeId || !fixedAbsolute || !movingAbsolute) return false;
    const minX = Math.min(fixedAbsolute.x, movingAbsolute.x);
    const minY = Math.min(fixedAbsolute.y, movingAbsolute.y);
    const maxX = Math.max(fixedAbsolute.x, movingAbsolute.x);
    const maxY = Math.max(fixedAbsolute.y, movingAbsolute.y);
    const padding = STRAIGHT_LINE_PADDING;
    const width = Math.max(STRAIGHT_LINE_MIN_LENGTH, Math.round(maxX - minX + padding * 2));
    const height = Math.max(STRAIGHT_LINE_MIN_LENGTH, Math.round(maxY - minY + padding * 2));
    const position = {
      x: Math.round(minX - padding),
      y: Math.round(minY - padding),
    };
    const toLocal = (point) => ({
      x: Math.round(point.x - position.x),
      y: Math.round(point.y - position.y),
    });

    let changed = false;
    setNodes((nodes) => (
      Array.isArray(nodes)
        ? nodes.map((node) => {
            if (node.id !== nodeId || node?.data?.symbolLabel !== STRAIGHT_LINE_SYMBOL_LABEL) {
              return node;
            }
            changed = true;
            const start = toLocal(fixedAbsolute);
            const end = toLocal(movingAbsolute);
            return {
              ...node,
              position,
              width,
              height,
              data: {
                ...(node.data || {}),
                line: {
                  ...(node.data?.line || {}),
                  width,
                  height,
                  start,
                  end,
                },
              },
              style: {
                ...(node.style || {}),
                width: `${width}px`,
                height: `${height}px`,
              },
            };
          })
        : nodes
    ));
    try {
      reactFlowInstance?.updateNodeInternals?.(nodeId);
    } catch {}
    return changed;
  }, [reactFlowInstance, setNodes]);

  const handleStraightLineResizePointerDown = useCallback(
    (event, endpoint) => {
      if (!isStraightLineToolbar || !toolbarSelectionNode || !reactFlowInstance) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rfNode = reactFlowInstance.getNode(toolbarSelectionNode.id);
      if (!rfNode) {
        return;
      }
      const line = toolbarSelectionNode?.data?.line || rfNode?.data?.line || {};
      const nodeX = rfNode.positionAbsolute?.x ?? rfNode.position?.x ?? toolbarSelectionNode.position?.x ?? 0;
      const nodeY = rfNode.positionAbsolute?.y ?? rfNode.position?.y ?? toolbarSelectionNode.position?.y ?? 0;
      const start = line.start || { x: STRAIGHT_LINE_PADDING, y: STRAIGHT_LINE_PADDING };
      const end = line.end || {
        x: Math.max(STRAIGHT_LINE_PADDING, Number(line.width || rfNode.width || STRAIGHT_LINE_MIN_LENGTH) - STRAIGHT_LINE_PADDING),
        y: Math.max(STRAIGHT_LINE_PADDING, Number(line.height || rfNode.height || STRAIGHT_LINE_MIN_LENGTH) - STRAIGHT_LINE_PADDING),
      };
      const startAbsolute = { x: nodeX + Number(start.x || 0), y: nodeY + Number(start.y || 0) };
      const endAbsolute = { x: nodeX + Number(end.x || 0), y: nodeY + Number(end.y || 0) };
      const fixedAbsolute = endpoint === 'start' ? endAbsolute : startAbsolute;
      const movingOrigin = endpoint === 'start' ? startAbsolute : endAbsolute;
      const target = event.currentTarget;
      const pointerId = event.pointerId;
      try {
        target.setPointerCapture?.(pointerId);
      } catch {}
      skipSelectionOnceRef.current = true;
      setHideDesignResizeHandles(true);
      straightLineResizeStateRef.current = {
        nodeId: toolbarSelectionNode.id,
        fixedAbsolute,
        movingOrigin,
        endpoint,
        changed: false,
      };
      const handlePointerMove = (moveEvent) => {
        const state = straightLineResizeStateRef.current;
        if (!state) return;
        moveEvent.preventDefault();
        const flow = reactFlowInstance.screenToFlowPosition({
          x: moveEvent.clientX,
          y: moveEvent.clientY,
        });
        const rawMovingAbsolute = {
          x: Math.round(flow.x),
          y: Math.round(flow.y),
        };
        const movingAbsolute = moveEvent.shiftKey
          ? lockPointToDominantAxis(state.fixedAbsolute, rawMovingAbsolute)
          : rawMovingAbsolute;
        updateStraightLineGeometry(state.nodeId, state.fixedAbsolute, movingAbsolute);
        state.changed =
          state.changed ||
          movingAbsolute.x !== Math.round(state.movingOrigin.x) ||
          movingAbsolute.y !== Math.round(state.movingOrigin.y);
      };
      const handlePointerUp = () => {
        const state = straightLineResizeStateRef.current;
        window.removeEventListener('pointermove', handlePointerMove, true);
        window.removeEventListener('pointerup', handlePointerUp, true);
        window.removeEventListener('pointercancel', handlePointerUp, true);
        try {
          target.releasePointerCapture?.(pointerId);
        } catch {}
        if (state?.changed) {
          saveCurrentState();
        }
        straightLineResizeStateRef.current = null;
        setHideDesignResizeHandles(false);
      };
      window.addEventListener('pointermove', handlePointerMove, true);
      window.addEventListener('pointerup', handlePointerUp, true);
      window.addEventListener('pointercancel', handlePointerUp, true);
    },
    [isStraightLineToolbar, toolbarSelectionNode, reactFlowInstance, updateStraightLineGeometry, saveCurrentState, lockPointToDominantAxis]
  );

  const renderedNodes = useMemo(() => (
    Array.isArray(rfNodes)
      ? rfNodes.map((node) => {
          const isSelectedDesignNode =
            Array.isArray(toolbarSelectedNodeIds)
            && toolbarSelectedNodeIds.length === 1
            && toolbarSelectedNodeIds[0] === node.id
            && !hideDesignResizeHandles
            && !isDragging;
          const baseNode = (() => {
            if (node?.type !== 'boxNode') return node;
            if (isSpacePressed) {
              return {
                ...node,
                draggable: false,
                selectable: false,
              };
            }
            if (node?.data?.locked === true) {
              return {
                ...node,
                draggable: false,
              };
            }
            return {
              ...node,
              draggable: true,
              selectable: node.selectable === false ? true : node.selectable,
            };
          })();

          if (baseNode?.type === 'boxNode') {
            return {
              ...baseNode,
              data: {
                ...(baseNode.data || {}),
                showResizeHandle: isSelectedDesignNode && isBoxToolbar,
                onResizePointerDown: handleBoxResizePointerDown,
              },
            };
          }
          if (baseNode?.type === 'straightLineNode') {
            return {
              ...baseNode,
              data: {
                ...(baseNode.data || {}),
                showResizeHandle: isSelectedDesignNode && isStraightLineToolbar,
                onResizeEndpointPointerDown: handleStraightLineResizePointerDown,
              },
            };
          }
          if (baseNode?.type === 'symbolDrawerNode') {
            return {
              ...baseNode,
              data: {
                ...(baseNode.data || {}),
                showResizeHandle: isSelectedDesignNode && isSymbolDrawerToolbar,
                onResizePointerDown: handleSymbolDrawerResizePointerDown,
              },
            };
          }
          return baseNode;
        })
      : rfNodes
  ), [
    rfNodes,
    toolbarSelectedNodeIds,
    hideDesignResizeHandles,
    isDragging,
    isSpacePressed,
    isBoxToolbar,
    isStraightLineToolbar,
    isSymbolDrawerToolbar,
    handleBoxResizePointerDown,
    handleStraightLineResizePointerDown,
    handleSymbolDrawerResizePointerDown,
  ]);

  const markSaved = useCallback(() => {
    if (!reactFlowInstance) return;
    const currentState = reactFlowInstance.toObject();
    const templateSettingsSnapshot = JSON.parse(JSON.stringify(templateSettings || {}));
    const currentHash = computeHash(currentState.nodes, currentState.edges, templateSettingsSnapshot);
    lastSavedHashRef.current = currentHash;
    onDirty && onDirty(false);
  }, [reactFlowInstance, onDirty, templateSettings]);

  useEffect(() => {
    if (registerMarkSaved) {
      registerMarkSaved(markSaved);
    }
  }, [registerMarkSaved, markSaved]);

  useEffect(() => {
    if (registerSelectAll) {
      registerSelectAll(selectAllElements);
    }
  }, [registerSelectAll, selectAllElements]);

  useEffect(() => {
    if (registerCloneSelection) {
      registerCloneSelection(cloneSelection);
    }
  }, [registerCloneSelection, cloneSelection]);

  // If Redux edge selection becomes empty (e.g., from inspector button), also clear local highlight only
  useEffect(() => {
    if (!currentSelectedEdges || currentSelectedEdges.length === 0) {
      clearLocalEdgeSelection();
      enforceEdgeDeselection();
      lastSelectionRef.current = { nodeIds: [], edgeIds: [] };
      setSelectedEdgeId(null);
    }
  }, [currentSelectedEdges, clearLocalEdgeSelection, enforceEdgeDeselection]);
  useEffect(() => {
    if (!reactFlowInstance) return;
    const selection = new Set(Array.isArray(currentSelectedEdges) ? currentSelectedEdges : []);
    const applySelection = (edges) => {
      if (!Array.isArray(edges)) return edges;
      let changed = false;
      const next = edges.map((edge) => {
        const shouldSelect = selection.has(edge.id);
        if (!!edge.selected === shouldSelect) {
          return edge;
        }
        changed = true;
        return { ...edge, selected: shouldSelect };
      });
      return changed ? next : edges;
    };
    try {
      reactFlowInstance.setEdges((eds) => applySelection(eds));
    } catch {}
    setEdges((eds) => applySelection(eds));
  }, [reactFlowInstance, currentSelectedEdges, setEdges]);

  // One-shot hard clear trigger (from inspector button)
  const clearTick = useSelector((state) => state.edge.clearTick);
  const lastClearTickRef = useRef(clearTick);
  useEffect(() => {
    if (clearTick !== lastClearTickRef.current) {
      lastClearTickRef.current = clearTick;
      skipSelectionOnceRef.current = true;
      clearLocalEdgeSelection();
      try {
        if (reactFlowInstance && reactFlowInstance.setEdges) {
          reactFlowInstance.setEdges((eds) => (Array.isArray(eds) ? eds.map((e) => ({ ...e, selected: false })) : eds));
        }
        if (reactFlowInstance && reactFlowInstance.setNodes) {
          reactFlowInstance.setNodes((nds) => (Array.isArray(nds) ? nds.map((n) => ({ ...n, selected: false })) : nds));
        }
      } catch {}
      dispatch(clearSelection());
      dispatch(clearEdgeSelection());
      dispatch(setSelectedNodeIds([]));
      dispatch(setSelectedEdgeIds([]));
      lastSelectionRef.current = { nodeIds: [], edgeIds: [] };
    }
  }, [clearTick, clearLocalEdgeSelection, reactFlowInstance, dispatch]);

  // When nothing is selected, return to Elements panel
  useEffect(() => {
    const noNodes = !currentSelectedNodes || currentSelectedNodes.length === 0;
    const noEdges = !currentSelectedEdges || currentSelectedEdges.length === 0;
    if (noNodes && noEdges) {
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
    }
  }, [currentSelectedNodes, currentSelectedEdges, dispatch]);

  const debouncedSaveHistory = useMemo(() => debounce(saveCurrentState, 500), [saveCurrentState]);

  useEffect(() => {
    return () => {
      debouncedSaveHistory.cancel();
    };
  }, [debouncedSaveHistory]);

  const templateSettingsString = useMemo(() => JSON.stringify(templateSettings || {}), [templateSettings]);
  const lastTemplateSettingsRef = useRef(templateSettingsString);
  useEffect(() => {
    if (!reactFlowInstance) return;
    if (lastTemplateSettingsRef.current !== templateSettingsString) {
      lastTemplateSettingsRef.current = templateSettingsString;
      debouncedSaveHistory();
    }
  }, [templateSettingsString, debouncedSaveHistory, reactFlowInstance]);

  // debug: legacy debouncedSaveHistory (unused)
  const spawnAssistSignatureRef = useRef('');

  const instantiateSymbol = useCallback(
    async (rawData, flowPosition) => {
      if (!rawData) return null;
      let parsedData;
      try {
        parsedData = JSON.parse(JSON.stringify(rawData));
      } catch {
        parsedData = { ...(rawData || {}) };
      }

      if (parsedData.isSubdiagram) {
        try {
          const newDiagramPayload = {
            label: 'New Subdiagram',
            description: '',
            parent_id: selectedDiagramId,
          };
          const subdiagram = await dispatch(createDiagram(newDiagramPayload));
          const subdiagramId = subdiagram?.payload?.diagram?.id;
          if (subdiagramId) {
            parsedData = { ...parsedData, subdiagramId };
          }
        } catch {}
      }

      const position = flowPosition ? { ...flowPosition } : { x: 0, y: 0 };
      const type = parsedData.symbolLabel === 'Bus'
        ? 'busNode'
        : parsedData.symbolLabel === 'TextBox'
        ? 'textBoxNode'
        : 'customNode';
      const draftNode = { id: 'draft', type, data: { ...parsedData }, position };
      const snappedPosition = alignNodeByHandle(draftNode);
      const newNode = {
        id: getId(),
        type,
        data: { ...parsedData },
        position: snappedPosition,
      };
      if (type === 'textBoxNode') {
        newNode.zIndex = DESIGN_TEXT_Z_INDEX;
      }

      newNode.data.scaleCm = DEFAULT_ICON_SIZE_PRESET.scaleCm;
      const initialTextSize =
        typeof parsedData.textSizePx === 'number' && parsedData.textSizePx > 0
          ? parsedData.textSizePx
          : globalLabelFontPx;
      newNode.data.textSizePx = initialTextSize;

      const nodesState = (store.getState && store.getState().node && store.getState().node.nodes) || [];
      const suggestedName = getNextSuggestedName(
        parsedData.symbolLabel,
        nodesState,
        parsedData.alias || parsedData.baseAlias || parsedData.variantAlias
      );
      const inspectorInfo = resolveInspectorForNode(newNode.data || {}, symbolLabelOverrides);
      const attributes = {
        ...(inspectorInfo.attributes || {}),
        ...(newNode.data?.attributes || {}),
      };
      if (!attributes.nombre) {
        attributes.nombre = {
          isVisible: true,
          value: suggestedName,
          label: 'Nombre',
        };
      } else {
        attributes.nombre = {
          ...attributes.nombre,
          value: normalizeSuggestedName(
            parsedData.symbolLabel,
            attributes.nombre.value ?? suggestedName,
            parsedData.alias || parsedData.baseAlias || parsedData.variantAlias
          ),
          label: attributes.nombre.label || 'Nombre',
          isVisible: true,
        };
      }
      newNode.data.attributes = attributes;

      setNodes((nds) => nds.concat(newNode));
      dispatch(addNode({ node: newNode }));
      return newNode;
    },
    [alignNodeByHandle, setNodes, dispatch, globalLabelFontPx, selectedDiagramId, symbolLabelOverrides]
  );

  const getSymbolConnectorRoles = useCallback((symbolData) => {
    const roles = new Set();
    const handles = Array.isArray(symbolData?.handles) ? symbolData.handles : [];
    handles.forEach((handle) => {
      const role = (handle?.type || '').toLowerCase();
      if (role === 'source' || role === 'target') {
        roles.add(role);
      }
    });
    if (roles.size === 0) {
      roles.add('source');
      roles.add('target');
    }
    return roles;
  }, []);

  const getCompatibleSpawnTargetRoles = useCallback((symbolData) => {
    const symbolRoles = getSymbolConnectorRoles(symbolData);
    const compatible = [];
    if (symbolRoles.has('source')) compatible.push('target');
    if (symbolRoles.has('target')) compatible.push('source');
    return compatible;
  }, [getSymbolConnectorRoles]);

  const getFirstSymbolHandleIdForRole = useCallback((symbolData, role) => {
    const handles = Array.isArray(symbolData?.handles) ? symbolData.handles : [];
    const exact = handles.find((handle) => (handle?.type || '').toLowerCase() === role);
    return exact?.id || role;
  }, []);

  const isSpawnConnectableNode = useCallback((node) => (
    node?.type === 'customNode' || node?.type === 'busNode'
  ), []);

  const readHandleMetaFromEvent = useCallback((event) => {
    const direct = event?.target && typeof event.target.closest === 'function'
      ? event.target.closest('.react-flow__handle')
      : null;
    const pointed = !direct && typeof document !== 'undefined'
      ? document.elementFromPoint(event.clientX, event.clientY)?.closest?.('.react-flow__handle')
      : null;
    const handleEl = direct || pointed;
    if (!handleEl) return null;
    const ds = handleEl.dataset || {};
    const rawType = (
      ds.handleType ||
      ds.handletype ||
      handleEl.getAttribute('data-handle-type') ||
      handleEl.getAttribute('data-handletype') ||
      (handleEl.classList.contains('react-flow__handle-target') ? 'target' : null) ||
      (handleEl.classList.contains('react-flow__handle-source') ? 'source' : null)
    );
    const role = typeof rawType === 'string' ? rawType.toLowerCase() : null;
    if (role !== 'source' && role !== 'target') return null;
    const nodeEl = handleEl.closest?.('.react-flow__node');
    const nodeId =
      ds.nodeId ||
      ds.nodeid ||
      handleEl.getAttribute('data-nodeid') ||
      handleEl.getAttribute('data-nodeId') ||
      nodeEl?.getAttribute?.('data-id') ||
      nodeEl?.dataset?.id ||
      null;
    const handleId =
      ds.handleId ||
      ds.handleid ||
      handleEl.getAttribute('data-handleid') ||
      handleEl.getAttribute('data-handleId') ||
      handleEl.getAttribute('data-id') ||
      null;
    if (!nodeId) return null;
    return { nodeId, handleId: handleId || role, role };
  }, []);

  const resolveSpawnHandlePlacement = useCallback((targetNode, targetHandle, targetRole = null) => {
    const nodeX = targetNode.positionAbsolute?.x ?? targetNode.position?.x ?? 0;
    const nodeY = targetNode.positionAbsolute?.y ?? targetNode.position?.y ?? 0;
    const nodeW = targetNode.width || 100;
    const nodeH = targetNode.height || 100;
    const center = { x: nodeX + nodeW / 2, y: nodeY + nodeH / 2 };
    const dx = targetHandle.x - center.x;
    const dy = targetHandle.y - center.y;
    const gap = 200;
    const spawnW = 100;
    const spawnH = 100;
    const targetAngle = typeof targetNode?.data?.angle === 'number'
      ? targetNode.data.angle
      : VERTICAL_ORIENTATION;
    const verticalTarget = targetAngle === VERTICAL_ORIENTATION;

    if (verticalTarget) {
      return targetRole === 'target'
        ? { x: targetHandle.x - spawnW / 2, y: targetHandle.y - spawnH - gap }
        : { x: targetHandle.x - spawnW / 2, y: targetHandle.y + gap };
    }

    if (Math.abs(dx) > Math.abs(dy)) {
      return dx < 0
        ? { x: targetHandle.x - spawnW - gap, y: targetHandle.y - spawnH / 2 }
        : { x: targetHandle.x + gap, y: targetHandle.y - spawnH / 2 };
    }
    return targetHandle.x < center.x
      ? { x: targetHandle.x - spawnW - gap, y: targetHandle.y - spawnH / 2 }
      : { x: targetHandle.x + gap, y: targetHandle.y - spawnH / 2 };
  }, []);

  const resolveSpawnHandleTarget = useCallback((event, symbolData) => {
    if (!reactFlowInstance) return null;
    const compatibleRoles = getCompatibleSpawnTargetRoles(symbolData);
    if (compatibleRoles.length === 0) return null;
    const compatibleRoleSet = new Set(compatibleRoles);
    const nodes = reactFlowInstance.getNodes?.() || [];
    const metaFromDom = readHandleMetaFromEvent(event);
    const meta = metaFromDom && compatibleRoleSet.has(metaFromDom.role)
      ? metaFromDom
      : (() => {
          if (typeof event?.clientX !== 'number' || typeof event?.clientY !== 'number') {
            return null;
          }
          let pointer = null;
          try {
            pointer = reactFlowInstance.screenToFlowPosition({
              x: event.clientX,
              y: event.clientY,
            });
          } catch {}
          if (!pointer) return null;
          const zoom = Number.isFinite(Number(viewTransform?.zoom)) && Number(viewTransform.zoom) > 0
            ? Number(viewTransform.zoom)
            : 1;
          const threshold = Math.max(12, 24 / zoom);
          let best = null;
          let bestDistance = Number.POSITIVE_INFINITY;
          nodes.forEach((node) => {
            if (!isSpawnConnectableNode(node)) return;
            enumerateHandles(node).forEach((handle) => {
              const role = (handle?.role || '').toLowerCase();
              if (!compatibleRoleSet.has(role)) return;
              const distance = Math.hypot(pointer.x - handle.x, pointer.y - handle.y);
              if (distance <= threshold && distance < bestDistance) {
                bestDistance = distance;
                best = {
                  nodeId: node.id,
                  handleId: handle.id || role,
                  role,
                };
              }
            });
          });
          return best;
        })();
    if (!meta) return null;
    const targetNode = nodes.find((node) => node.id === meta.nodeId);
    if (!targetNode || !isSpawnConnectableNode(targetNode)) return null;
    const handles = enumerateHandles(targetNode).filter((handle) => (handle?.role || '').toLowerCase() === meta.role);
    if (handles.length === 0) return null;
    const exact = handles.find((handle) => String(handle.id) === String(meta.handleId));
    const targetHandle = exact || handles[0];
    const newRole = meta.role === 'target' ? 'source' : 'target';
    const targetAngle = typeof targetNode?.data?.angle === 'number'
      ? targetNode.data.angle
      : VERTICAL_ORIENTATION;
    const nextSymbolData = targetAngle !== VERTICAL_ORIENTATION
      ? { ...(symbolData || {}), angle: targetAngle }
      : symbolData;
    const newHandleId = getFirstSymbolHandleIdForRole(nextSymbolData, newRole);
    const position = resolveSpawnHandlePlacement(targetNode, targetHandle, meta.role);
    const edgeParams = meta.role === 'target'
      ? {
          sourceHandle: newHandleId,
          target: targetNode.id,
          targetHandle: targetHandle.id || meta.handleId || 'target',
        }
      : {
          source: targetNode.id,
          sourceHandle: targetHandle.id || meta.handleId || 'source',
          targetHandle: newHandleId,
        };
    return { position, edgeParams, symbolData: nextSymbolData, targetNode };
  }, [
    getCompatibleSpawnTargetRoles,
    getFirstSymbolHandleIdForRole,
    isSpawnConnectableNode,
    reactFlowInstance,
    readHandleMetaFromEvent,
    resolveSpawnHandlePlacement,
    viewTransform?.zoom,
  ]);

  const updateSpawnConnectionAssist = useCallback((symbolData) => {
    if (!reactFlowInstance || !symbolData) return;
    const compatibleRoles = getCompatibleSpawnTargetRoles(symbolData);
    if (compatibleRoles.length === 0) {
      spawnAssistSignatureRef.current = '';
      dispatch(setHighlightHandles([]));
      dispatch(clearConnectionAssist());
      return;
    }
    const roleSet = new Set(compatibleRoles);
    const keys = new Set();
    (reactFlowInstance.getNodes?.() || []).forEach((node) => {
      if (!isSpawnConnectableNode(node)) return;
      enumerateHandles(node).forEach((handle) => {
        const role = (handle?.role || '').toLowerCase();
        if (roleSet.has(role)) {
          keys.add(`${node.id}|${handle.id || role}`);
        }
      });
    });
    const nextKeys = Array.from(keys);
    const signature = `${compatibleRoles.join(',')}::${nextKeys.join(',')}`;
    if (spawnAssistSignatureRef.current === signature) return;
    spawnAssistSignatureRef.current = signature;
    dispatch(setHighlightHandles(nextKeys));
    dispatch(setConnectionAssist({ expectedRoles: compatibleRoles }));
  }, [dispatch, getCompatibleSpawnTargetRoles, isSpawnConnectableNode, reactFlowInstance]);

  const clearSpawnConnectionAssist = useCallback(() => {
    spawnAssistSignatureRef.current = '';
    spawnDragPayloadRef.current = null;
    dispatch(setHighlightHandles([]));
    dispatch(clearConnectionAssist());
  }, [dispatch]);

  const placeSpawnSymbolOnHandle = useCallback(async (event, symbolData) => {
    if (spawnPlacementInFlightRef.current) return true;
    const resolved = resolveSpawnHandleTarget(event, symbolData);
    if (!resolved) return false;
    spawnPlacementInFlightRef.current = true;
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof event?.nativeEvent?.stopImmediatePropagation === 'function') {
      event.nativeEvent.stopImmediatePropagation();
    } else if (typeof event?.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
    try {
      const newNode = await instantiateSymbol(resolved.symbolData || symbolData, resolved.position);
      if (!newNode?.id) {
        return false;
      }
      const edge = {
        id: `edge_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        source: resolved.edgeParams.source || newNode.id,
        target: resolved.edgeParams.target || newNode.id,
        sourceHandle: resolved.edgeParams.sourceHandle,
        targetHandle: resolved.edgeParams.targetHandle,
        type: 'orthogonalEdge',
        className: 'edge-color',
        data: {
          waypoints: [],
          disableInspector: true,
        },
      };
      const existingEdges = reactFlowInstance?.getEdges?.() || currentEdges || [];
      const nextEdges = addEdge(edge, existingEdges);
      setEdges(nextEdges);
      dispatch(setEdgesActionCreator({ edges: stripTransientEdgeFields(nextEdges) }));

      const existingNodes = reactFlowInstance?.getNodes?.() || currentNodes || [];
      const nextNodes = [
        ...existingNodes.filter((node) => node.id !== newNode.id),
        { ...newNode, selected: false },
      ];
      selectionSquelchUntilRef.current = Date.now() + 150;
      ignoreSelectionUntilRef.current = Date.now() + 150;
      skipSelectionOnceRef.current = true;
      clearLocalEdgeSelection();
      waypointsRef.current = [];
      drawConnRef.current = { active: false, src: null, waypoints: [], mouse: null, hover: null };
      setDrawConn({ active: false, src: null, waypoints: [], mouse: null, hover: null });
      suppressConnectStartUntilRef.current = Date.now() + CONNECT_CLICK_SUPPRESSION_MS;
      setConnectOnClickEnabled(false);
      window.setTimeout(() => {
        setConnectOnClickEnabled(true);
      }, CONNECT_CLICK_SUPPRESSION_MS + 40);
      try {
        reactFlowInstance?.setEdges?.((edges) =>
          Array.isArray(edges) ? edges.map((item) => ({ ...item, selected: false })) : edges
        );
        reactFlowInstance?.setNodes?.((nodes) =>
          Array.isArray(nodes)
            ? nodes.map((node) => ({ ...node, selected: false }))
            : nodes
        );
      } catch {}
      setNodes((nodes) => {
        const list = Array.isArray(nodes) ? nodes : [];
        const withNewNode = list.some((node) => node.id === newNode.id)
          ? list
          : list.concat(newNode);
        return withNewNode.map((node) => ({ ...node, selected: false }));
      });
      dispatch(clearSelection());
      dispatch(clearEdgeSelection());
      dispatch(setSelectedEdgeIds([]));
      dispatch(setSelectedNodeIds([]));
      dispatch(setCurrentNodeId(null));
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
      lastSelectionRef.current = { nodeIds: [], edgeIds: [] };
      saveCurrentState({ nodes: nextNodes, edges: nextEdges });
      clearSpawnConnectionAssist();
      return true;
    } catch {
      return false;
    } finally {
      spawnPlacementInFlightRef.current = false;
    }
  }, [
    clearLocalEdgeSelection,
    clearSpawnConnectionAssist,
    currentEdges,
    currentNodes,
    dispatch,
    instantiateSymbol,
    reactFlowInstance,
    resolveSpawnHandleTarget,
    saveCurrentState,
    setEdges,
    setNodes,
    stripTransientEdgeFields,
  ]);

  const createBoxNodeInstance = useCallback(
    (placement) => {
      if (!placement || !placement.position || !placement.size) {
        return null;
      }
      const preset = boxTool?.preset || {};
      const isReportBox = !!preset.table;
      const width = Math.max(BOX_MIN_SIZE, Math.round(placement.size.width));
      const height = Math.max(BOX_MIN_SIZE, Math.round(placement.size.height));
      const nodesState =
        (store.getState && store.getState().node && store.getState().node.nodes) || [];
      const suggestedName = getNextSuggestedName(BOX_SYMBOL_LABEL, nodesState);
      const presetAttributes = preset.attributes || {};
      const baseAttributes = {
        strokeWidth: {
          isVisible: true,
          value: presetAttributes.strokeWidth?.value ?? BOX_DEFAULT_STROKE,
          label: 'Ancho de borde (px)',
        },
        strokeColor: {
          isVisible: true,
          value: presetAttributes.strokeColor?.value ?? BOX_DEFAULT_STROKE_COLOR,
          label: 'Color de borde',
          systemDefined: true,
        },
        backgroundColor: {
          isVisible: true,
          value: presetAttributes.backgroundColor?.value ?? BOX_DEFAULT_BACKGROUND_COLOR,
          label: 'Color de fondo',
          systemDefined: true,
        },
        backgroundAlpha: {
          isVisible: true,
          value: presetAttributes.backgroundAlpha?.value ?? BOX_DEFAULT_BACKGROUND_ALPHA,
          label: 'Opacidad de fondo',
          systemDefined: true,
        },
        strokeAlpha: {
          isVisible: true,
          value: presetAttributes.strokeAlpha?.value ?? BOX_DEFAULT_STROKE_ALPHA,
          label: 'Opacidad de borde',
          systemDefined: true,
        },
        columns: {
          isVisible: true,
          value: presetAttributes.columns?.value ?? BOX_DEFAULT_COLUMNS,
          label: 'Columnas',
          systemDefined: true,
        },
        rows: {
          isVisible: true,
          value: presetAttributes.rows?.value ?? BOX_DEFAULT_ROWS,
          label: 'Filas',
          systemDefined: true,
        },
        width: {
          isVisible: true,
          value: width,
          label: 'Ancho (px)',
        },
        height: {
          isVisible: true,
          value: height,
          label: 'Alto (px)',
        },
        borderStyle: {
          isVisible: true,
          value: presetAttributes.borderStyle?.value ?? 'solid',
          label: 'Estilo de borde',
        },
        tableFontSize: {
          isVisible: false,
          value: presetAttributes.tableFontSize?.value ?? BOX_TABLE_DEFAULT_FONT_SIZE,
          label: 'Tamaño de letra',
          systemDefined: true,
        },
        nombre: {
          isVisible: true,
          value: suggestedName,
          label: 'Nombre',
        },
      };

      const nodeData = {
        symbolLabel: BOX_SYMBOL_LABEL,
        attributes: baseAttributes,
      };

      const newNode = {
        id: getId(),
        type: 'boxNode',
        position: {
          x: placement.position.x,
          y: placement.position.y,
        },
        data: {
          ...nodeData,
          table: preset.table || null,
          attributes: baseAttributes,
        },
        zIndex: isReportBox ? DESIGN_REPORT_Z_INDEX : DESIGN_BOX_Z_INDEX,
        className: isReportBox ? 'react-flow__node-box-report' : 'react-flow__node-box-background',
        draggable: true,
        selectable: true,
        width,
        height,
        style: {
          width: `${width}px`,
          height: `${height}px`,
        },
      };

      setNodes((nds) => nds.concat(newNode));
      dispatch(addNode({ node: newNode }));
      dispatch(setSelectedNodeIds([newNode.id]));
      dispatch(setCurrentNodeId(null));
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
      lastSelectionRef.current = { nodeIds: [newNode.id], edgeIds: [] };
      selectionSquelchUntilRef.current = Date.now() + 120;
      saveCurrentState();
      return newNode;
    },
    [setNodes, dispatch, saveCurrentState, boxTool?.preset]
  );

  const createStraightLineNodeInstance = useCallback(
    (placement) => {
      if (!placement || !placement.position || !placement.size) {
        return null;
      }
      const color = normalizeHexColor(straightLineTool?.color) || STRAIGHT_LINE_DEFAULT_COLOR;
      const strokeWidthValue = Number(straightLineTool?.strokeWidth);
      const strokeWidth = Number.isFinite(strokeWidthValue)
        ? Math.min(24, Math.max(1, Math.round(strokeWidthValue)))
        : STRAIGHT_LINE_DEFAULT_STROKE;
      const nodesState =
        (store.getState && store.getState().node && store.getState().node.nodes) || [];
      const suggestedName = getNextSuggestedName(STRAIGHT_LINE_SYMBOL_LABEL, nodesState);
      const width = Math.max(1, Math.round(placement.size.width));
      const height = Math.max(1, Math.round(placement.size.height));
      const newNode = {
        id: getId(),
        type: 'straightLineNode',
        position: {
          x: placement.position.x,
          y: placement.position.y,
        },
        data: {
          symbolLabel: STRAIGHT_LINE_SYMBOL_LABEL,
          line: {
            color,
            strokeWidth,
            width,
            height,
            start: placement.start,
            end: placement.finish,
          },
          attributes: {
            strokeColor: {
              isVisible: true,
              value: color,
              label: 'Color',
              systemDefined: true,
            },
            strokeWidth: {
              isVisible: true,
              value: strokeWidth,
              label: 'Grosor',
              systemDefined: true,
            },
            nombre: {
              isVisible: true,
              value: suggestedName,
              label: 'Nombre',
            },
          },
        },
        zIndex: DESIGN_BOX_Z_INDEX + 1,
        draggable: true,
        selectable: true,
        width,
        height,
      };

      setNodes((nds) => nds.concat(newNode));
      dispatch(addNode({ node: newNode }));
      dispatch(setSelectedNodeIds([newNode.id]));
      dispatch(setCurrentNodeId(null));
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
      lastSelectionRef.current = { nodeIds: [newNode.id], edgeIds: [] };
      selectionSquelchUntilRef.current = Date.now() + 120;
      saveCurrentState();
      return newNode;
    },
    [setNodes, dispatch, saveCurrentState, straightLineTool?.color, straightLineTool?.strokeWidth]
  );

  const createSymbolDrawerNodeInstance = useCallback(
    (position) => {
      if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
        return null;
      }
      const nodesState =
        (store.getState && store.getState().node && store.getState().node.nodes) || [];
      const suggestedName = getNextSuggestedName(SYMBOL_DRAWER_SYMBOL_LABEL, nodesState);
      const drawerHeight = getSymbolDrawerContentHeight(
        getUsedSymbolDrawerItems(nodesState).length,
        SYMBOL_DRAWER_DEFAULT_WIDTH,
      );
      const newNode = {
        id: getId(),
        type: 'symbolDrawerNode',
        position: {
          x: position.x,
          y: position.y,
        },
        data: {
          symbolLabel: SYMBOL_DRAWER_SYMBOL_LABEL,
          drawer: {
            width: SYMBOL_DRAWER_DEFAULT_WIDTH,
            height: drawerHeight,
          },
          attributes: {
            nombre: {
              isVisible: false,
              value: suggestedName,
              label: 'Nombre',
            },
          },
        },
        zIndex: DESIGN_REPORT_Z_INDEX,
        draggable: true,
        selectable: true,
        width: SYMBOL_DRAWER_DEFAULT_WIDTH,
        height: drawerHeight,
        style: {
          width: `${SYMBOL_DRAWER_DEFAULT_WIDTH}px`,
          height: `${drawerHeight}px`,
        },
      };

      setNodes((nds) => nds.concat(newNode));
      dispatch(addNode({ node: newNode }));
      dispatch(setSelectedNodeIds([newNode.id]));
      dispatch(setCurrentNodeId(null));
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
      lastSelectionRef.current = { nodeIds: [newNode.id], edgeIds: [] };
      selectionSquelchUntilRef.current = Date.now() + 120;
      saveCurrentState();
      return newNode;
    },
    [setNodes, dispatch, saveCurrentState]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    try {
      const payload = event.dataTransfer.getData('application/reactflow');
      if (!payload) return;
      const parsedData = JSON.parse(payload);
      updateSpawnConnectionAssist(parsedData);
    } catch {}
  }, [updateSpawnConnectionAssist]);

  const onDragLeave = useCallback((event) => {
    const related = event.relatedTarget;
    if (related && event.currentTarget?.contains?.(related)) {
      return;
    }
    clearSpawnConnectionAssist();
  }, [clearSpawnConnectionAssist]);

  useEffect(() => {
    const handleSymbolDragStart = (event) => {
      const payload = event?.detail?.payload;
      if (payload) {
        spawnDragPayloadRef.current = payload;
        updateSpawnConnectionAssist(payload);
      }
    };
    const handleSymbolDragEnd = () => {
      spawnDragPayloadRef.current = null;
      clearSpawnConnectionAssist();
    };
    window.addEventListener('sololine:symbol-drag-start', handleSymbolDragStart);
    window.addEventListener('sololine:symbol-drag-end', handleSymbolDragEnd);
    return () => {
      window.removeEventListener('sololine:symbol-drag-start', handleSymbolDragStart);
      window.removeEventListener('sololine:symbol-drag-end', handleSymbolDragEnd);
    };
  }, [clearSpawnConnectionAssist, updateSpawnConnectionAssist]);

  // Event Handlers
  const onDrop = useCallback(
    async (event) => {
      event.preventDefault();
      const payload = event.dataTransfer.getData('application/reactflow');
      if (!payload || !reactFlowInstance) return;

      let parsedData;
      try {
        parsedData = JSON.parse(payload);
      } catch {
        parsedData = null;
      }
      if (!parsedData) return;

      const handledByConnector = await placeSpawnSymbolOnHandle(event, parsedData);
      if (handledByConnector) {
        window.dispatchEvent(new CustomEvent('sololine:symbol-drop-complete'));
        dispatch(clearSpawnPreview());
        return;
      }

      const bounds = reactFlowWrapper.current?.getBoundingClientRect();
      const x = bounds ? event.clientX - bounds.left : event.clientX;
      const y = bounds ? event.clientY - bounds.top : event.clientY;
      const position = reactFlowInstance.project({ x, y });
      const newNode = await instantiateSymbol(parsedData, position);
      if (newNode?.id) {
        selectionSquelchUntilRef.current = Date.now() + 150;
        ignoreSelectionUntilRef.current = Date.now() + 150;
        skipSelectionOnceRef.current = true;
        clearLocalEdgeSelection();
        try {
          reactFlowInstance.setEdges?.((eds) =>
            Array.isArray(eds) ? eds.map((edge) => ({ ...edge, selected: false })) : eds
          );
          reactFlowInstance.setNodes?.((nds) =>
            Array.isArray(nds)
              ? nds.map((node) => ({ ...node, selected: node.id === newNode.id }))
              : nds
          );
        } catch {}
        setEdges((eds) =>
          Array.isArray(eds) ? eds.map((edge) => ({ ...edge, selected: false })) : eds
        );
        setNodes((nds) =>
          Array.isArray(nds)
            ? nds.map((node) => ({ ...node, selected: node.id === newNode.id }))
            : nds
        );
        dispatch(clearEdgeSelection());
        dispatch(setSelectedEdgeIds([]));
        dispatch(setSelectedNodeIds([newNode.id]));
        dispatch(setCurrentNodeId(newNode.id));
        lastSelectionRef.current = { nodeIds: [newNode.id], edgeIds: [] };
        applyInspectorForNode(newNode);
      }
      window.dispatchEvent(new CustomEvent('sololine:symbol-drop-complete'));
      dispatch(clearSpawnPreview());
      clearSpawnConnectionAssist();
    },
    [
      reactFlowInstance,
      instantiateSymbol,
      dispatch,
      clearLocalEdgeSelection,
      setEdges,
      setNodes,
      applyInspectorForNode,
      placeSpawnSymbolOnHandle,
      clearSpawnConnectionAssist,
    ]
  );

  // Track last mouse position over the canvas and its flow coordinates
  const [lastMouseFlow, setLastMouseFlowState] = useState({
    x: 0,
    y: 0,
    screenX: 0,
    screenY: 0,
    inside: false,
  });
  const [isBoxShiftPressed, setIsBoxShiftPressed] = useState(false);
  const lastMouseFlowRef = useRef(lastMouseFlow);
  const lastMouseThrottlerRef = useRef({
    x: null,
    y: null,
    screenX: null,
    screenY: null,
    inside: null,
    ts: 0,
  });
  const updateLastMouseFlow = useCallback((next) => {
    lastMouseFlowRef.current = next;
    setLastMouseFlowState((prev) => {
      if (
        prev &&
        prev.x === next.x &&
        prev.y === next.y &&
        prev.screenX === next.screenX &&
        prev.screenY === next.screenY &&
        prev.inside === next.inside
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const el = reactFlowWrapper.current;
    if (!el || !reactFlowInstance) return;
    const syncPointerState = (e, insideOverride = null) => {
      try {
        const rect = el.getBoundingClientRect();
        const inside = insideOverride ?? (
          e.clientX >= rect.left
          && e.clientX <= rect.right
          && e.clientY >= rect.top
          && e.clientY <= rect.bottom
        );
        const flow = reactFlowInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const now = Date.now();
        const last = lastMouseThrottlerRef.current;
        const deltaScreen = Math.max(Math.abs((last.screenX ?? e.clientX) - e.clientX), Math.abs((last.screenY ?? e.clientY) - e.clientY));
        const deltaFlow = Math.max(Math.abs((last.x ?? flow.x) - flow.x), Math.abs((last.y ?? flow.y) - flow.y));
        const shouldUpdate =
          last.inside !== inside ||
          last.x === null ||
          deltaScreen >= 3 ||
          deltaFlow >= 3 ||
          now - (last.ts || 0) >= 80;
        if (!shouldUpdate) return;
        lastMouseThrottlerRef.current = {
          x: flow.x,
          y: flow.y,
          screenX: e.clientX,
          screenY: e.clientY,
          inside,
          ts: now,
        };
        updateLastMouseFlow({
          x: flow.x,
          y: flow.y,
          screenX: e.clientX,
          screenY: e.clientY,
          inside,
        });
      } catch {}
    };

    const onPointerMove = (e) => {
      syncPointerState(e, true);
    };

    const onPointerEnter = (e) => {
      syncPointerState(e, true);
    };

    const onPointerLeave = (e) => {
      syncPointerState(e, false);
    };

    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerenter', onPointerEnter);
    el.addEventListener('pointerleave', onPointerLeave);
    return () => {
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerenter', onPointerEnter);
      el.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [reactFlowInstance, updateLastMouseFlow]);

  useEffect(() => {
    const wrapper = reactFlowWrapper.current;
    if (!wrapper || !reactFlowInstance) return undefined;

    const getFlowPoint = (event) => {
      try {
        return reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
      } catch {
        return null;
      }
    };

    const isBlockedBoxTarget = (target) => {
      if (!target || typeof target.closest !== 'function') {
        return false;
      }
      return !!target.closest(
        '.react-flow__node, .react-flow__edge, .react-flow__handle, .react-flow__controls, .react-flow__panel, .node-selection-overlay, .box-node__resize, .straight-line-node__resize, .symbol-drawer-node__resize, .diagram-node-context-menu'
      );
    };

    const syncLastPointer = (event, flowPoint) => {
      if (!flowPoint) return;
      updateLastMouseFlow({
        x: flowPoint.x,
        y: flowPoint.y,
        screenX: event.clientX,
        screenY: event.clientY,
        inside: true,
      });
    };

    const completeBoxAtPointer = (event, anchor, forceSquare) => {
      const pointerFlow = getFlowPoint(event);
      syncLastPointer(event, pointerFlow);
      if (!pointerFlow) return false;
      const placement = resolveBoxPlacement(anchor, pointerFlow, forceSquare);
      if (placement) {
        createBoxNodeInstance(placement);
      }
      dispatch(resetBoxTool());
      suppressBoxPaneClickUntilRef.current = Date.now() + 250;
      return true;
    };

    const handlePointerMove = (event) => {
      const interaction = boxPointerInteractionRef.current;
      if (!interaction) return;
      const pointerFlow = getFlowPoint(event);
      syncLastPointer(event, pointerFlow);
      const distance = Math.max(
        Math.abs(event.clientX - interaction.startClientX),
        Math.abs(event.clientY - interaction.startClientY),
      );
      if (distance >= 4) {
        interaction.dragged = true;
      }
    };

    const handlePointerUp = (event) => {
      const interaction = boxPointerInteractionRef.current;
      if (!interaction || event.pointerId !== interaction.pointerId) {
        return;
      }
      boxPointerInteractionRef.current = null;
      document.removeEventListener('pointermove', handlePointerMove, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
      document.removeEventListener('pointercancel', handlePointerUp, true);
      if (interaction.createOnRelease || interaction.dragged) {
        event.preventDefault();
        event.stopPropagation();
        completeBoxAtPointer(
          event,
          interaction.anchor,
          event.shiftKey || isBoxShiftPressed,
        );
      } else {
        suppressBoxPaneClickUntilRef.current = Date.now() + 250;
      }
    };

    const handlePointerDown = (event) => {
      const tool = boxToolRef.current;
      if (!tool?.active || modalIsOpen || event.button !== 0) {
        return;
      }
      if (isBlockedBoxTarget(event.target)) {
        return;
      }
      const pointerFlow = getFlowPoint(event);
      if (!pointerFlow) return;

      event.preventDefault();
      event.stopPropagation();
      syncLastPointer(event, pointerFlow);

      if (!tool.anchor && tool?.preset?.table) {
        const placement = resolveBoxPresetPlacement(pointerFlow, tool.preset);
        if (placement) {
          createBoxNodeInstance(placement);
        }
        dispatch(resetBoxTool());
        suppressBoxPaneClickUntilRef.current = Date.now() + 250;
        return;
      }

      const anchor = tool.anchor || normalizeBoxPoint(pointerFlow) || pointerFlow;
      if (!tool.anchor) {
        dispatch(setBoxToolAnchor(anchor));
      }

      boxPointerInteractionRef.current = {
        pointerId: event.pointerId,
        anchor,
        startClientX: event.clientX,
        startClientY: event.clientY,
        dragged: false,
        createOnRelease: !!tool.anchor,
      };
      document.addEventListener('pointermove', handlePointerMove, true);
      document.addEventListener('pointerup', handlePointerUp, true);
      document.addEventListener('pointercancel', handlePointerUp, true);
    };

    wrapper.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      wrapper.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('pointermove', handlePointerMove, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
      document.removeEventListener('pointercancel', handlePointerUp, true);
      boxPointerInteractionRef.current = null;
    };
  }, [
    reactFlowInstance,
    modalIsOpen,
    dispatch,
    updateLastMouseFlow,
    normalizeBoxPoint,
    resolveBoxPlacement,
    resolveBoxPresetPlacement,
    createBoxNodeInstance,
    isBoxShiftPressed,
  ]);

  useEffect(() => {
    const wrapper = reactFlowWrapper.current;
    if (!wrapper || !reactFlowInstance) return undefined;

    const getFlowPoint = (event) => {
      try {
        return reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
      } catch {
        return null;
      }
    };

    const isBlockedTarget = (target) => {
      if (!target || typeof target.closest !== 'function') {
        return false;
      }
      return !!target.closest(
        '.react-flow__node, .react-flow__edge, .react-flow__handle, .react-flow__controls, .react-flow__panel, .node-selection-overlay, .box-node__resize, .straight-line-node__resize, .symbol-drawer-node__resize, .diagram-node-context-menu'
      );
    };

    const syncLastPointer = (event, flowPoint) => {
      if (!flowPoint) return;
      updateLastMouseFlow({
        x: flowPoint.x,
        y: flowPoint.y,
        screenX: event.clientX,
        screenY: event.clientY,
        inside: true,
      });
    };

    const completeStraightLineAtPointer = (event, anchor) => {
      const pointerFlow = getFlowPoint(event);
      syncLastPointer(event, pointerFlow);
      if (!pointerFlow) return false;
      const placement = resolveStraightLinePlacement(anchor, pointerFlow, event.shiftKey || isBoxShiftPressed);
      if (placement) {
        createStraightLineNodeInstance(placement);
      }
      dispatch(resetStraightLineTool());
      suppressStraightLinePaneClickUntilRef.current = Date.now() + 250;
      return true;
    };

    const handlePointerMove = (event) => {
      const interaction = straightLinePointerInteractionRef.current;
      if (!interaction) return;
      const pointerFlow = getFlowPoint(event);
      syncLastPointer(event, pointerFlow);
      const distance = Math.max(
        Math.abs(event.clientX - interaction.startClientX),
        Math.abs(event.clientY - interaction.startClientY),
      );
      if (distance >= 4) {
        interaction.dragged = true;
      }
    };

    const handlePointerUp = (event) => {
      const interaction = straightLinePointerInteractionRef.current;
      if (!interaction || event.pointerId !== interaction.pointerId) {
        return;
      }
      straightLinePointerInteractionRef.current = null;
      document.removeEventListener('pointermove', handlePointerMove, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
      document.removeEventListener('pointercancel', handlePointerUp, true);
      if (interaction.createOnRelease || interaction.dragged) {
        event.preventDefault();
        event.stopPropagation();
        completeStraightLineAtPointer(event, interaction.anchor);
      } else {
        suppressStraightLinePaneClickUntilRef.current = Date.now() + 250;
      }
    };

    const handlePointerDown = (event) => {
      const tool = straightLineToolRef.current;
      if (!tool?.active || modalIsOpen || event.button !== 0) {
        return;
      }
      if (isBlockedTarget(event.target)) {
        return;
      }
      const pointerFlow = getFlowPoint(event);
      if (!pointerFlow) return;

      event.preventDefault();
      event.stopPropagation();
      syncLastPointer(event, pointerFlow);

      const anchor = tool.anchor || normalizeStraightLinePoint(pointerFlow) || pointerFlow;
      if (!tool.anchor) {
        dispatch(setStraightLineToolAnchor(anchor));
      }

      straightLinePointerInteractionRef.current = {
        pointerId: event.pointerId,
        anchor,
        startClientX: event.clientX,
        startClientY: event.clientY,
        dragged: false,
        createOnRelease: !!tool.anchor,
      };
      document.addEventListener('pointermove', handlePointerMove, true);
      document.addEventListener('pointerup', handlePointerUp, true);
      document.addEventListener('pointercancel', handlePointerUp, true);
    };

    wrapper.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      wrapper.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('pointermove', handlePointerMove, true);
      document.removeEventListener('pointerup', handlePointerUp, true);
      document.removeEventListener('pointercancel', handlePointerUp, true);
      straightLinePointerInteractionRef.current = null;
    };
  }, [
    reactFlowInstance,
    modalIsOpen,
    dispatch,
    updateLastMouseFlow,
    isBoxShiftPressed,
    normalizeStraightLinePoint,
    resolveStraightLinePlacement,
    createStraightLineNodeInstance,
  ]);

  useEffect(() => {
    if (!straightLineTool?.active || modalIsOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        dispatch(resetStraightLineTool());
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [straightLineTool?.active, dispatch, modalIsOpen]);

  // Handle spawn requests from Sidebar (Enter in search)
  const spawnRequest = useSelector((state) => state.view.spawnRequest);
  const spawnPreview = useSelector((state) => state.view.spawnPreview);
  useEffect(() => {
    if (spawnPreview?.data) {
      updateSpawnConnectionAssist(spawnPreview.data);
      return;
    }
    if (!drawConnRef.current?.active) {
      clearSpawnConnectionAssist();
    }
  }, [clearSpawnConnectionAssist, spawnPreview, updateSpawnConnectionAssist]);

  useEffect(() => {
    if (!spawnRequest) return;
    const { data, placementMode = 'auto' } = spawnRequest;
    if (!data) {
      dispatch(clearSpawnRequest());
      return;
    }

    const execute = async () => {
      try {
        if (!reactFlowInstance) return;
        const lf = lastMouseFlowRef.current || { x: 0, y: 0, inside: false };
        let position;

        if (placementMode === 'center') {
          const wrapperRect = reactFlowWrapper.current?.getBoundingClientRect();
          if (wrapperRect) {
            position = reactFlowInstance.project({
              x: wrapperRect.width / 2,
              y: wrapperRect.height / 2,
            });
          } else {
            position = { x: 0, y: 0 };
          }
        } else if (lf.inside) {
          position = { x: lf.x, y: lf.y };
        } else {
          const wrapperRect = reactFlowWrapper.current?.getBoundingClientRect();
          if (wrapperRect) {
            position = reactFlowInstance.project({
              x: wrapperRect.width / 2,
              y: wrapperRect.height / 2,
            });
          } else {
            position = { x: 0, y: 0 };
          }
        }

        await instantiateSymbol(data, position);
      } catch {}
      dispatch(clearSpawnPreview());
      dispatch(clearSpawnRequest());
    };

    execute();
  }, [spawnRequest, instantiateSymbol, dispatch, reactFlowInstance]);

  useEffect(() => {
    if (!boxTool?.active || modalIsOpen) {
      setIsBoxShiftPressed(false);
      return;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Shift') {
        setIsBoxShiftPressed(true);
      } else if (event.key === 'Escape') {
        dispatch(resetBoxTool());
      }
    };
    const handleKeyUp = (event) => {
      if (event.key === 'Shift') {
        setIsBoxShiftPressed(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [boxTool?.active, dispatch, modalIsOpen]);

  const isConnectionAllowed = useCallback((params) => {
    if (!params?.source || !params?.target) {
      return false;
    }
    return params.source !== params.target;
  }, []);

  const onConnect = useCallback(
    (params) => {
        if (!isConnectionAllowed(params)) {
          cancelConnectRef.current = false;
          waypointsRef.current = [];
          drawConnRef.current = { active: false, src: null, waypoints: [], mouse: null, hover: null };
          setDrawConn({ active: false, src: null, waypoints: [], mouse: null, hover: null });
          clearValidHoverHandle();
          dispatch(setHighlightHandles([]));
          dispatch(clearConnectionAssist());
          return;
        }
        if (cancelConnectRef.current) {
          cancelConnectRef.current = false;
          waypointsRef.current = [];
          drawConnRef.current = { active: false, src: null, waypoints: [], mouse: null, hover: null };
          setDrawConn({ active: false, src: null, waypoints: [], mouse: null, hover: null });
          clearValidHoverHandle();
          dispatch(setHighlightHandles([]));
          dispatch(clearConnectionAssist());
          return;
        }
      const snap = drawConnRef.current;
      const wpsRef = Array.isArray(waypointsRef.current) ? waypointsRef.current.slice() : [];
      // If finishing on a bus node without a specific handle id, select the nearest handle
      const rf = reactFlowInstance;
      let srcHandleOverride = null;
      let tgtHandleOverride = null;
      try {
        const nodes = rf?.getNodes?.() || [];
        const srcNode = nodes.find(n => n.id === params.source);
        const tgtNode = nodes.find(n => n.id === params.target);
        const refPt = (snap && snap.mouse) ? snap.mouse : null;
        const pickNearest = (node, role) => {
          if (!node || node.type !== 'busNode' || !refPt) return null;
          const list = enumerateHandles(node) || [];
          const sameRole = list.filter(h => (h.role || '').toLowerCase() === role);
          if (sameRole.length === 0) return null;
          let best = sameRole[0];
          let bestD = Math.hypot(refPt.x - best.x, refPt.y - best.y);
          for (let i = 1; i < sameRole.length; i++) {
            const h = sameRole[i];
            const d = Math.hypot(refPt.x - h.x, refPt.y - h.y);
            if (d < bestD) { best = h; bestD = d; }
          }
          return String(best.id);
        };
        if (!params.targetHandle) tgtHandleOverride = pickNearest(tgtNode, 'target');
        if (!params.sourceHandle) srcHandleOverride = pickNearest(srcNode, 'source');
      } catch {}

      const baseEdge = {
        ...params,
        ...(srcHandleOverride ? { sourceHandle: srcHandleOverride } : {}),
        ...(tgtHandleOverride ? { targetHandle: tgtHandleOverride } : {}),
        type: 'orthogonalEdge',
        className: 'edge-color',
        data: {
          waypoints: [],
          disableInspector: true,
        }
      };
      const existingEdges = reactFlowInstance?.getEdges?.() || currentEdges || [];
      const haveWps = wpsRef.length > 0 || (snap && Array.isArray(snap.waypoints) && snap.waypoints.length > 0);
      let nextEdges;
      if (haveWps) {
        let finalWps = (wpsRef.length > 0 ? wpsRef : snap.waypoints) || [];
        // When a connection starts from a target handle we collect elbows in reverse order, so flip them
        if (snap?.src?.role === 'target' && finalWps.length > 0) {
          finalWps = [...finalWps].reverse();
        }
        const customId = `edge_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const edgeWithWps = {
          id: customId,
          ...baseEdge,
          data: { ...(baseEdge.data || {}), waypoints: finalWps.map(p => ({ x: p.x, y: p.y })) },
        };
        nextEdges = [...existingEdges, edgeWithWps];
      } else {
        // fallback to default behavior
        nextEdges = addEdge(baseEdge, existingEdges);
      }
      setEdges(nextEdges);
      dispatch(setEdgesActionCreator({ edges: stripTransientEdgeFields(nextEdges) }));
      if (snap && snap.active) setDrawConn({ active: false, src: null, waypoints: [], mouse: null, hover: null });
      waypointsRef.current = [];
      suppressConnectStartUntilRef.current = Date.now() + CONNECT_CLICK_SUPPRESSION_MS;
      clearValidHoverHandle();
      dispatch(setHighlightHandles([]));
      dispatch(clearConnectionAssist());
      saveCurrentState();
    },
    [clearValidHoverHandle, currentEdges, dispatch, enumerateHandles, isConnectionAllowed, reactFlowInstance, saveCurrentState, setDrawConn, setEdges, stripTransientEdgeFields]
  );

  const beginLiveConnection = useCallback((event, params) => {
    try {
      if (isSpawnPlacementActive()) {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (typeof event?.nativeEvent?.stopImmediatePropagation === 'function') {
          event.nativeEvent.stopImmediatePropagation();
        } else if (typeof event?.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        waypointsRef.current = [];
        drawConnRef.current = { active: false, src: null, waypoints: [], mouse: null, hover: null };
        setDrawConn({ active: false, src: null, waypoints: [], mouse: null, hover: null });
        return;
      }
      if (Date.now() < (suppressConnectStartUntilRef.current || 0)) {
        return;
      }
      if (drawConnRef.current?.active) return;
      const { nodeId, handleId, handleType } = params || {};
      if (!nodeId || (handleType !== 'source' && handleType !== 'target')) return;
      const resolvedHandleId = (() => {
        if (handleId) return handleId;
        const el = event?.target && typeof event.target.closest === 'function'
          ? event.target.closest('.react-flow__handle')
          : null;
        if (!el) return handleType === 'source' ? 'source' : 'target';
        const ds = el.dataset || {};
        return (
          ds.handleId ||
          ds.handleid ||
          el.getAttribute('data-handleid') ||
          el.getAttribute('data-handleId') ||
          el.getAttribute('data-id') ||
          (handleType === 'source' ? 'source' : 'target')
        );
      })();
      const key = `${nodeId}|${resolvedHandleId || (handleType === 'source' ? 'source' : 'target')}`;
      const originRole = handleType === 'target' ? 'target' : 'source';
      const expectedRole = originRole === 'source' ? 'target' : 'source';
      dispatch(setHighlightHandles([key]));
      dispatch(setConnectionAssist({
        expectedRole,
        originNodeId: nodeId,
        originHandleId: resolvedHandleId,
      }));
      const rf = reactFlowInstance;
      const node = (rf?.getNodes?.() || []).find((n) => n.id === nodeId);
      if (!node) return;
      const role = originRole;
      let base = null;
      try {
        const list = enumerateHandles(node) || [];
        const exact = list.find((h) => (h.role || '').toLowerCase() === role && String(h.id) === String(resolvedHandleId));
        if (exact) base = { x: exact.x, y: exact.y };
        else {
          const sameRole = list.filter((h) => (h.role || '').toLowerCase() === role);
          if (sameRole.length > 0) {
            const mid = sameRole[Math.floor(sameRole.length / 2)];
            base = { x: mid.x, y: mid.y };
          }
        }
      } catch {}
      if (!base) {
        const x = (node.positionAbsolute?.x ?? node.position?.x ?? 0);
        const y = (node.positionAbsolute?.y ?? node.position?.y ?? 0);
        const w = node.width || 0;
        const h = node.height || 0;
        const ang = typeof node?.data?.angle === 'number' ? node.data.angle : VERTICAL_ORIENTATION;
        const vertical = ang === VERTICAL_ORIENTATION;
        base = vertical
          ? (role === 'source' ? { x: x + w / 2, y: y + h } : { x: x + w / 2, y })
          : (role === 'source' ? { x: x + w, y: y + h / 2 } : { x, y: y + h / 2 });
      }
      waypointsRef.current = [];
      const nextDrawConn = { active: true, src: { nodeId, handleId: resolvedHandleId, role, base }, waypoints: [], mouse: base, hover: null };
      drawConnRef.current = nextDrawConn;
      setDrawConn(nextDrawConn);
    } catch {}
  }, [dispatch, enumerateHandles, isSpawnPlacementActive, reactFlowInstance]);

  // Begin a connect gesture when user starts from a handle
  const onConnectStart = useCallback((event, params) => {
    beginLiveConnection(event, params);
  }, [beginLiveConnection]);

  const onConnectEnd = useCallback(() => {
    if (drawConnRef.current?.active) return;
    clearValidHoverHandle();
    dispatch(setHighlightHandles([]));
    dispatch(clearConnectionAssist());
  }, [clearValidHoverHandle, dispatch]);

  useEffect(() => {
    if (drawConn.active) return;
    clearValidHoverHandle();
    dispatch(setHighlightHandles([]));
    dispatch(clearConnectionAssist());
  }, [clearValidHoverHandle, dispatch, drawConn.active]);

  useEffect(() => {
    if (!drawConn.active || !drawConn.src?.nodeId) return;
    const originRole = drawConn.src.role === 'target' ? 'target' : 'source';
    const expectedRole = originRole === 'source' ? 'target' : 'source';
    dispatch(setConnectionAssist({
      expectedRole,
      originNodeId: drawConn.src.nodeId,
      originHandleId: drawConn.src.handleId || null,
    }));
  }, [dispatch, drawConn.active, drawConn.src]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const activeSpawnPayload = getActiveSpawnPlacementPayload();
      if (!activeSpawnPayload) return;
      void placeSpawnSymbolOnHandle(event, activeSpawnPayload).then((handled) => {
        if (handled) {
          window.dispatchEvent(new CustomEvent('sololine:symbol-drop-complete'));
          dispatch(clearSpawnPreview());
        }
      });
    };
    const handleClick = (event) => {
      if (Date.now() < (suppressConnectStartUntilRef.current || 0)) return;
      const activeSpawnPayload = getActiveSpawnPlacementPayload();
      if (activeSpawnPayload) {
        void placeSpawnSymbolOnHandle(event, activeSpawnPayload).then((handled) => {
          if (handled) {
            window.dispatchEvent(new CustomEvent('sololine:symbol-drop-complete'));
            dispatch(clearSpawnPreview());
          }
        });
        return;
      }
      const handleEl = event.target?.closest?.('.react-flow__handle');
      if (!handleEl) return;
      if (drawConnRef.current?.active) return;
      const ds = handleEl.dataset || {};
      const handleType = (
        ds.handleType ||
        ds.handletype ||
        handleEl.getAttribute('data-handle-type') ||
        handleEl.getAttribute('data-handletype') ||
        (handleEl.classList.contains('react-flow__handle-target') ? 'target' : null) ||
        (handleEl.classList.contains('react-flow__handle-source') ? 'source' : null)
      );
      const nodeId = ds.nodeId || ds.nodeid || handleEl.getAttribute('data-nodeid') || handleEl.getAttribute('data-nodeId');
      const handleId = ds.handleId || ds.handleid || handleEl.getAttribute('data-handleid') || handleEl.getAttribute('data-handleId') || handleEl.getAttribute('data-id');
      if (!nodeId || (handleType !== 'source' && handleType !== 'target')) return;
      beginLiveConnection(event, { nodeId, handleId, handleType });
    };
    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('click', handleClick, true);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('click', handleClick, true);
    };
  }, [beginLiveConnection, dispatch, getActiveSpawnPlacementPayload, placeSpawnSymbolOnHandle]);

  const onEdgeClick = useCallback((event, edge) => {
    if (!edge) return;
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }

    const alreadySoloSelected = Array.isArray(currentSelectedEdges)
      && currentSelectedEdges.length === 1
      && currentSelectedEdges[0] === edge.id;

    if (alreadySoloSelected) {
      ignoreSelectionUntilRef.current = Date.now() + 120;
      skipSelectionOnceRef.current = true;
      dispatch(clearNodeSelection());
      dispatch(clearEdgeSelection());
      dispatch(setSelectedEdgeIds([]));
      setSelectedEdgeId(null);
      clearLocalEdgeSelection();
      try {
        if (reactFlowInstance && reactFlowInstance.setEdges) {
          reactFlowInstance.setEdges((eds) =>
            Array.isArray(eds) ? eds.map((e) => ({ ...e, selected: false })) : eds
          );
        }
      } catch {}
      lastSelectionRef.current = { nodeIds: [], edgeIds: [] };
      return;
    }

    dispatch(clearNodeSelection());
    setSelectedEdgeId(edge.id);
    dispatch(selectSingleEdge(edge.id));
    dispatch(setSelectedEdgeIds([edge.id]));
    try {
      reactFlowInstance?.setNodes?.((nds) =>
        Array.isArray(nds) ? nds.map((n) => ({ ...n, selected: false })) : nds
      );
      setEdges((eds) =>
        Array.isArray(eds)
          ? eds.map((e) => (e.id === edge.id ? { ...e, selected: true } : { ...e, selected: false }))
          : eds
      );
    } catch {}
    lastSelectionRef.current = { nodeIds: [], edgeIds: [edge.id] };
  }, [currentSelectedEdges, dispatch, setEdges, reactFlowInstance, clearLocalEdgeSelection]);

  const cancelSearchIfActive = useCallback(() => {
    try {
      const st = (store.getState && store.getState()) || null;
      const editorState = st && st.editor;
      const isForced = !!(editorState && editorState.forceSearchResults);
      const hasQuery = !!(editorState && editorState.searchQuery && editorState.searchQuery.trim().length > 0);
      if (!isForced && !hasQuery) {
        return false;
      }
    } catch {}
    dispatch(search({ query: '' }));
    dispatch(setForceSearchResults(false));
    dispatch(setSearchSelectedIndex(0));
    dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
    return true;
  }, [dispatch]);


  const clearAllSelection = useCallback(() => {
    justSelectedRef.current = false;
    paneClearedRef.current = true;
    ignoreSelectionUntilRef.current = Date.now() + 150;
    clearLocalEdgeSelection();
    try {
      if (reactFlowInstance && reactFlowInstance.setEdges) {
        reactFlowInstance.setEdges((eds) =>
          Array.isArray(eds) ? eds.map((e) => ({ ...e, selected: false })) : eds
        );
      }
      if (reactFlowInstance && reactFlowInstance.setNodes) {
        reactFlowInstance.setNodes((nds) =>
          Array.isArray(nds) ? nds.map((n) => ({ ...n, selected: false })) : nds
        );
      }
    } catch {}
    try {
      setNodes((nds) =>
        Array.isArray(nds)
          ? nds.map((n) => (n && n.selected ? { ...n, selected: false } : n))
          : nds
      );
    } catch {}
    skipSelectionOnceRef.current = true;
    dispatch(clearSelection());
    dispatch(clearEdgeSelection());
    dispatch(setSelectedEdgeIds([]));
    dispatch(setSelectedNodeIds([]));
    setSelectedTemplateBlockIds([]);
    dispatch(selectTemplateBlock(null));
    dispatch(setCurrentNodeId(null));
    dispatch(setLabelPositionEditNodeId(null));
    dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
    dispatch(clearSpawnPreview());
    dispatch(resetBoxTool());
    dispatch(resetStraightLineTool());
    dispatch(resetSymbolDrawerTool());
    lastSelectionRef.current = { nodeIds: [], edgeIds: [] };
    cancelSearchIfActive();
  }, [dispatch, clearLocalEdgeSelection, enforceEdgeDeselection, reactFlowInstance, setNodes, cancelSearchIfActive]);


  const onPaneClick = useCallback(async (evt) => {
    setNodeContextMenu(null);
    if (boxTool?.active) {
      if (Date.now() < suppressBoxPaneClickUntilRef.current) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      if (!reactFlowInstance) {
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
      const pointerFlow = reactFlowInstance.screenToFlowPosition({
        x: evt.clientX,
        y: evt.clientY,
      });
      if (!boxTool.anchor && boxTool?.preset?.table) {
        const placement = resolveBoxPresetPlacement(pointerFlow, boxTool.preset);
        if (placement) {
          createBoxNodeInstance(placement);
        }
        dispatch(resetBoxTool());
        return;
      }
      if (!boxTool.anchor) {
        const anchorPoint = normalizeBoxPoint(pointerFlow) || pointerFlow;
        dispatch(setBoxToolAnchor(anchorPoint));
        return;
      }
      const placement = resolveBoxPlacement(
        boxTool.anchor,
        pointerFlow,
        evt.shiftKey || isBoxShiftPressed,
      );
      if (placement) {
        createBoxNodeInstance(placement);
      }
      dispatch(resetBoxTool());
      return;
    }
    if (straightLineTool?.active) {
      if (Date.now() < suppressStraightLinePaneClickUntilRef.current) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      if (!reactFlowInstance) {
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
      const pointerFlow = reactFlowInstance.screenToFlowPosition({
        x: evt.clientX,
        y: evt.clientY,
      });
      if (!straightLineTool.anchor) {
        const anchorPoint = normalizeStraightLinePoint(pointerFlow) || pointerFlow;
        dispatch(setStraightLineToolAnchor(anchorPoint));
        return;
      }
      const placement = resolveStraightLinePlacement(
        straightLineTool.anchor,
        pointerFlow,
        evt.shiftKey || isBoxShiftPressed,
      );
      if (placement) {
        createStraightLineNodeInstance(placement);
      }
      dispatch(resetStraightLineTool());
      return;
    }
    if (symbolDrawerTool?.active) {
      if (!reactFlowInstance) {
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
      const pointerFlow = reactFlowInstance.screenToFlowPosition({
        x: evt.clientX,
        y: evt.clientY,
      });
      const position = normalizeBoxPoint(pointerFlow) || pointerFlow;
      createSymbolDrawerNodeInstance(position);
      dispatch(resetSymbolDrawerTool());
      return;
    }
    if (spawnPreview && spawnPreview.data) {
      const handledByConnector = await placeSpawnSymbolOnHandle(evt, spawnPreview.data);
      if (handledByConnector) {
        window.dispatchEvent(new CustomEvent('sololine:symbol-drop-complete'));
        dispatch(clearSpawnPreview());
        return;
      }
      if (reactFlowInstance && reactFlowWrapper.current) {
        const bounds = reactFlowWrapper.current.getBoundingClientRect();
        const projected = reactFlowInstance.project({
          x: evt.clientX - bounds.left,
          y: evt.clientY - bounds.top,
        });
        void instantiateSymbol(spawnPreview.data, projected);
      }
      window.dispatchEvent(new CustomEvent('sololine:symbol-drop-complete'));
      dispatch(clearSpawnPreview());
      return;
    }
    const hadEdgeSelectionOnly =
      !hasNodeSelection &&
      ((Array.isArray(currentSelectedEdgesRef.current) && currentSelectedEdgesRef.current.length > 0) ||
        edgeSelectionCount > 0);
    if (hadEdgeSelectionOnly) {
      emulateActiveColorClick();
      // Keep clearing selection so the edge doesn't immediately reselect itself
    }
    // If a selection was just made via marquee, don't immediately clear it
    if (justSelectedRef.current && Date.now() < justSelectedUntilRef.current) {
      justSelectedRef.current = false;
      justSelectedUntilRef.current = 0;
      return;
    }
    justSelectedRef.current = false;
    justSelectedUntilRef.current = 0;
    // While drawing, we capture elbows via global listeners only (avoid duplicate adds)
    if (drawConn.active) return;
    // Force-clear selection regardless of current focus (e.g., inspector input focused)
    paneClearedRef.current = true;
    ignoreSelectionUntilRef.current = Date.now() + 150;
    // Also clear React Flow local selection state on edges to avoid lingering highlight
    clearLocalEdgeSelection();
    enforceEdgeDeselection();
    // Clear React Flow internal selection (selection-only change)
    try {
      if (reactFlowInstance && reactFlowInstance.setEdges) {
        reactFlowInstance.setEdges((eds) => (Array.isArray(eds) ? eds.map((e) => ({ ...e, selected: false })) : eds));
      }
      if (reactFlowInstance && reactFlowInstance.setNodes) {
        reactFlowInstance.setNodes((nds) => (Array.isArray(nds) ? nds.map((n) => ({ ...n, selected: false })) : nds));
      }
    } catch {}
    try {
      setNodes((nds) =>
        Array.isArray(nds)
          ? nds.map((n) => (n && n.selected ? { ...n, selected: false } : n))
          : nds
      );
    } catch {}
    // Select first node briefly to force RF to drop edge selection, then clear
    skipSelectionOnceRef.current = true;
    dispatch(clearSelection());
    dispatch(clearEdgeSelection());
    dispatch(setSelectedEdgeIds([]));
    dispatch(setSelectedNodeIds([]));
    setSelectedTemplateBlockIds([]);
    dispatch(selectTemplateBlock(null));
    setSelectedEdgeId(null);
    lastSelectionRef.current = { nodeIds: [], edgeIds: [] };
    try {
      const activeEl = document.activeElement;
      if (activeEl && typeof activeEl.blur === 'function') {
        activeEl.blur();
      }
    } catch {}
    const cancelled = cancelSearchIfActive();
    if (!cancelled) {
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
    }
  }, [
    dispatch,
    clearLocalEdgeSelection,
    enforceEdgeDeselection,
    setNodes,
    drawConn.active,
    reactFlowInstance,
    cancelSearchIfActive,
    spawnPreview,
    instantiateSymbol,
    placeSpawnSymbolOnHandle,
    boxTool?.active,
    boxTool?.anchor,
    boxTool?.preset,
    straightLineTool?.active,
    straightLineTool?.anchor,
    symbolDrawerTool?.active,
    normalizeBoxPoint,
    resolveBoxPlacement,
    resolveBoxPresetPlacement,
    normalizeStraightLinePoint,
    resolveStraightLinePlacement,
    createBoxNodeInstance,
    createStraightLineNodeInstance,
    createSymbolDrawerNodeInstance,
    isBoxShiftPressed,
    hasNodeSelection,
    edgeSelectionCount,
    emulateActiveColorClick,
  ]);

  const closeNodeContextMenu = useCallback(() => {
    setNodeContextMenu(null);
  }, []);

  const canShowTextPositionActionForNode = useCallback((node) => {
    if (!node?.id) return false;
    const symbol = node?.data?.symbolLabel;
    return symbol !== BOX_SYMBOL_LABEL
      && symbol !== STRAIGHT_LINE_SYMBOL_LABEL
      && symbol !== SYMBOL_DRAWER_SYMBOL_LABEL;
  }, []);

  const selectSingleNodeForContextMenu = useCallback((node) => {
    if (!node?.id) return;
    selectionSquelchUntilRef.current = Date.now() + 120;
    ignoreSelectionUntilRef.current = Date.now() + 120;
    skipSelectionOnceRef.current = true;
    clearLocalEdgeSelection();
    try {
      if (reactFlowInstance?.setEdges) {
        reactFlowInstance.setEdges((eds) =>
          Array.isArray(eds) ? eds.map((edge) => ({ ...edge, selected: false })) : eds
        );
      }
      if (reactFlowInstance?.setNodes) {
        reactFlowInstance.setNodes((nds) =>
          Array.isArray(nds)
            ? nds.map((currentNode) => ({ ...currentNode, selected: currentNode.id === node.id }))
            : nds
        );
      }
    } catch {}
    setEdges((eds) =>
      Array.isArray(eds) ? eds.map((edge) => ({ ...edge, selected: false })) : eds
    );
    setNodes((nds) =>
      Array.isArray(nds)
        ? nds.map((currentNode) => ({ ...currentNode, selected: currentNode.id === node.id }))
        : nds
    );
    dispatch(clearEdgeSelection());
    dispatch(setSelectedEdgeIds([]));
    dispatch(setSelectedNodeIds([node.id]));
    dispatch(setCurrentNodeId(isInspectorlessDesignNode(node) ? null : node.id));
    lastSelectionRef.current = { nodeIds: [node.id], edgeIds: [] };
    applyInspectorForNode(node);
  }, [applyInspectorForNode, clearLocalEdgeSelection, dispatch, reactFlowInstance, setEdges, setNodes]);

  const openContextMenuAtPosition = useCallback((event, actions, isMultiSelection = false, nodeId = null) => {
    if (!Array.isArray(actions) || actions.length === 0) {
      setNodeContextMenu(null);
      return;
    }

    const viewportPadding = 12;
    const estimatedMenuWidth = 180;
    const estimatedMenuHeight = actions.reduce((total, action) => total + (action.type === 'separator' ? 12 : 42), 16);
    const nextLeft = Math.min(
      event.clientX,
      Math.max(viewportPadding, window.innerWidth - estimatedMenuWidth - viewportPadding),
    );
    const nextTop = Math.min(
      event.clientY,
      Math.max(viewportPadding, window.innerHeight - estimatedMenuHeight - viewportPadding),
    );

    setNodeContextMenu({
      nodeId,
      left: nextLeft,
      top: nextTop,
      actions,
      isMultiSelection,
    });
  }, []);

  const buildTemplateFromSelection = useCallback(() => {
    const nodeIds = new Set(Array.isArray(toolbarSelectedNodeIds) ? toolbarSelectedNodeIds : []);
    const sourceNodes = reactFlowInstance?.getNodes?.() || (Array.isArray(currentNodes) ? currentNodes : []);
    const selectedSourceNodes = (Array.isArray(sourceNodes) ? sourceNodes : [])
      .filter((node) => node?.id && nodeIds.has(node.id));
    const selectedBlocks = Array.isArray(selectedTemplateBlocks) ? selectedTemplateBlocks : [];
    const selectionCount = selectedSourceNodes.length + selectedBlocks.length;
    if (selectionCount < 2) {
      return null;
    }

    const boundsEntries = [];
    selectedSourceNodes.forEach((node) => {
      const position = node.position || { x: 0, y: 0 };
      const width = Number.isFinite(Number(node.width)) ? Number(node.width) : 100;
      const height = Number.isFinite(Number(node.height)) ? Number(node.height) : 100;
      boundsEntries.push({
        x: Number(position.x) || 0,
        y: Number(position.y) || 0,
        width,
        height,
      });
    });
    selectedBlocks.forEach((block) => {
      const position = getTemplateBlockPosition(block);
      const size = getTemplateBlockSize(block);
      boundsEntries.push({
        x: position.x,
        y: position.y,
        width: size.width,
        height: size.height,
      });
    });

    const minX = Math.min(...boundsEntries.map((item) => item.x));
    const minY = Math.min(...boundsEntries.map((item) => item.y));
    const maxX = Math.max(...boundsEntries.map((item) => item.x + item.width));
    const maxY = Math.max(...boundsEntries.map((item) => item.y + item.height));

    const nodes = selectedSourceNodes.map((node) => {
      const cloned = deepCloneTemplateValue(node) || {};
      const position = cloned.position || { x: 0, y: 0 };
      return {
        ...cloned,
        position: {
          x: (Number(position.x) || 0) - minX,
          y: (Number(position.y) || 0) - minY,
        },
        selected: false,
        dragging: false,
        positionAbsolute: undefined,
      };
    });

    const selectedEdgeIds = new Set(Array.isArray(currentSelectedEdges) ? currentSelectedEdges : []);
    const edges = (Array.isArray(currentEdges) ? currentEdges : [])
      .filter((edge) => {
        if (!edge?.source || !edge?.target) return false;
        if (selectedEdgeIds.has(edge.id)) return true;
        return nodeIds.has(edge.source) && nodeIds.has(edge.target);
      })
      .map((edge) => ({
        ...(deepCloneTemplateValue(edge) || {}),
        selected: false,
      }));

    const blocks = selectedBlocks.map((block) => {
      const cloned = deepCloneTemplateValue(block) || {};
      const position = getTemplateBlockPosition(cloned);
      if (cloned.type === 'image') {
        cloned.imageX = position.x - minX;
        cloned.imageY = position.y - minY;
      } else {
        cloned.textX = position.x - minX;
        cloned.textY = position.y - minY;
      }
      return cloned;
    });

    return {
      version: 1,
      count: selectionCount,
      bounds: {
        width: Math.max(1, maxX - minX),
        height: Math.max(1, maxY - minY),
      },
      nodes,
      edges,
      templateBlocks: blocks,
    };
  }, [
    currentEdges,
    currentNodes,
    currentSelectedEdges,
    reactFlowInstance,
    selectedTemplateBlocks,
    toolbarSelectedNodeIds,
  ]);

  const handleCreateTemplateFromSelection = useCallback(async () => {
    const payload = buildTemplateFromSelection();
    if (!payload) {
      window.alert('Selecciona 2 o más elementos para crear una plantilla.');
      return;
    }
    if (typeof onCreateTemplate !== 'function') {
      window.alert('No se pudo crear la plantilla.');
      return;
    }
    try {
      await onCreateTemplate(payload);
    } catch (err) {
      window.alert(err?.data?.message || err?.message || 'No se pudo crear la plantilla.');
    }
  }, [buildTemplateFromSelection, onCreateTemplate]);

  const insertEditorTemplate = useCallback((template) => {
    const payload = template?.payload || template;
    if (!payload || typeof payload !== 'object') {
      window.alert('No se pudo insertar la plantilla.');
      return;
    }
    const sourceNodes = Array.isArray(payload.nodes) ? payload.nodes : [];
    const sourceEdges = Array.isArray(payload.edges) ? payload.edges : [];
    const sourceBlocks = Array.isArray(payload.templateBlocks) ? payload.templateBlocks : [];
    if (sourceNodes.length + sourceBlocks.length === 0) {
      window.alert('La plantilla no tiene elementos para insertar.');
      return;
    }

    const wrapperBounds = reactFlowWrapper.current?.getBoundingClientRect?.();
    const centerPoint = wrapperBounds
      ? {
          x: wrapperBounds.left + wrapperBounds.width / 2,
          y: wrapperBounds.top + wrapperBounds.height / 2,
        }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const flowCenter = reactFlowInstance?.screenToFlowPosition
      ? reactFlowInstance.screenToFlowPosition(centerPoint)
      : { x: 0, y: 0 };
    const templateWidth = Number(payload.bounds?.width) || 300;
    const templateHeight = Number(payload.bounds?.height) || 200;
    const insertOrigin = {
      x: flowCenter.x - templateWidth / 2,
      y: flowCenter.y - templateHeight / 2,
    };

    const existingNodes = reactFlowInstance?.getNodes?.() || (Array.isArray(currentNodes) ? currentNodes : []);
    const existingNodeIds = new Set((Array.isArray(existingNodes) ? existingNodes : [])
      .map((node) => node?.id)
      .filter(Boolean));
    const nodeIdMap = new Map();
    const nextSelectedNodeIds = [];
    const newNodes = sourceNodes.map((sourceNode) => {
      const cloned = cloneNode
        ? cloneNode(deepCloneTemplateValue(sourceNode), existingNodeIds)
        : deepCloneTemplateValue(sourceNode);
      if (!cloned) return null;
      const newId = cloned.id || createEditorTemplateElementId('node');
      existingNodeIds.add(newId);
      nodeIdMap.set(sourceNode.id, newId);
      nextSelectedNodeIds.push(newId);
      return {
        ...cloned,
        id: newId,
        position: {
          x: insertOrigin.x + (Number(sourceNode.position?.x) || 0),
          y: insertOrigin.y + (Number(sourceNode.position?.y) || 0),
        },
        selected: true,
        dragging: false,
        positionAbsolute: undefined,
      };
    }).filter(Boolean);

    const existingEdges = Array.isArray(currentEdges) ? currentEdges : [];
    const newEdges = sourceEdges
      .map((edge) => {
        const source = nodeIdMap.get(edge?.source);
        const target = nodeIdMap.get(edge?.target);
        if (!source || !target) return null;
        return {
          ...(deepCloneTemplateValue(edge) || {}),
          id: createEditorTemplateElementId('edge'),
          source,
          target,
          selected: false,
        };
      })
      .filter(Boolean);

    const newBlockIds = [];
    const newBlocks = sourceBlocks.map((block) => {
      const cloned = deepCloneTemplateValue(block) || {};
      const nextId = createEditorTemplateElementId('tmpl');
      const rowId = createEditorTemplateElementId('tmpl_row');
      newBlockIds.push(nextId);
      const next = {
        ...cloned,
        id: nextId,
        rowId,
        rowIndex: 0,
        rowColumns: 1,
      };
      const position = getTemplateBlockPosition(cloned);
      if (next.type === 'image') {
        next.imageX = insertOrigin.x + position.x;
        next.imageY = insertOrigin.y + position.y;
      } else {
        next.textX = insertOrigin.x + position.x;
        next.textY = insertOrigin.y + position.y;
      }
      return next;
    });

    const nextNodes = [
      ...(Array.isArray(existingNodes) ? existingNodes : []).map((node) => ({ ...node, selected: false })),
      ...newNodes,
    ];
    const nextEdges = [
      ...existingEdges.map((edge) => ({ ...edge, selected: false })),
      ...newEdges,
    ];
    const nextBlocks = [
      ...(Array.isArray(templateSettings.blocks) ? templateSettings.blocks : []),
      ...newBlocks,
    ];

    try {
      reactFlowInstance?.setNodes?.(nextNodes);
      reactFlowInstance?.setEdges?.(nextEdges);
    } catch {}
    setNodes(nextNodes);
    setEdges(nextEdges);
    dispatch(setNodesActionCreator({ nodes: nextNodes }));
    dispatch(setEdgesActionCreator({ edges: nextEdges }));
    dispatch(setTemplateBlocks(nextBlocks));
    dispatch(setSelectedNodeIds(nextSelectedNodeIds));
    dispatch(setSelectedEdgeIds([]));
    setSelectedTemplateBlockIds(newBlockIds);
    if (newBlockIds[0]) {
      dispatch(selectTemplateBlock(newBlockIds[0]));
    }
    lastSelectionRef.current = { nodeIds: nextSelectedNodeIds, edgeIds: [] };
    requestAnimationFrame(() => saveCurrentState());
  }, [
    cloneNode,
    currentEdges,
    currentNodes,
    dispatch,
    reactFlowInstance,
    saveCurrentState,
    setEdges,
    setNodes,
    templateSettings.blocks,
  ]);

  useEffect(() => {
    if (registerInsertTemplate) {
      registerInsertTemplate(insertEditorTemplate);
    }
  }, [registerInsertTemplate, insertEditorTemplate]);

  const getMultiSelectionContextMenuActions = useCallback(() => ([
    {
      key: 'align-column',
      label: 'Alinear columna',
      onClick: handleAlignVertical,
    },
    {
      key: 'align-row',
      label: 'Alinear fila',
      onClick: handleAlignHorizontal,
    },
    {
      key: 'separator-selection',
      type: 'separator',
    },
    {
      key: 'out-of-service',
      label: selectedNodesOutOfService ? 'En Servicio' : 'Fuera de servicio',
      onClick: () => handleToggleOutOfService(!selectedNodesOutOfService),
    },
    {
      key: 'clone',
      label: 'Clonar',
      onClick: cloneSelection,
    },
    {
      key: 'create-template',
      label: 'Crear plantilla',
      onClick: handleCreateTemplateFromSelection,
    },
    {
      key: 'delete',
      label: 'Eliminar',
      onClick: () => handleDeleteSelected(),
    },
  ]), [cloneSelection, handleAlignHorizontal, handleAlignVertical, handleToggleOutOfService, handleCreateTemplateFromSelection, handleDeleteSelected, selectedNodesOutOfService]);

  const handleToggleBoxLockedById = useCallback((nodeId, nextLocked) => {
    if (!nodeId) return;
    setNodes((nodes) => {
      let didChange = false;
      const updated = nodes.map((node) => {
        if (node.id !== nodeId || node?.data?.symbolLabel !== BOX_SYMBOL_LABEL) {
          return node;
        }
        didChange = true;
        return {
          ...node,
          draggable: !nextLocked,
          data: {
            ...(node.data || {}),
            locked: nextLocked,
          },
        };
      });
      return didChange ? updated : nodes;
    });
    requestAnimationFrame(() => saveCurrentState());
  }, [setNodes, saveCurrentState]);

  const cloneSingleNodeById = useCallback((nodeId) => {
    if (!nodeId || typeof cloneNode !== 'function' || typeof setNodes !== 'function') return;
    const sourceNodes = reactFlowInstance?.getNodes?.() || (Array.isArray(currentNodes) ? currentNodes : []);
    const sourceNode = (Array.isArray(sourceNodes) ? sourceNodes : []).find((entry) => entry?.id === nodeId);
    if (!sourceNode) return;
    const existingNodeIds = new Set((Array.isArray(sourceNodes) ? sourceNodes : [])
      .map((entry) => entry?.id)
      .filter(Boolean));
    const cloned = cloneNode(JSON.parse(JSON.stringify(sourceNode)), existingNodeIds);
    if (!cloned?.id) return;
    const nextNodes = [
      ...(Array.isArray(sourceNodes) ? sourceNodes : []).map((entry) => ({
        ...entry,
        selected: false,
      })),
      {
        ...cloned,
        selected: true,
      },
    ];
    try {
      reactFlowInstance?.setNodes?.(nextNodes);
    } catch {}
    setNodes(nextNodes);
    dispatch(setNodesActionCreator({ nodes: nextNodes }));
    dispatch(setSelectedNodeIds([cloned.id]));
    dispatch(setSelectedEdgeIds([]));
    lastSelectionRef.current = { nodeIds: [cloned.id], edgeIds: [] };
    requestAnimationFrame(() => saveCurrentState());
  }, [cloneNode, currentNodes, dispatch, reactFlowInstance, setNodes, saveCurrentState]);

  const disconnectEdgesForNode = useCallback((nodeId) => {
    if (!nodeId) return;
    const nextEdges = Array.isArray(currentEdges)
      ? currentEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
      : [];
    if (nextEdges.length === currentEdges.length) return;
    const resetEdges = nextEdges.map((edge) => ({
      ...edge,
      selected: false,
    }));
    setEdges(resetEdges);
    if (reactFlowInstance && typeof reactFlowInstance.setEdges === 'function') {
      try {
        reactFlowInstance.setEdges(resetEdges);
      } catch {}
    }
    dispatch(setEdgesActionCreator({ edges: stripTransientEdgeFields(resetEdges) }));
    dispatch(clearEdgeSelection());
    dispatch(setSelectedEdgeIds([]));
    requestAnimationFrame(() => saveCurrentState());
  }, [currentEdges, setEdges, reactFlowInstance, dispatch, stripTransientEdgeFields, saveCurrentState]);

  const handleNodeContextMenu = useCallback((event, node) => {
    if (drawConn.active) {
      return;
    }
    if (!reactFlowWrapper.current || !node?.id) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const isAlreadySoloSelected =
      Array.isArray(currentSelectedNodes)
      && currentSelectedNodes.length === 1
      && currentSelectedNodes[0] === node.id;
    const isAlreadyInMultiSelection =
      Array.isArray(currentSelectedNodes)
      && currentSelectedNodes.length > 1
      && currentSelectedNodes.includes(node.id);

    if (!isAlreadySoloSelected && !isAlreadyInMultiSelection) {
      selectSingleNodeForContextMenu(node);
    }

    const actions = isAlreadyInMultiSelection
      ? getMultiSelectionContextMenuActions()
      : [
          node?.data?.symbolLabel === BOX_SYMBOL_LABEL
            ? null
            : {
                key: 'disconnect-node',
                label: 'Desconectar',
                onClick: () => disconnectEdgesForNode(node.id),
              },
          canShowTextPositionActionForNode(node)
            ? {
                key: 'text-position',
                label: 'Mover texto',
                onClick: () => handleStartTextPositionEditForNode(node),
              }
            : null,
          node?.data?.symbolLabel === BOX_SYMBOL_LABEL
            ? null
            : {
                key: 'out-of-service',
                label: node?.data?.outOfService === true ? 'En Servicio' : 'Fuera de servicio',
                onClick: () => setNodesOutOfServiceById([node.id], node?.data?.outOfService !== true),
              },
          node?.data?.symbolLabel === BOX_SYMBOL_LABEL
            ? {
                key: 'lock-box',
                label: node?.data?.locked === true ? 'Soltar' : 'Fijar',
                onClick: () => handleToggleBoxLockedById(node.id, node?.data?.locked !== true),
              }
            : null,
          {
            key: 'clone',
            label: 'Clonar',
            onClick: () => cloneSingleNodeById(node.id),
          },
          {
            key: 'delete',
            label: 'Eliminar',
            onClick: () => handleDeleteSelected([node.id], []),
          },
        ].filter(Boolean);

    if (actions.length === 0) {
      setNodeContextMenu(null);
      return;
    }

    openContextMenuAtPosition(event, actions, isAlreadyInMultiSelection, node.id);
  }, [
    canShowTextPositionActionForNode,
    cloneSingleNodeById,
    cloneSelection,
    currentSelectedEdges,
    currentSelectedNodes,
    disconnectEdgesForNode,
    drawConn.active,
    getMultiSelectionContextMenuActions,
    handleDeleteSelected,
    handleToggleBoxLockedById,
    handleStartTextPositionEditForNode,
    openContextMenuAtPosition,
    selectSingleNodeForContextMenu,
    setNodesOutOfServiceById,
  ]);

  const handlePaneContextMenu = useCallback((event) => {
    if (drawConn.active) {
      return;
    }
    if (!reactFlowWrapper.current) {
      return;
    }
    const selectedCount =
      (Array.isArray(toolbarSelectedNodeIds) ? toolbarSelectedNodeIds.length : 0)
      + (Array.isArray(selectedTemplateBlockIds) ? selectedTemplateBlockIds.length : 0);
    if (selectedCount < 2) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    openContextMenuAtPosition(event, getMultiSelectionContextMenuActions(), true, null);
  }, [drawConn.active, getMultiSelectionContextMenuActions, openContextMenuAtPosition, selectedTemplateBlockIds, toolbarSelectedNodeIds]);

  const handleTemplateTextContextMenu = useCallback((event, block) => {
    if (!block?.id) return;
    event.preventDefault();
    event.stopPropagation();
    const isPartOfMultiSelection =
      Array.isArray(selectedTemplateBlockIds)
      && selectedTemplateBlockIds.includes(block.id)
      && ((Array.isArray(selectedTemplateBlockIds) ? selectedTemplateBlockIds.length : 0)
        + (Array.isArray(toolbarSelectedNodeIds) ? toolbarSelectedNodeIds.length : 0)) > 1;
    if (isPartOfMultiSelection) {
      openContextMenuAtPosition(event, getMultiSelectionContextMenuActions(), true, block.id);
      return;
    }
    openContextMenuAtPosition(event, [
      {
        key: 'duplicate-template-text',
        label: 'Clonar',
        onClick: () => dispatch(duplicateTemplateBlock(block.id)),
      },
    ], false, block.id);
  }, [dispatch, getMultiSelectionContextMenuActions, openContextMenuAtPosition, selectedTemplateBlockIds, toolbarSelectedNodeIds]);

  const handleTemplateImageContextMenu = useCallback((event, block) => {
    if (!block?.id) return;
    event.preventDefault();
    event.stopPropagation();
    const isPartOfMultiSelection =
      Array.isArray(selectedTemplateBlockIds)
      && selectedTemplateBlockIds.includes(block.id)
      && ((Array.isArray(selectedTemplateBlockIds) ? selectedTemplateBlockIds.length : 0)
        + (Array.isArray(toolbarSelectedNodeIds) ? toolbarSelectedNodeIds.length : 0)) > 1;
    if (isPartOfMultiSelection) {
      openContextMenuAtPosition(event, getMultiSelectionContextMenuActions(), true, block.id);
      return;
    }
    openContextMenuAtPosition(event, [
      {
        key: 'duplicate-template-image',
        label: 'Clonar',
        onClick: () => dispatch(duplicateTemplateBlock(block.id)),
      },
    ], false, block.id);
  }, [dispatch, getMultiSelectionContextMenuActions, openContextMenuAtPosition, selectedTemplateBlockIds, toolbarSelectedNodeIds]);

  const handleCanvasContextMenuCapture = useCallback((event) => {
    if (drawConn.active) {
      return;
    }
    const selectedCount =
      (Array.isArray(toolbarSelectedNodeIds) ? toolbarSelectedNodeIds.length : 0)
      + (Array.isArray(selectedTemplateBlockIds) ? selectedTemplateBlockIds.length : 0);
    if (selectedCount < 2) {
      return;
    }
    const target = event.target;
    if (!target || typeof target.closest !== 'function') {
      return;
    }
    if (target.closest('.react-flow__node') || target.closest('.react-flow__edge') || target.closest('.react-flow__handle')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    openContextMenuAtPosition(event, getMultiSelectionContextMenuActions(), true, null);
  }, [drawConn.active, getMultiSelectionContextMenuActions, openContextMenuAtPosition, selectedTemplateBlockIds, toolbarSelectedNodeIds]);

  const handleCanvasClickCapture = useCallback((event) => {
    if (drawConn.active || boxTool?.active || straightLineTool?.active || symbolDrawerTool?.active || spawnPreview) {
      return;
    }
    if (justSelectedRef.current && Date.now() < justSelectedUntilRef.current) {
      return;
    }
    const hasAnySelection =
      (Array.isArray(currentSelectedNodes) && currentSelectedNodes.length > 0)
      || (Array.isArray(currentSelectedEdges) && currentSelectedEdges.length > 0)
      || selectedTemplateBlockIds.length > 0
      || !!selectedTemplateBlockId
      || edgeSelectionCount > 0;
    if (!hasAnySelection) {
      return;
    }
    const target = event.target;
    if (!target || typeof target.closest !== 'function') {
      return;
    }
    const interactiveSelector = [
      '.react-flow__node',
      '.react-flow__edge',
      '.react-flow__handle',
      '.react-flow__controls',
      '.react-flow__panel',
      '.react-flow__minimap',
      '.node-selection-overlay',
      '.box-node__resize',
      '.straight-line-node__resize',
      '.symbol-drawer-node__resize',
      '.diagram-node-context-menu',
      '.template-floating-text',
      '.template-floating-image',
      '.template-anchor-block',
      '.template-builder-panel',
      '#menu-bar-overlay-root',
      'button',
      'input',
      'select',
      'textarea',
      '[contenteditable="true"]',
    ].join(', ');
    if (target.closest(interactiveSelector)) {
      return;
    }
    clearAllSelection();
  }, [
    boxTool?.active,
    straightLineTool?.active,
    symbolDrawerTool?.active,
    clearAllSelection,
    currentSelectedEdges,
    currentSelectedNodes,
    drawConn.active,
    edgeSelectionCount,
    selectedTemplateBlockId,
    selectedTemplateBlockIds,
    spawnPreview,
  ]);

  useEffect(() => {
    if (!nodeContextMenu) return undefined;

    const handlePointerDown = (event) => {
      if (event.target?.closest?.('.diagram-node-context-menu')) {
        return;
      }
      setNodeContextMenu(null);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setNodeContextMenu(null);
      }
    };

    const handleViewportChange = () => {
      setNodeContextMenu(null);
    };

    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [nodeContextMenu]);


  useEffect(() => {
    if (selectedEdgeId) {
      setEdges((edges) =>
        edges.map((edge) =>
          edge.id === selectedEdgeId
            ? { ...edge }
            : edge
        )
      );
    }
  }, [selectedEdgeId, setEdges]);  

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (modalIsOpen) {
        return;
      }
      // Ignore canvas shortcuts while keyboard focus or the event target is an editable control.
      const isTyping = (() => {
        const editableElements = [event.target, document.activeElement].filter(Boolean);
        return editableElements.some((element) => {
          const tag = (element.tagName || '').toLowerCase();
          if (tag === 'input' || tag === 'textarea' || element.isContentEditable) {
            return true;
          }
          return typeof element.closest === 'function' && !!element.closest('input, textarea, [contenteditable="true"]');
        });
      })();
      if (!event.ctrlKey && !event.metaKey && !event.altKey && event.key === 'Escape') {
        if (spawnPreview) {
          event.preventDefault();
          dispatch(clearSpawnPreview());
          return;
        }
        if (symbolDrawerTool?.active) {
          event.preventDefault();
          dispatch(resetSymbolDrawerTool());
          return;
        }
        if (drawConnRef.current?.active) {
          event.preventDefault();
          waypointsRef.current = [];
          setDrawConn({ active: false, src: null, waypoints: [], mouse: null, hover: null });
          dispatch(setHighlightHandles([]));
          cancelConnectRef.current = true;
          return;
        }
      }
      // Enter on canvas -> open clean search focused (only when mouse is inside canvas and not typing)
      if (!event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey && event.key === 'Enter') {
        const st = (store.getState && store.getState()) || null;
        const inside = !!(lastMouseFlowRef.current && lastMouseFlowRef.current.inside);
        if (!isTyping && inside) {
          event.preventDefault();
          const toggleButton = document.querySelector('.sidebar-toggle');
          if (toggleButton && toggleButton.classList.contains('closed') && typeof toggleButton.click === 'function') {
            toggleButton.click();
          }
          dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
          dispatch(search({ query: '' }));
          dispatch(setForceSearchResults(true));
          dispatch(setSearchSelectedIndex(0));
          try {
            const el = document.querySelector('.searchBarInput');
            if (el && typeof el.focus === 'function') {
              el.focus();
              if (typeof el.select === 'function') el.select();
            }
          } catch {}
          return;
        }
      }

      // Toggle Inspector drawer (I)
      if (!event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey && (event.key === 'i' || event.key === 'I')) {
        if (!isTyping) {
          event.preventDefault();
          const toggleButton = document.querySelector('.sidebar-toggle');
          if (toggleButton && typeof toggleButton.click === 'function') {
            toggleButton.click();
          }
          return;
        }
      }

      // Select all elements (Ctrl/Cmd + A)
      if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && (event.key === 'a' || event.key === 'A')) {
        if (!isTyping) {
          event.preventDefault();
          selectAllElements();
        }
        return;
      }

      // Clone selection (Ctrl/Cmd + C)
      if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && (event.key === 'c' || event.key === 'C')) {
        if (!isTyping) {
          event.preventDefault();
          cloneSelection();
        }
        return;
      }

      // Rotate selected nodes (R)
      if (!event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey && (event.key === 'r' || event.key === 'R')) {
        if (!isTyping && Array.isArray(toolbarSelectedNodeIds) && toolbarSelectedNodeIds.length > 0) {
          event.preventDefault();
          if (toolbarSelectedNodeIds.length === 1 && toolbarCurrentNode) {
            rotateSingleNode();
          } else if (toolbarSelectedNodeIds.length > 1) {
            rotateSelectedNodes();
          }
        }
        return;
      }

      // Undo / Redo
      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        dispatch(performUndo());
        return;
      } else if ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'Z' || event.key === 'y')) {
        dispatch(performRedo());
        return;
      }

      // Clear selection with Escape
      if (event.key === 'Escape') {
        // Cancel custom connection drawing if active
        if (drawConn.active) {
          setDrawConn({ active: false, src: null, waypoints: [], mouse: null, hover: null });
          waypointsRef.current = [];
        }
        const searchWasActive = cancelSearchIfActive();
        clearLocalEdgeSelection();
        enforceEdgeDeselection();
        try {
          if (reactFlowInstance && reactFlowInstance.setEdges) {
            reactFlowInstance.setEdges((eds) => (Array.isArray(eds) ? eds.map((e) => ({ ...e, selected: false })) : eds));
          }
          if (reactFlowInstance && reactFlowInstance.setNodes) {
            reactFlowInstance.setNodes((nds) => (Array.isArray(nds) ? nds.map((n) => ({ ...n, selected: false })) : nds));
          }
        } catch {}
        // Clear Redux selections
        dispatch(clearSelection());
        dispatch(clearEdgeSelection());
        dispatch(setSelectedEdgeIds([]));
        dispatch(setSelectedNodeIds([]));
        setSelectedTemplateBlockIds([]);
        lastSelectionRef.current = { nodeIds: [], edgeIds: [] };
        if (!searchWasActive) {
          dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
        }
        return;
      }

      // Delete selected nodes and edges with Delete or Backspace in one shot
      if (event.key === 'Delete' || event.key === 'Backspace') {
        // When typing in inputs/textareas/contenteditable, do not delete graph elements
        const ae = document.activeElement;
        const tag = (ae && ae.tagName ? ae.tagName.toLowerCase() : '');
        const typing = (tag === 'input' || tag === 'textarea' || (ae && ae.isContentEditable));
        if (typing) return;
        const templateBlockIdsToDelete = Array.isArray(selectedTemplateBlockIds) && selectedTemplateBlockIds.length > 0
          ? selectedTemplateBlockIds
          : [];
        let nodeIdsToDelete = [];
        let edgeIdsToDelete = [];
        try {
          const rf = reactFlowInstance;
          const rfSelNodes = (rf && rf.getNodes) ? (rf.getNodes() || []).filter((n) => n.selected).map((n) => n.id) : [];
          const rfSelEdges = (rf && rf.getEdges) ? (rf.getEdges() || []).filter((e) => e.selected).map((e) => e.id) : [];
          const reduxSelectedNodes = Array.isArray(currentSelectedNodesRef.current) ? currentSelectedNodesRef.current : [];
          const reduxSelectedEdges = Array.isArray(currentSelectedEdgesRef.current) ? currentSelectedEdgesRef.current : [];
          const selectionSnapshot = lastSelectionRef.current || { nodeIds: [], edgeIds: [] };
          const uiSelectedNodes = Array.isArray(selectionSnapshot.nodeIds) ? selectionSnapshot.nodeIds : [];
          const uiSelectedEdges = Array.isArray(selectionSnapshot.edgeIds) ? selectionSnapshot.edgeIds : [];
          const hasEdgeSelection = (uiSelectedEdges.length > 0) || (rfSelEdges.length > 0) || (reduxSelectedEdges.length > 0);

          nodeIdsToDelete = (() => {
            if (uiSelectedNodes.length > 0) return uiSelectedNodes;
            if (!hasEdgeSelection && reduxSelectedNodes.length > 0) {
              return reduxSelectedNodes;
            }
            if (!hasEdgeSelection && rfSelNodes.length > 0) {
              return rfSelNodes;
            }
            return [];
          })();

          edgeIdsToDelete = (() => {
            if (uiSelectedEdges.length > 0) return uiSelectedEdges;
            if (reduxSelectedEdges.length > 0) {
              return reduxSelectedEdges;
            }
            return rfSelEdges;
          })();

        } catch {}
        if ((!nodeIdsToDelete || nodeIdsToDelete.length === 0) && Array.isArray(toolbarSelectedNodeIds) && toolbarSelectedNodeIds.length > 0) {
          nodeIdsToDelete = toolbarSelectedNodeIds;
        }
        if ((!edgeIdsToDelete || edgeIdsToDelete.length === 0) && Array.isArray(currentSelectedEdgesRef.current) && currentSelectedEdgesRef.current.length > 0) {
          edgeIdsToDelete = currentSelectedEdgesRef.current;
        }
        if (
          (!nodeIdsToDelete || nodeIdsToDelete.length === 0) &&
          Array.isArray(edgeIdsToDelete) &&
          edgeIdsToDelete.length > 0
        ) {
          emulateActiveColorClick();
        }
        const hasGraphSelectionToDelete =
          (nodeIdsToDelete && nodeIdsToDelete.length > 0)
          || (edgeIdsToDelete && edgeIdsToDelete.length > 0);
        const singleTemplateBlockIdsToDelete = (
          !hasGraphSelectionToDelete
          && templateBlockIdsToDelete.length === 0
          && selectedTemplateBlockId
          && Array.isArray(templateSettings.blocks)
          && templateSettings.blocks.some((block) => block?.id === selectedTemplateBlockId)
        ) ? [selectedTemplateBlockId] : [];
        const resolvedTemplateBlockIdsToDelete = templateBlockIdsToDelete.length > 0
          ? templateBlockIdsToDelete
          : singleTemplateBlockIdsToDelete;
        if (
          hasGraphSelectionToDelete
          || resolvedTemplateBlockIdsToDelete.length > 0
        ) {
          event.preventDefault();
          handleDeleteSelected(nodeIdsToDelete, edgeIdsToDelete, resolvedTemplateBlockIdsToDelete);
          return;
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [
    dispatch,
    drawConn.active,
    spawnPreview,
    symbolDrawerTool?.active,
    selectAllElements,
    cloneSelection,
    toolbarSelectedNodeIds,
    toolbarCurrentNode,
    rotateSingleNode,
    rotateSelectedNodes,
    handleDeleteSelected,
    emulateActiveColorClick,
    clearLocalEdgeSelection,
    enforceEdgeDeselection,
    cancelSearchIfActive,
    modalIsOpen,
    reactFlowInstance,
    selectedTemplateBlockId,
    selectedTemplateBlockIds,
    templateSettings.blocks,
  ]);

  // Edge changes color when clicked //

  const [changeStyle, setChangeStyle] = useState(false);

  const updateInspector = useCallback((selectedNode) => {
    if (!selectedNode) {
      return;
    }
    applyInspectorForNode(selectedNode);
  }, [applyInspectorForNode]);

  // Build a label string for an edge from its visible attributes
  const buildEdgeLabel = (edge) => {
    const attrs = edge?.data?.attributes;
    if (!attrs) {
      // fallback to legacy flat fields
      const tokens = [];
      if (edge?.data?.ampacity) {
        const ampacityUnit = ['A', 'kA'].includes(edge?.data?.ampacityUnit) ? edge.data.ampacityUnit : 'A';
        tokens.push(`${edge.data.ampacity} ${ampacityUnit}`.trim());
      }
      if (edge?.data?.caliberValue) tokens.push(`${edge.data.caliberValue} ${edge?.data?.caliberUnit || ''}`.trim());
      return tokens.join(' • ');
    }

    const tokens = [];
    if (attrs.ampacity?.isVisible && attrs.ampacity.value !== '') {
      const ampacityUnit = ['A', 'kA'].includes(attrs.ampacity?.unitSuffix) ? attrs.ampacity.unitSuffix : 'A';
      tokens.push(`${attrs.ampacity.value} ${ampacityUnit}`.trim());
    }
    const showCaliber = (attrs.caliberValue?.isVisible || attrs.caliberUnit?.isVisible) && (attrs.caliberValue?.value !== '' || attrs.caliberUnit?.value);
    if (showCaliber) {
      const val = attrs.caliberValue?.value ?? '';
      const unit = attrs.caliberUnit?.value ?? '';
      tokens.push(`${val} ${unit}`.trim());
    }
    if (attrs.unitType?.isVisible && attrs.unitType?.value) {
      tokens.push(`${attrs.unitType.value}`);
    }

    return tokens.join(' • ');
  };

  const getSelectionCurrentNodeId = useCallback((nodeIds) => {
    if (!Array.isArray(nodeIds) || nodeIds.length !== 1) {
      return null;
    }
    const [nodeId] = nodeIds;
    const node = (Array.isArray(currentNodes) ? currentNodes : []).find((item) => item.id === nodeId);
    if (isInspectorlessDesignNode(node)) {
      return null;
    }
    return nodeId;
  }, [currentNodes]);

  const applyNodeSelection = useCallback((nodeIds, { focusNode = null } = {}) => {
    const nextIds = Array.isArray(nodeIds) ? Array.from(new Set(nodeIds.filter(Boolean))) : [];
    selectionSquelchUntilRef.current = Date.now() + 120;
    ignoreSelectionUntilRef.current = Date.now() + 120;
    skipSelectionOnceRef.current = true;
    clearLocalEdgeSelection();
    try {
      reactFlowInstance?.setEdges?.((eds) =>
        Array.isArray(eds) ? eds.map((edge) => ({ ...edge, selected: false })) : eds
      );
      reactFlowInstance?.setNodes?.((nds) =>
        Array.isArray(nds) ? nds.map((n) => ({ ...n, selected: nextIds.includes(n.id) })) : nds
      );
    } catch {}
    setNodes((nds) =>
      Array.isArray(nds) ? nds.map((n) => ({ ...n, selected: nextIds.includes(n.id) })) : nds
    );
    dispatch(clearEdgeSelection());
    dispatch(setSelectedEdgeIds([]));
    dispatch(setSelectedNodeIds(nextIds));
    setSelectedTemplateBlockIds([]);
    dispatch(selectTemplateBlock(null));
    dispatch(setCurrentNodeId(getSelectionCurrentNodeId(nextIds)));
    lastSelectionRef.current = { nodeIds: nextIds, edgeIds: [] };

    if (focusNode) {
      updateInspector(focusNode);
    } else if (nextIds.length === 0) {
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
    }

    return nextIds;
  }, [clearLocalEdgeSelection, dispatch, getSelectionCurrentNodeId, reactFlowInstance, setNodes, updateInspector]);

  const onNodeClick = useCallback((event, node) => {
    if (!node?.id) return;
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    if (isSpacePressedRef.current && node.type === 'boxNode') {
      applyNodeSelection([]);
      return;
    }
    if (Date.now() < suppressNodeClickUntilRef.current) {
      return;
    }
    if (isInspectorlessDesignNode(node)) {
      applyNodeSelection([node.id], { focusNode: null });
      dispatch(setCurrentNodeId(null));
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
      return;
    }

    const selectedIds = Array.isArray(currentSelectedNodesRef.current)
      ? currentSelectedNodesRef.current
      : [];
    if (event?.shiftKey) {
      const nextIds = selectedIds.includes(node.id)
        ? selectedIds.filter((id) => id !== node.id)
        : [...selectedIds, node.id];
      applyNodeSelection(nextIds, { focusNode: nextIds.includes(node.id) ? node : null });
      return;
    }

    applyNodeSelection([node.id], { focusNode: node });
  }, [applyNodeSelection, dispatch]);
  
  const selStartRef = useRef(null);
  const cancelConnectRef = useRef(false);
  const onSelectionStart = useCallback((evt) => {
    // Selection marquee started on the pane
    if (!reactFlowWrapper.current) {
      isDraggingRef.current = false;
      setDragging(false);
      setIsMarqueeSelecting(false);
      return;
    }
    isDraggingRef.current = true;
    setDragging(true);
    setIsMarqueeSelecting(true);
    debouncedSaveHistory.cancel();
    if (evt && typeof evt.clientX === 'number' && typeof evt.clientY === 'number') {
      selStartRef.current = { x: evt.clientX, y: evt.clientY };
    }
    const pointer = (evt && typeof evt.clientX === 'number' && typeof evt.clientY === 'number' && reactFlowInstance)
      ? reactFlowInstance.screenToFlowPosition({ x: evt.clientX, y: evt.clientY })
      : null;
    captureSelectionSnapshot(pointer);
  }, [captureSelectionSnapshot, reactFlowInstance, debouncedSaveHistory]);

  const onSelectionDrag = useCallback((evt) => {
    isDraggingRef.current = true;
    setDragging(true);
    const selectedIds = Array.isArray(currentSelectedNodes) ? currentSelectedNodes : [];
    if (selectedIds.length === 0) {
      selectionDragLockRef.current = null;
      return;
    }
    const rf = reactFlowInstance;
    if (!rf?.getNode) return;
    const pointerFlow = (evt && typeof evt.clientX === 'number' && typeof evt.clientY === 'number' && reactFlowInstance)
      ? reactFlowInstance.screenToFlowPosition({ x: evt.clientX, y: evt.clientY })
      : null;
    const currentPositions = new Map();
    selectedIds.forEach((id) => {
      const rfNode = rf.getNode(id);
      if (rfNode && rfNode.position) {
        currentPositions.set(id, { x: rfNode.position.x, y: rfNode.position.y });
      }
    });
    if (currentPositions.size === 0) return;

    let lock = selectionDragLockRef.current;
    if (!lock || !(lock.origin instanceof Map)) {
      lock = captureSelectionSnapshot(pointerFlow);
    }
    if (!lock) return;

    const refreshed = new Map();
    selectedIds.forEach((id) => {
      const pos = currentPositions.get(id);
      if (pos) refreshed.set(id, pos);
    });
    lock.axis = null;
    lock.shiftOrigin = null;
    if (pointerFlow) {
      lock.shiftPointer = pointerFlow;
    }
    lock.origin = refreshed;
    if (lock && (pointerFlow || lock.initialNodePositions)) {
      let translation = null;
      if (pointerFlow && lock.initialPointer) {
        translation = {
          dx: pointerFlow.x - lock.initialPointer.x,
          dy: pointerFlow.y - lock.initialPointer.y,
        };
      } else if (lock.initialNodePositions) {
        translation = computeSelectionDelta(lock.initialNodePositions, currentPositions);
      }
      if (translation) {
        applyEdgeTranslation(lock, translation.dx || 0, translation.dy || 0);
        applySelectedTemplateBlockTranslation(translation.dx || 0, translation.dy || 0);
      }
    }
  }, [currentSelectedNodes, currentSelectedEdges, reactFlowInstance, captureSelectionSnapshot, applyEdgeTranslation, applySelectedTemplateBlockTranslation, computeSelectionDelta]);

  const onSelectionDragStop = useCallback((evt) => {
    isDraggingRef.current = false;
    const nodeIdsToRestore = Array.isArray(currentSelectedNodes) ? currentSelectedNodes : [];
    const isDraggingExistingSelection = nodeIdsToRestore.length > 0;
    setDragging(false);
    if (isDraggingExistingSelection) {
      setIsMarqueeSelecting(false);
    }
    debouncedSaveHistory.cancel();
    suppressNodeClickUntilRef.current = Date.now() + 250;
    const lock = selectionDragLockRef.current;
    let finalDelta = null;
    if (lock && lock.initialNodePositions) {
      const currentPositions = new Map();
      const rf = reactFlowInstance;
      if (rf?.getNode) {
        lock.initialNodePositions.forEach((_, id) => {
          const rfNode = rf.getNode(id);
          if (rfNode && rfNode.position) {
            currentPositions.set(id, { x: rfNode.position.x, y: rfNode.position.y });
          }
        });
      }
      if (currentPositions.size > 0) {
        finalDelta = computeSelectionDelta(lock.initialNodePositions, currentPositions);
      }
    }
    if (!finalDelta && lock?.lastEdgeShift) {
      finalDelta = lock.lastEdgeShift;
    }
    // Snap all selected nodes to the grid when group-dragging ends
    if (currentSelectedNodes && currentSelectedNodes.length > 0) {
      const selectedSet = new Set(currentSelectedNodes);
      let alignDelta = null;
      setNodes((nds) => nds.map((n) => {
        if (!selectedSet.has(n.id)) return n;
        const aligned = alignNodeByHandle(n);
        if (!alignDelta && lock?.initialNodePositions?.has(n.id)) {
          const originPos = lock.initialNodePositions.get(n.id);
          alignDelta = {
            dx: aligned.x - originPos.x,
            dy: aligned.y - originPos.y,
          };
        }
        return { ...n, position: aligned };
      }));
      const edgeDelta = alignDelta || finalDelta;
      if (lock && edgeDelta) {
        applyEdgeTranslation(lock, edgeDelta.dx || 0, edgeDelta.dy || 0);
      }
      requestAnimationFrame(() => saveCurrentState());
    }
    if (nodeIdsToRestore.length > 0) {
      selectionSquelchUntilRef.current = Date.now() + 180;
      ignoreSelectionUntilRef.current = Date.now() + 180;
      skipSelectionOnceRef.current = true;
      setNodes((nds) =>
        Array.isArray(nds)
          ? nds.map((n) => ({ ...n, selected: nodeIdsToRestore.includes(n.id) }))
          : nds
      );
      try {
        reactFlowInstance?.setNodes?.((nds) =>
          Array.isArray(nds)
            ? nds.map((n) => ({ ...n, selected: nodeIdsToRestore.includes(n.id) }))
            : nds
        );
      } catch {}
      dispatch(setSelectedNodeIds(nodeIdsToRestore));
      dispatch(setCurrentNodeId(getSelectionCurrentNodeId(nodeIdsToRestore)));
      lastSelectionRef.current = { nodeIds: nodeIdsToRestore, edgeIds: [] };
    }
    selectionDragLockRef.current = null;
    // Clear selection-drag start ref
    try { /* noop */ } finally {
      if (isDraggingExistingSelection) {
        selStartRef.current = null;
      }
      // mark that a marquee selection just occurred so a single click won't immediately clear it
      if ((currentSelectedNodes && currentSelectedNodes.length > 0) || (currentSelectedEdges && currentSelectedEdges.length > 0)) {
        justSelectedRef.current = true;
        justSelectedUntilRef.current = Date.now() + 150;
      }
    }
  }, [
    currentSelectedNodes,
    currentSelectedEdges,
    reactFlowInstance,
    setNodes,
    debouncedSaveHistory,
    saveCurrentState,
    alignNodeByHandle,
    dispatch,
    applyEdgeTranslation,
    computeSelectionDelta,
    getSelectionCurrentNodeId,
  ]);

  const onSelectionEnd = useCallback((evt) => {
    isDraggingRef.current = false;
    setDragging(false);
    setIsMarqueeSelecting(false);
    try {
      const start = selStartRef.current;
      if (!start || !reactFlowWrapper.current) return;
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const end = { x: evt?.clientX ?? start.x, y: evt?.clientY ?? start.y };
      const dx = Math.abs(end.x - start.x);
      const dy = Math.abs(end.y - start.y);
      if (dx <= 3 && dy <= 3) {
        selStartRef.current = null;
        return;
      } // too small for marquee

      // Convert screen coords to flow coords
      const p1 = reactFlowInstance.screenToFlowPosition({ x: start.x, y: start.y });
      const p2 = reactFlowInstance.screenToFlowPosition({ x: end.x, y: end.y });
      const minX = Math.min(p1.x, p2.x);
      const maxX = Math.max(p1.x, p2.x);
      const minY = Math.min(p1.y, p2.y);
      const maxY = Math.max(p1.y, p2.y);
      const rect = { minX, minY, maxX, maxY };
      const screenRect = {
        left: Math.min(start.x, end.x),
        right: Math.max(start.x, end.x),
        top: Math.min(start.y, end.y),
        bottom: Math.max(start.y, end.y),
      };
      const rectsIntersect = (a, b) => (
        a.left <= b.right
        && a.right >= b.left
        && a.top <= b.bottom
        && a.bottom >= b.top
      );

      const pointInRect = (pt, r) => pt.x >= r.minX && pt.x <= r.maxX && pt.y >= r.minY && pt.y <= r.maxY;
      const linesIntersect = (a, b, c, d) => {
        const cross = (p, q, r) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
        const onSeg = (p, q, r) => Math.min(p.x, r.x) <= q.x && q.x <= Math.max(p.x, r.x) && Math.min(p.y, r.y) <= q.y && q.y <= Math.max(p.y, r.y);
        const d1 = cross(a, b, c);
        const d2 = cross(a, b, d);
        const d3 = cross(c, d, a);
        const d4 = cross(c, d, b);
        if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
        if (d1 === 0 && onSeg(a, c, b)) return true;
        if (d2 === 0 && onSeg(a, d, b)) return true;
        if (d3 === 0 && onSeg(c, a, d)) return true;
        if (d4 === 0 && onSeg(c, b, d)) return true;
        return false;
      };
      const segIntersectsRect = (a, b, r) => {
        if (pointInRect(a, r) || pointInRect(b, r)) return true;
        const tl = { x: r.minX, y: r.minY };
        const tr = { x: r.maxX, y: r.minY };
        const br = { x: r.maxX, y: r.maxY };
        const bl = { x: r.minX, y: r.maxY };
        return (
          linesIntersect(a, b, tl, tr) ||
          linesIntersect(a, b, tr, br) ||
          linesIntersect(a, b, br, bl) ||
          linesIntersect(a, b, bl, tl)
        );
      };

      // Build edge geometry using node centers and optional waypoints
      const rf = reactFlowInstance;
      const nodesById = new Map((rf.getNodes() || []).map((n) => [n.id, n]));
      const edgeIds = [];
      for (const e of (rf.getEdges() || [])) {
        const src = nodesById.get(e.source);
        const tgt = nodesById.get(e.target);
        if (!src || !tgt) continue;
        const sx = (src.positionAbsolute?.x ?? src.position?.x ?? 0) + ((src.width ?? 0) / 2);
        const sy = (src.positionAbsolute?.y ?? src.position?.y ?? 0) + ((src.height ?? 0) / 2);
        const tx = (tgt.positionAbsolute?.x ?? tgt.position?.x ?? 0) + ((tgt.width ?? 0) / 2);
        const ty = (tgt.positionAbsolute?.y ?? tgt.position?.y ?? 0) + ((tgt.height ?? 0) / 2);
        const pts = [{ x: sx, y: sy }, ...((e?.data?.waypoints) || []), { x: tx, y: ty }];
        if (pts.some((p) => pointInRect(p, rect))) { edgeIds.push(e.id); continue; }
        let hit = false;
        for (let i = 0; i < pts.length - 1; i++) {
          if (segIntersectsRect(pts[i], pts[i + 1], rect)) { hit = true; break; }
        }
        if (hit) edgeIds.push(e.id);
      }

      if (edgeIds.length > 0) {
        selectionSquelchUntilRef.current = Date.now() + 120;
      }
      const selectedDesignBlockIds = Array.from(document.querySelectorAll('.template-floating-text[data-template-block-id], .template-floating-image[data-template-block-id]'))
        .reduce((ids, element) => {
          const blockId = element.getAttribute('data-template-block-id');
          if (!blockId || ids.includes(blockId)) return ids;
          const box = element.getBoundingClientRect();
          const intersectsSelection = rectsIntersect(screenRect, {
            left: box.left,
            right: box.right,
            top: box.top,
            bottom: box.bottom,
          });
          return intersectsSelection ? [...ids, blockId] : ids;
        }, []);
      const selectedDesignBlockIdSet = new Set(selectedDesignBlockIds);
      if (templateSettings.pageOption !== 'none') {
        const imageBlocks = (Array.isArray(templateSettings.blocks) ? templateSettings.blocks : [])
          .filter((block) => block?.id && block.type === 'image');
        if (imageBlocks.length > 0) {
          const tiles = new Set();
          if (templateSettings.pageOption === 'all-pages') {
            (Array.isArray(currentNodes) ? currentNodes : []).forEach((node) => {
              const nx = node?.position?.x ?? 0;
              const ny = node?.position?.y ?? 0;
              tiles.add(`${Math.floor(nx / templatePageWidth)},${Math.floor(ny / templatePageHeight)}`);
            });
          }
          if (tiles.size === 0 || templateSettings.pageOption !== 'all-pages') {
            tiles.clear();
            tiles.add('0,0');
          }
          const selectedFlowRect = {
            left: rect.minX,
            right: rect.maxX,
            top: rect.minY,
            bottom: rect.maxY,
          };
          Array.from(tiles).forEach((tileKey) => {
            const [col, row] = tileKey.split(',').map(Number);
            const pageLeft = col * templatePageWidth;
            const pageTop = row * templatePageHeight;
            imageBlocks.forEach((block) => {
              if (selectedDesignBlockIdSet.has(block.id)) return;
              const imageWidth = clampTemplateImageScaleValue(block.imageScale);
              const imageRatio = normalizeTemplateImageRatioValue(block.imageRatio);
              const imageHeight = imageRatio === 'rectangle' ? imageWidth * (3 / 4) : imageWidth;
              const fallbackX = Math.max(templatePageWidth - imageWidth - 80, 0);
              const imageX = Number.isFinite(Number(block.imageX)) ? Number(block.imageX) : fallbackX;
              const imageY = Number.isFinite(Number(block.imageY)) ? Number(block.imageY) : 80;
              const imageRect = {
                left: pageLeft + imageX,
                right: pageLeft + imageX + imageWidth,
                top: pageTop + imageY,
                bottom: pageTop + imageY + imageHeight,
              };
              if (rectsIntersect(selectedFlowRect, imageRect)) {
                selectedDesignBlockIdSet.add(block.id);
              }
            });
          });
        }
      }
      const nextSelectedDesignBlockIds = Array.from(selectedDesignBlockIdSet);
      setSelectedTemplateBlockIds(nextSelectedDesignBlockIds);
      dispatch(selectTemplateBlock(nextSelectedDesignBlockIds[0] || null));
      if (nextSelectedDesignBlockIds.length > 0 || edgeIds.length > 0) {
        justSelectedRef.current = true;
        justSelectedUntilRef.current = Date.now() + 150;
      }
      dispatch(setSelectedEdgeIds(edgeIds));
      setEdges((eds) => (Array.isArray(eds) ? eds.map((e) => ({ ...e, selected: edgeIds.includes(e.id) })) : eds));
      selStartRef.current = null;
    } catch {
      selStartRef.current = null;
    }
  }, [currentNodes, dispatch, reactFlowInstance, setEdges, templatePageHeight, templatePageWidth, templateSettings.blocks, templateSettings.pageOption]);

  // Track mouse position while drawing a connection for preview (snapped + Shift axis lock)
  useEffect(() => {
    if (!drawConn.active) return;
    const onMove = (e) => {
      try {
        const p0 = reactFlowInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const shift = e.shiftKey;
        const hoverMeta = (() => {
          // Inspect the DOM element under the cursor to determine which handle role we are aiming for
          if (typeof document === 'undefined') return null;
          const el = document.elementFromPoint(e.clientX, e.clientY);
          const handleEl = el && el.closest ? el.closest('.react-flow__handle') : null;
          if (!handleEl) return null;
          const ds = handleEl.dataset || {};
          const rawType = ds.handleType || ds.handletype || handleEl.getAttribute('data-handle-type') || handleEl.getAttribute('data-handletype');
          let type = typeof rawType === 'string' ? rawType.toLowerCase() : null;
          if (!type) {
            if (handleEl.classList.contains('react-flow__handle-target')) type = 'target';
            else if (handleEl.classList.contains('react-flow__handle-source')) type = 'source';
          }
          if (type !== 'source' && type !== 'target') type = null;
          const nodeEl = handleEl.closest?.('.react-flow__node');
          const nodeId =
            ds.nodeId ||
            ds.nodeid ||
            handleEl.getAttribute('data-nodeid') ||
            handleEl.getAttribute('data-nodeId') ||
            nodeEl?.getAttribute?.('data-id') ||
            nodeEl?.dataset?.id ||
            null;
          const handleId = ds.handleId || ds.handleid || handleEl.getAttribute('data-handleid') || handleEl.getAttribute('data-handleId') || handleEl.getAttribute('data-id') || null;
          if (!type) return null;
          return { nodeId: nodeId || null, handleId: handleId || null, type, element: handleEl };
        })();
        const srcRole = typeof drawConnRef.current?.src?.role === 'string'
          ? drawConnRef.current.src.role.toLowerCase()
          : null;
        const hoverRole = typeof hoverMeta?.type === 'string' ? hoverMeta.type.toLowerCase() : null;
        const sameNodeHover = !!drawConnRef.current?.src?.nodeId && !!hoverMeta?.nodeId && drawConnRef.current.src.nodeId === hoverMeta.nodeId;
        const validHover = !!srcRole && !!hoverRole && srcRole !== hoverRole && !sameNodeHover;
        if (validHover && hoverMeta?.element) {
          if (validHoverHandleRef.current !== hoverMeta.element) {
            clearValidHoverHandle();
            hoverMeta.element.classList.add('connection-assist-valid');
            validHoverHandleRef.current = hoverMeta.element;
          }
          dispatch(setConnectionAssistHover({
            hoveredNodeId: hoverMeta.nodeId || null,
            hoveredHandleId: hoverMeta.handleId || null,
          }));
        } else {
          clearValidHoverHandle();
        }
        const hover = hoverMeta
          ? { nodeId: hoverMeta.nodeId, handleId: hoverMeta.handleId, type: hoverMeta.type }
          : null;
        setDrawConn((d) => {
          if (!d || !d.active) return d;
          let p = snapToGrid ? snapToGrid(p0) : p0;
          if (shift) {
            const last = (d.waypoints && d.waypoints.length > 0) ? d.waypoints[d.waypoints.length - 1] : d.src?.base;
            if (last) {
              const dx = Math.abs(p.x - last.x);
              const dy = Math.abs(p.y - last.y);
              p = dx >= dy ? { x: p.x, y: last.y } : { x: last.x, y: p.y };
            }
          }
          return { ...d, mouse: { x: p.x, y: p.y }, hover };
        });
      } catch {}
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      clearValidHoverHandle();
    };
  }, [clearValidHoverHandle, dispatch, drawConn.active, reactFlowInstance, snapToGrid]);

  // Right-click (context menu) to undo last waypoint while drawing
  useEffect(() => {
    if (!drawConn.active) return;
    const onContext = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDrawConn((d) => {
        if (!d || !d.active) return d;
        if (d.waypoints && d.waypoints.length > 0) {
          const next = d.waypoints.slice(0, -1);
          waypointsRef.current = next;
          const last = next.length > 0 ? next[next.length - 1] : d.src?.base;
          return { ...d, waypoints: next, mouse: last };
        }
        waypointsRef.current = [];
        return { active: false, src: null, waypoints: [], mouse: null, hover: null };
      });
    };
    window.addEventListener('contextmenu', onContext, { capture: true });
    return () => window.removeEventListener('contextmenu', onContext, { capture: true });
  }, [drawConn.active]);

  // Global mousedown to add elbows while connecting (pane click may not fire during connect)
  useEffect(() => {
    if (!drawConn.active) return;
    const addPointFromEvent = (e) => {
      try {
        if (e.button !== 0) return; // only left click
        const now = Date.now();
        if (now - (lastElbowTsRef.current || 0) < 40) return; // de-dupe
        lastElbowTsRef.current = now;
        const isHandle = !!(e.target && (e.target.closest && e.target.closest('.react-flow__handle')));
        if (isHandle) return; // let handle clicks finish the connection
        const p0 = reactFlowInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
        let p = snapToGrid ? snapToGrid(p0) : p0;
        const shift = e.shiftKey;
        setDrawConn((d) => {
          if (!d || !d.active) return d;
          const last = (d.waypoints && d.waypoints.length > 0) ? d.waypoints[d.waypoints.length - 1] : d.src?.base;
          if (shift && last) {
            const dx = Math.abs(p.x - last.x);
            const dy = Math.abs(p.y - last.y);
            p = dx >= dy ? { x: p.x, y: last.y } : { x: last.x, y: p.y };
          }
          const wp = { x: p.x, y: p.y };
          waypointsRef.current = [...waypointsRef.current, wp];
          const next = { ...d, waypoints: [...d.waypoints, wp], mouse: wp };
          return next;
        });
      } catch {}
    };
    const onDown = (e) => addPointFromEvent(e);
    window.addEventListener('mousedown', onDown, { capture: true });
    return () => {
      window.removeEventListener('mousedown', onDown, { capture: true });
    };
  }, [drawConn.active, reactFlowInstance, snapToGrid]);
    
  // Significant Events
  const onInit = useCallback(() => {
    saveCurrentState();
  }, [saveCurrentState]);

  const onError = useCallback(() => {
    saveCurrentState();
  }, [saveCurrentState]);

  const isBusNodeEntry = useCallback((node) => (
    !!node && (node.type === 'busNode' || node?.data?.symbolLabel === 'Bus')
  ), []);

  const isDesignOnlyNodeEntry = useCallback((node) => (
    !!node && (
      node.type === 'boxNode'
      || node.type === 'textBoxNode'
      || node.type === 'straightLineNode'
      || node.type === 'symbolDrawerNode'
    )
  ), []);

  const collectBusCascadeNodeIds = useCallback((busId, edges = [], nodes = []) => {
    if (!busId || !Array.isArray(edges) || edges.length === 0) {
      return new Set([busId]);
    }
    const nodeIds = new Set((Array.isArray(nodes) ? nodes : []).map((node) => node?.id).filter(Boolean));
    const movingIds = new Set([busId]);
    const queue = [busId];
    while (queue.length > 0) {
      const sourceId = queue.shift();
      edges.forEach((edge) => {
        if (!edge || edge.source !== sourceId || !edge.target) {
          return;
        }
        if (nodeIds.size > 0 && !nodeIds.has(edge.target)) {
          return;
        }
        if (movingIds.has(edge.target)) {
          return;
        }
        movingIds.add(edge.target);
        queue.push(edge.target);
      });
    }
    return movingIds;
  }, []);

  const captureBusCascadeDrag = useCallback((busNode, alignedBusPosition) => {
    if (!isBusNodeEntry(busNode) || !busNode?.id) {
      busCascadeDragRef.current = null;
      return null;
    }
    const rfNodes = reactFlowInstance?.getNodes?.() || currentNodes || [];
    const rfEdges = reactFlowInstance?.getEdges?.() || currentEdges || [];
    const movingIds = collectBusCascadeNodeIds(busNode.id, rfEdges, rfNodes);
    if (movingIds.size <= 1) {
      busCascadeDragRef.current = null;
      return null;
    }

    const originPositions = new Map();
    const stationaryPositions = new Map();
    rfNodes.forEach((node) => {
      if (!node?.id) {
        return;
      }
      if (!movingIds.has(node.id)) {
        if (node.position) {
          stationaryPositions.set(node.id, { x: node.position.x || 0, y: node.position.y || 0 });
        }
        return;
      }
      const position = node.id === busNode.id
        ? alignedBusPosition
        : node.position;
      if (!position) {
        return;
      }
      originPositions.set(node.id, { x: position.x || 0, y: position.y || 0 });
    });

    const edgeWaypoints = new Map();
    rfEdges.forEach((edge) => {
      if (!edge?.id || !movingIds.has(edge.source) || !movingIds.has(edge.target)) {
        return;
      }
      const waypoints = Array.isArray(edge?.data?.waypoints) ? edge.data.waypoints : [];
      if (waypoints.length === 0) {
        return;
      }
      edgeWaypoints.set(
        edge.id,
        waypoints.map((point) => ({ x: point.x, y: point.y }))
      );
    });

    const snapshot = {
      busId: busNode.id,
      movingIds,
      originBusPosition: {
        x: alignedBusPosition?.x ?? busNode.position?.x ?? 0,
        y: alignedBusPosition?.y ?? busNode.position?.y ?? 0,
      },
      originPositions,
      stationaryPositions,
      edgeWaypoints,
      lastDelta: { dx: 0, dy: 0 },
    };
    busCascadeDragRef.current = snapshot;
    return snapshot;
  }, [collectBusCascadeNodeIds, currentEdges, currentNodes, isBusNodeEntry, reactFlowInstance]);

  const translateBusCascadeEdges = useCallback((snapshot, dx, dy) => {
    if (!snapshot?.edgeWaypoints || snapshot.edgeWaypoints.size === 0) {
      return;
    }
    const translate = (edges = []) => {
      if (!Array.isArray(edges)) return edges;
      let changed = false;
      const nextEdges = edges.map((edge) => {
        const originWaypoints = snapshot.edgeWaypoints.get(edge.id);
        if (!originWaypoints) {
          return edge;
        }
        changed = true;
        return {
          ...edge,
          data: {
            ...(edge.data || {}),
            waypoints: originWaypoints.map((point) => ({
              x: point.x + dx,
              y: point.y + dy,
            })),
          },
        };
      });
      return changed ? nextEdges : edges;
    };
    setEdges((edges) => translate(edges));
    try {
      reactFlowInstance?.setEdges?.((edges) => translate(edges));
    } catch {}
  }, [reactFlowInstance, setEdges]);

  const applyBusCascadeTranslation = useCallback((busNode, alignedBusPosition, { final = false } = {}) => {
    if (!isBusNodeEntry(busNode) || !busNode?.id) {
      return false;
    }
    let snapshot = busCascadeDragRef.current;
    if (!snapshot || snapshot.busId !== busNode.id) {
      snapshot = captureBusCascadeDrag(busNode, alignedBusPosition);
    }
    if (!snapshot || !snapshot.originPositions || snapshot.originPositions.size <= 1) {
      return false;
    }
    const dx = (alignedBusPosition?.x ?? 0) - snapshot.originBusPosition.x;
    const dy = (alignedBusPosition?.y ?? 0) - snapshot.originBusPosition.y;
    const EPS = 0.0001;
    const sameDelta =
      Math.abs((snapshot.lastDelta?.dx || 0) - dx) < EPS &&
      Math.abs((snapshot.lastDelta?.dy || 0) - dy) < EPS;
    if (!final && sameDelta) {
      return true;
    }

    setNodes((nodes) =>
      Array.isArray(nodes)
        ? nodes.map((node) => {
            const origin = snapshot.originPositions.get(node.id);
            if (!origin) {
              const stationary = snapshot.stationaryPositions?.get(node.id);
              if (
                stationary &&
                node.position &&
                (
                  Math.abs(node.position.x - stationary.x) >= EPS ||
                  Math.abs(node.position.y - stationary.y) >= EPS
                )
              ) {
                return { ...node, position: stationary };
              }
              return node;
            }
            const nextPosition = node.id === snapshot.busId
              ? alignedBusPosition
              : { x: origin.x + dx, y: origin.y + dy };
            if (
              node.position &&
              Math.abs(node.position.x - nextPosition.x) < EPS &&
              Math.abs(node.position.y - nextPosition.y) < EPS
            ) {
              return node;
            }
            return { ...node, position: nextPosition };
          })
        : nodes
    );
    translateBusCascadeEdges(snapshot, dx, dy);
    snapshot.lastDelta = { dx, dy };
    return true;
  }, [captureBusCascadeDrag, isBusNodeEntry, setNodes, translateBusCascadeEdges]);

  const onNodeDragStop = useCallback((event, node) => {
    isDraggingRef.current = false;
    setDragging(false);
    setHideDesignResizeHandles(false);
    debouncedSaveHistory.cancel();
    suppressNodeClickUntilRef.current = Date.now() + 250;
    const isBusDrag = isBusNodeEntry(node);
    const isDesignDrag = isDesignOnlyNodeEntry(node);
    if (isBusDrag) {
      busDragAutoConnectSuppressedRef.current = false;
    }
    const finalAligned = node && node.position
      ? (isDesignDrag ? { x: node.position.x, y: node.position.y } : alignNodeByHandle(node))
      : null;
    if (node && node.position) {
      if (isBusDrag) {
        const appliedCascade = applyBusCascadeTranslation(node, finalAligned, { final: true });
        if (!appliedCascade) {
          setNodes((nds) =>
            nds.map((n) => (n.id === node.id ? { ...n, position: finalAligned } : n))
          );
        }
      } else {
        setNodes((nds) =>
          nds.map((n) => (n.id === node.id ? { ...n, position: finalAligned } : n))
        );
      }
    }
    if (isBusDrag) {
      busCascadeDragRef.current = null;
    }
    templateSelectionDragRef.current = null;
    if (node?.id && nodeDragLockRef.current[node.id]) {
      delete nodeDragLockRef.current[node.id];
    }
    // clear highlights on drag end
    dispatch(setHighlightHandles([]));
    // attempt to insert node into an edge if both handles overlapped the same edge
    if (!isDesignDrag && node && node.id) {
      tryInsertNodeIntoEdgeOnDrop(node.id);
    }
    if (node?.id && nodeShakeStateRef.current[node.id]) {
      delete nodeShakeStateRef.current[node.id];
    }
    const dragSelection = nodeDragSelectionRef.current;
    nodeDragSelectionRef.current = null;
    const nodeIdsToRestore = Array.isArray(dragSelection?.nodeIds) ? dragSelection.nodeIds : [];
    if (node?.id && nodeIdsToRestore.includes(node.id)) {
      selectionSquelchUntilRef.current = Date.now() + 150;
      ignoreSelectionUntilRef.current = Date.now() + 150;
      skipSelectionOnceRef.current = true;
      setNodes((nds) =>
        Array.isArray(nds)
          ? nds.map((n) => ({ ...n, selected: nodeIdsToRestore.includes(n.id) }))
          : nds
      );
      try {
        reactFlowInstance?.setNodes?.((nds) =>
          Array.isArray(nds)
            ? nds.map((n) => ({ ...n, selected: nodeIdsToRestore.includes(n.id) }))
            : nds
        );
      } catch {}
      dispatch(setSelectedNodeIds(nodeIdsToRestore));
      dispatch(setCurrentNodeId(getSelectionCurrentNodeId(nodeIdsToRestore)));
      lastSelectionRef.current = { nodeIds: nodeIdsToRestore, edgeIds: [] };
    }
    requestAnimationFrame(() => saveCurrentState());
  }, [
    debouncedSaveHistory,
    saveCurrentState,
    setNodes,
    alignNodeByHandle,
    dispatch,
    reactFlowInstance,
    isBusNodeEntry,
    isDesignOnlyNodeEntry,
    applyBusCascadeTranslation,
    getSelectionCurrentNodeId,
  ]);

  const onNodesDelete = useCallback((nodesToDelete) => {
    if (Array.isArray(nodesToDelete) && nodesToDelete.length > 0) {
      const ids = new Set(nodesToDelete.map((n) => n.id));
      setNodes((nds) => nds.filter((n) => !ids.has(n.id)));
      dispatch(clearSelection());
      dispatch(clearEdgeSelection());
      dispatch(setSelectedNodeIds([]));
      dispatch(setSelectedEdgeIds([]));
      lastSelectionRef.current = { nodeIds: [], edgeIds: [] };
      dispatch(setCurrentNodeId(null));
      dispatch(setTabPanel({ tabPanel: EDITOR_TAB_PANEL_ELEMENTS }));
      skipSelectionOnceRef.current = true;
      ignoreSelectionUntilRef.current = Date.now() + 200;
      selectionSquelchUntilRef.current = Date.now() + 120;
      saveCurrentState();
    } else {
      saveCurrentState();
    }
  }, [setNodes, dispatch, saveCurrentState]);

  const resetShakeStateForNode = useCallback((nodeId, position = null) => {
    if (!nodeId) return;
    nodeShakeStateRef.current[nodeId] = {
      lastPos: position ? { x: position.x, y: position.y } : null,
      lastDir: null,
      reversals: 0,
      lastTs: Date.now(),
      triggered: false,
      axis: null,
      travel: 0,
      startTs: Date.now(),
    };
  }, []);

  const detectShakeForNode = useCallback((nodeId, position) => {
    if (!nodeId || !position) return false;
    const state = nodeShakeStateRef.current[nodeId] || {
      lastPos: null,
      lastDir: null,
      reversals: 0,
      lastTs: 0,
      triggered: false,
      axis: null,
      travel: 0,
      startTs: 0,
    };
    if (state.triggered) return true;
    const now = Date.now();
    if (!state.lastPos) {
      state.lastPos = { x: position.x, y: position.y };
      state.lastTs = now;
      state.startTs = now;
      nodeShakeStateRef.current[nodeId] = state;
      return false;
    }
    const dx = position.x - state.lastPos.x;
    const dy = position.y - state.lastPos.y;
    const axisGuess = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
    if (!state.axis) {
      state.axis = axisGuess;
    }
    if (axisGuess !== state.axis) {
      // ignore axis flip; wait for clearer gesture
      return false;
    }
    const delta = state.axis === 'x' ? dx : dy;
    const distance = Math.abs(delta);
    if (distance < SHAKE_MIN_DELTA_PX) {
      return false;
    }
    const dir = delta > 0 ? 1 : -1;
    const withinWindow = now - state.lastTs <= SHAKE_REVERSAL_WINDOW_MS;
    const withinInterval = now - state.lastTs <= SHAKE_MAX_INTERVAL_MS;
    const totalElapsed = state.startTs ? (now - state.startTs) : 0;
    const shouldReset =
      !withinWindow ||
      !withinInterval ||
      (totalElapsed > SHAKE_MAX_TOTAL_MS && state.reversals > 0);
    if (shouldReset) {
      state.reversals = 0;
      state.travel = 0;
      state.axis = state.axis || axisGuess;
      state.startTs = now;
    }
    if (state.lastDir && dir !== state.lastDir && withinInterval) {
      state.reversals += 1;
      if (state.reversals === 1 && !state.startTs) {
        state.startTs = now;
      }
    }
    state.lastDir = dir;
    state.travel += distance;
    state.lastPos = { x: position.x, y: position.y };
    state.lastTs = now;
    const hasEnoughTravel = state.travel >= SHAKE_MIN_TRAVEL_PX;
    if (state.reversals >= SHAKE_REQUIRED_REVERSALS && hasEnoughTravel) {
      state.triggered = true;
      nodeShakeStateRef.current[nodeId] = state;
      return true;
    }
    nodeShakeStateRef.current[nodeId] = state;
    return false;
  }, []);

  const onEdgeUpdateEnd = useCallback(() => {
    saveCurrentState();
  }, [saveCurrentState]);   

  const onEdgesDelete = useCallback((edgesToDelete) => {
    if (Array.isArray(edgesToDelete) && edgesToDelete.length > 0) {
      const ids = new Set(edgesToDelete.map((e) => e.id));
      setEdges((eds) => eds.filter((e) => !ids.has(e.id)));
      dispatch(clearEdgeSelection());
      saveCurrentState();
    } else {
      saveCurrentState();
    }
  }, [setEdges, dispatch, saveCurrentState]);   

  // Continuous handle-aligned snapping while dragging a single node
  const onNodeDragStart = useCallback((evt, node) => {
    if (!node || !node.position) return;
    if (isSpacePressedRef.current && node.type === 'boxNode') {
      evt?.preventDefault?.();
      evt?.stopPropagation?.();
      return;
    }
    isDraggingRef.current = true;
    setDragging(true);
    setHideDesignResizeHandles(true);
    debouncedSaveHistory.cancel();
    const isBusDrag = isBusNodeEntry(node);
    const isDesignDrag = isDesignOnlyNodeEntry(node);
    busDragAutoConnectSuppressedRef.current = isBusDrag;
    const currentSelection = Array.isArray(currentSelectedNodesRef.current)
      ? currentSelectedNodesRef.current
      : [];
    const shouldExtendSelection = !!evt?.shiftKey && !isBusDrag && !currentSelection.includes(node.id);
    const selectedNodeIds = shouldExtendSelection
      ? applyNodeSelection([...currentSelection, node.id], { focusNode: node })
      : Array.isArray(currentSelectedNodesRef.current)
      && currentSelectedNodesRef.current.includes(node.id)
        ? currentSelectedNodesRef.current
        : [node.id];
    const restoredSelectionIds = isBusDrag ? [node.id] : selectedNodeIds;
    const allowAutoConnect = !isDesignDrag && !isBusDrag && selectedNodeIds.length === 1;
    if (!allowAutoConnect) {
      dispatch(setHighlightHandles([]));
    }
    nodeDragSelectionRef.current = { nodeIds: [...restoredSelectionIds] };
    if (isBusDrag) {
      selectionSquelchUntilRef.current = Date.now() + 150;
      ignoreSelectionUntilRef.current = Date.now() + 150;
      skipSelectionOnceRef.current = true;
      try {
        reactFlowInstance?.setNodes?.((nds) =>
          Array.isArray(nds)
            ? nds.map((n) => ({ ...n, selected: n.id === node.id }))
            : nds
        );
      } catch {}
      setNodes((nds) =>
        Array.isArray(nds)
          ? nds.map((n) => ({ ...n, selected: n.id === node.id }))
          : nds
      );
      dispatch(setSelectedNodeIds([node.id]));
      dispatch(setCurrentNodeId(node.id));
      lastSelectionRef.current = { nodeIds: [node.id], edgeIds: [] };
    }
    if (!selectionDragLockRef.current) {
      const pointerFlow = (evt && typeof evt.clientX === 'number' && typeof evt.clientY === 'number' && reactFlowInstance)
        ? reactFlowInstance.screenToFlowPosition({ x: evt.clientX, y: evt.clientY })
        : null;
      captureSelectionSnapshot(pointerFlow, isBusDrag ? [node.id] : null);
    }
    const aligned = isDesignDrag
      ? { x: node.position.x, y: node.position.y }
      : alignNodeByHandle(node);
    if (isBusDrag) {
      captureBusCascadeDrag(node, aligned);
    } else {
      busCascadeDragRef.current = null;
    }
    if (!isDesignDrag) {
      resetShakeStateForNode(node.id, aligned);
    }
    nodeDragLockRef.current[node.id] = {
      originAligned: aligned,
      originRaw: { x: node.position.x, y: node.position.y },
      axis: null,
      anchorAligned: aligned,
      allowAutoConnect,
    };
  }, [
    alignNodeByHandle,
    captureSelectionSnapshot,
    reactFlowInstance,
    resetShakeStateForNode,
    isBusNodeEntry,
    captureBusCascadeDrag,
    isDesignOnlyNodeEntry,
    setNodes,
    dispatch,
    debouncedSaveHistory,
    applyNodeSelection,
  ]);

  const normalizeNodeForGeometry = useCallback((nodeObj, overridePos = null) => {
    if (!nodeObj) return null;
    const baseX = typeof overridePos?.x === 'number'
      ? overridePos.x
      : (nodeObj.positionAbsolute?.x ?? nodeObj.position?.x ?? 0);
    const baseY = typeof overridePos?.y === 'number'
      ? overridePos.y
      : (nodeObj.positionAbsolute?.y ?? nodeObj.position?.y ?? 0);

    return {
      ...nodeObj,
      position: { ...(nodeObj.position || {}), x: baseX, y: baseY },
      positionAbsolute: { ...(nodeObj.positionAbsolute || nodeObj.position || {}), x: baseX, y: baseY },
    };
  }, []);

  // Helper: enumerate absolute handle positions for a node (source and target)
  function enumerateHandles(nodeObj) {
    if (!nodeObj) return [];
    const roleList = [];
    const x = (nodeObj.positionAbsolute?.x ?? nodeObj.position?.x ?? 0);
    const y = (nodeObj.positionAbsolute?.y ?? nodeObj.position?.y ?? 0);
    const w = nodeObj.width || 0;
    const h = nodeObj.height || 0;
    const ang = typeof nodeObj?.data?.angle === 'number' ? nodeObj.data.angle : VERTICAL_ORIENTATION;
    const grid = Math.max(5, Math.min(200, Number(gridGapPx) || 25));
    if (nodeObj.type === 'busNode') {
      const { handles } = enumerateBusHandles(nodeObj, grid);
      return handles;
    }

    const bounds = nodeObj.handleBounds || nodeObj.__rf?.handleBounds;
    const pushFromBounds = (list, role) => {
      if (!Array.isArray(list)) return;
      list.forEach((handle, idx) => {
        if (!handle) return;
        const offsetX = (handle.x ?? 0) + (handle.width ?? 0) / 2;
        const offsetY = (handle.y ?? 0) + (handle.height ?? 0) / 2;
        if (!Number.isFinite(offsetX) || !Number.isFinite(offsetY)) return;
        const handleId = handle.id && String(handle.id).length > 0
          ? handle.id
          : `${role}-${idx}`;
        roleList.push({
          role,
          id: handleId,
          x: x + offsetX,
          y: y + offsetY,
        });
      });
    };
    pushFromBounds(bounds?.source, 'source');
    pushFromBounds(bounds?.target, 'target');
    if (roleList.length > 0) {
      return roleList;
    }

    // Custom node
    const list = Array.isArray(nodeObj?.data?.handles) ? nodeObj.data.handles : null;
    const mapPos = (pos) => {
      if (ang === VERTICAL_ORIENTATION) return pos;
      switch (pos) {
        case Position.Top: return Position.Left;
        case Position.Bottom: return Position.Right;
        case Position.Left: return Position.Top;
        case Position.Right: return Position.Bottom;
        default: return pos;
      }
    };
    const pickPercent = (val, total) => {
      if (typeof val === 'number') return Math.max(0, Math.min(1, total ? val / total : 0));
      const s = String(val || '').trim();
      if (s.endsWith('%')) {
        const p = parseFloat(s.slice(0, -1));
        if (!isNaN(p)) return Math.max(0, Math.min(1, p / 100));
      }
      return 0.5;
    };
    if (list && list.length > 0) {
      const customAnchors = computeCustomHandleAnchors(nodeObj, gridGapPx);
      if (customAnchors && customAnchors.length > 0) {
        return customAnchors;
      }
      list.forEach((hCfg, idx) => {
        const mapped = mapPos(hCfg.position);
        const style = hCfg?.style || {};
        const frac = (hCfg.position === Position.Top || hCfg.position === Position.Bottom)
          ? pickPercent(style.left, w)
          : pickPercent(style.top, h);
        let hx = x, hy = y;
        switch (mapped) {
          case Position.Top: hx = x + w * frac; hy = y; break;
          case Position.Bottom: hx = x + w * frac; hy = y + h; break;
          case Position.Left: hx = x; hy = y + h * frac; break;
          case Position.Right: hx = x + w; hy = y + h * frac; break;
          default: break;
        }
        roleList.push({ role: (hCfg.type || '').toLowerCase(), id: hCfg.id || `${idx}`, x: hx, y: hy });
      });
      return roleList;
    }
    // Default: Bottom source / Top target (vertical) or Right/Left (horizontal)
    if (ang === VERTICAL_ORIENTATION) {
      roleList.push({ role: 'target', id: 'target', x: x + w / 2, y });
      roleList.push({ role: 'source', id: 'source', x: x + w / 2, y: y + h });
    } else {
      roleList.push({ role: 'target', id: 'target', x, y: y + h / 2 });
      roleList.push({ role: 'source', id: 'source', x: x + w, y: y + h / 2 });
    }
    return roleList;
  }

  // Auto-connect if any source/target handles overlap (within epsilon)
  const tryAutoConnect = useCallback((movedNodeId, movedPos) => {
    try {
      if (busDragAutoConnectSuppressedRef.current) {
        return;
      }
      const rf = reactFlowInstance;
      const nodesRF = rf?.getNodes?.() || [];
      const moved = normalizeNodeForGeometry(nodesRF.find((n) => n.id === movedNodeId), movedPos);
      if (!moved) return;
      if (moved?.type === 'busNode') {
        return;
      }
      const movedNode = moved;
      const movedHandles = enumerateHandles(movedNode);
      if (!movedHandles || movedHandles.length === 0) return;
      const others = nodesRF
        .filter((n) => n.id !== movedNodeId)
        .map((n) => normalizeNodeForGeometry(n));
      const eps = Math.max(4, Math.floor((gridGapPx || 25) / 3));
      const edgesRF = rf?.getEdges?.() || [];
      const makeKey = (e) => `${e.source}|${e.sourceHandle || ''}|${e.target}|${e.targetHandle || ''}`;
      const existing = new Set(edgesRF.map(makeKey));
      const high = new Set();

      // helper to check if a specific handle is already connected by any edge
      const isHandleConnected = (nodeId, role, handleId) => {
        if (role === 'source') {
          return edgesRF.some(e => e.source === nodeId && (
            (handleId && e.sourceHandle === handleId) || (!handleId && !e.sourceHandle)
          ));
        } else {
          return edgesRF.some(e => e.target === nodeId && (
            (handleId && e.targetHandle === handleId) || (!handleId && !e.targetHandle)
          ));
        }
      };

      const connectedOtherIds = new Set();

      for (const other of others) {
        if (connectedOtherIds.has(other.id)) continue;
        const oHandles = enumerateHandles(other);
        if (!oHandles || oHandles.length === 0) continue;
        let connectedThisOther = false;
        for (const a of movedHandles) {
          if (connectedThisOther) break;
          for (const b of oHandles) {
            if (connectedThisOther) break;
            // source->target or target->source only
            let src = null, tgt = null;
            if (a.role === 'source' && b.role === 'target') { src = { node: movedNode, h: a }; tgt = { node: other, h: b }; }
            else if (a.role === 'target' && b.role === 'source') { src = { node: other, h: b }; tgt = { node: movedNode, h: a }; }
            else continue;
            const dx = Math.abs(a.x - b.x);
            const dy = Math.abs(a.y - b.y);
            if (dx <= eps && dy <= eps) {
              // mark highlight while overlapping, but skip handles that are already connected
              const id1 = src.h.id || src.h.role || 'source';
              const id2 = tgt.h.id || tgt.h.role || 'target';
              if (!isHandleConnected(src.node.id, 'source', id1)) high.add(`${src.node.id}|${id1}`);
              if (!isHandleConnected(tgt.node.id, 'target', id2)) high.add(`${tgt.node.id}|${id2}`);
              const params = {
                source: src.node.id,
                target: tgt.node.id,
                sourceHandle: src.h.id && src.h.id !== 'source' ? src.h.id : undefined,
                targetHandle: tgt.h.id && tgt.h.id !== 'target' ? tgt.h.id : undefined,
              };
              const key = `${params.source}|${params.sourceHandle || ''}|${params.target}|${params.targetHandle || ''}`;
              if (existing.has(key)) continue;
              // Create edge (orthogonal)
              const newEdge = {
                ...params,
                type: 'orthogonalEdge',
                className: 'edge-color',
                data: {
                  waypoints: [],
                  disableInspector: true,
                }
              };
              setEdges((eds) => addEdge(newEdge, eds));
              existing.add(key);
              connectedOtherIds.add(other.id);
              connectedThisOther = true;
            }
          }
        }
      }
      // Also highlight moved handles that overlap any edge path
      try {
        const nodesById = new Map(nodesRF.map((n) => [n.id, normalizeNodeForGeometry(n)]));
        const epsEdge = Math.max(4, Math.floor((gridGapPx || 25) / 3));
        const nearEdge = (pt, e) => {
          const src = nodesById.get(e.source);
          const tgt = nodesById.get(e.target);
          if (!src || !tgt) return false;
          const sx = (src.positionAbsolute?.x ?? src.position?.x ?? 0) + ((src.width ?? 0) / 2);
          const sy = (src.positionAbsolute?.y ?? src.position?.y ?? 0) + ((src.height ?? 0) / 2);
          const tx = (tgt.positionAbsolute?.x ?? tgt.position?.x ?? 0) + ((tgt.width ?? 0) / 2);
          const ty = (tgt.positionAbsolute?.y ?? tgt.position?.y ?? 0) + ((tgt.height ?? 0) / 2);
          const wps = (e?.data?.waypoints || []).map((p) => ({ x: p.x, y: p.y }));
          const pts = [ { x: sx, y: sy }, ...wps, { x: tx, y: ty } ];
          for (let i = 0; i < pts.length - 1; i++) {
            if (pointSegDist(pt, pts[i], pts[i + 1]) <= epsEdge) return true;
          }
          return false;
        };
        for (const a of movedHandles) {
          const pt = { x: a.x, y: a.y };
          for (const e of edgesRF) {
            if (nearEdge(pt, e)) {
              // skip highlighting if that handle is already connected
              const hid = a.id || a.role;
              const role = (a.role || '').toLowerCase() === 'target' ? 'target' : 'source';
              if (!isHandleConnected(movedNode.id, role, hid)) {
                const k = `${movedNode.id}|${hid}`;
                high.add(k);
              }
              break;
            }
          }
        }
      } catch {}
      // apply highlight set for this frame
      dispatch(setHighlightHandles(Array.from(high)));
    } catch {}
  }, [reactFlowInstance, setEdges, gridGapPx, dispatch, normalizeNodeForGeometry]);

  const onNodeDrag = useCallback((evt, node) => {
    if (!node || !node.position) return;
    if (isDesignOnlyNodeEntry(node)) {
      const nextPosition = { x: node.position.x, y: node.position.y };
      applySelectedTemplateBlockDrag(node, nextPosition);
      setNodes((nds) =>
        Array.isArray(nds)
          ? nds.map((n) => (
              n.id === node.id
                ? { ...n, position: nextPosition }
                : n
            ))
          : nds
      );
      return;
    }
    let aligned = alignNodeByHandle(node);
    let lockEntry = nodeDragLockRef.current[node.id];
    if (!lockEntry) {
      const selectedNodeIds = Array.isArray(currentSelectedNodesRef.current)
        && currentSelectedNodesRef.current.includes(node.id)
          ? currentSelectedNodesRef.current
          : [node.id];
      lockEntry = {
        originAligned: aligned,
        originRaw: { x: node.position.x, y: node.position.y },
        axis: null,
        anchorAligned: aligned,
        allowAutoConnect: !isBusNodeEntry(node) && selectedNodeIds.length === 1,
      };
      nodeDragLockRef.current[node.id] = lockEntry;
    } else {
      lockEntry.axis = null;
      lockEntry.originAligned = aligned;
      lockEntry.originRaw = { x: node.position.x, y: node.position.y };
      lockEntry.anchorAligned = aligned;
    }
    const shook = detectShakeForNode(node.id, aligned);
    if (shook) {
      disconnectEdgesForNode(node.id);
    }
    if (isBusNodeEntry(node)) {
      const appliedCascade = applyBusCascadeTranslation(node, aligned);
      if (!appliedCascade) {
        setNodes((nds) =>
          nds.map((n) => (n.id === node.id ? { ...n, position: aligned } : n))
        );
      }
    } else {
      setNodes((nds) =>
        nds.map((n) => (n.id === node.id ? { ...n, position: aligned } : n))
      );
    }
    applySelectedTemplateBlockDrag(node, aligned);
    if (lockEntry.allowAutoConnect) {
      tryAutoConnect(node.id, aligned);
    }
  }, [
    alignNodeByHandle,
    detectShakeForNode,
    disconnectEdgesForNode,
    setNodes,
    tryAutoConnect,
    isBusNodeEntry,
    isDesignOnlyNodeEntry,
    applyBusCascadeTranslation,
    applySelectedTemplateBlockDrag,
  ]);

  useEffect(() => {
    if (!autoConnectRequest?.nodeId) return;
    const nodeId = autoConnectRequest.nodeId;
    try {
      if (busDragAutoConnectSuppressedRef.current) {
        return;
      }
      const rfNode = reactFlowInstance?.getNode?.(nodeId);
      const basePos = rfNode?.position || null;
      tryAutoConnect(nodeId, basePos);
    } finally {
      dispatch(clearAutoConnectRequest());
    }
  }, [autoConnectRequest, reactFlowInstance, tryAutoConnect, dispatch]);

  // Geometry helpers for point-to-edge proximity
  const getEdgePolyline = useCallback((edge, nodesById) => {
    const src = nodesById.get(edge.source);
    const tgt = nodesById.get(edge.target);
    if (!src || !tgt) return null;
    const grid = Math.max(5, Math.min(200, Number(gridGapPx) || 25));

    const anchorFor = (node, role, handleId) => {
      if (!node) return null;
      const x = (node.positionAbsolute?.x ?? node.position?.x ?? 0);
      const y = (node.positionAbsolute?.y ?? node.position?.y ?? 0);
      const w = node.width || 0;
      const h = node.height || 0;
      const ang = typeof node?.data?.angle === 'number' ? node.data.angle : VERTICAL_ORIENTATION;
      // bus node uses our unified spacing & count
      if (node.type === 'busNode') {
        const layout = getBusLayout(node, grid);
        const anchor = getBusHandleAnchor(node, layout, role === 'source' ? 'source' : 'target', handleId);
        if (!anchor) return null;
        const leadDistance = Math.max(10, layout.thickness + 6);
        if (layout.horizontal) {
          const delta = role === 'source' ? leadDistance : -leadDistance;
          return {
            base: anchor,
            lead: { x: anchor.x, y: anchor.y + delta },
          };
        }
        const delta = role === 'source' ? leadDistance : -leadDistance;
        return {
          base: anchor,
          lead: { x: anchor.x + delta, y: anchor.y },
        };
      }
      // custom/default nodes: center anchors by side
      const vertical = ang === VERTICAL_ORIENTATION;
      if (vertical) {
        return role === 'source' ? { base: { x: x + w / 2, y: y + h } } : { base: { x: x + w / 2, y } };
      }
      return role === 'source' ? { base: { x: x + w, y: y + h / 2 } } : { base: { x, y: y + h / 2 } };
    };

    const s = anchorFor(src, 'source', edge.sourceHandle);
    const t = anchorFor(tgt, 'target', edge.targetHandle);
    const wps = (edge?.data?.waypoints || []).map((p) => ({ x: p.x, y: p.y }));
    const hasManualRoute = wps.length > 0;
    const pts = [];
    if (s?.base) pts.push(s.base);
    if (s?.lead && !hasManualRoute) pts.push(s.lead);
    pts.push(...wps);
    if (t?.lead && !hasManualRoute) pts.push(t.lead);
    if (t?.base) pts.push(t.base);
    return pts;
  }, [gridGapPx]);

  const pointSegDist = (p, a, b) => {
    // axis-aligned segments for ortho, but handle general case
    const vx = b.x - a.x, vy = b.y - a.y;
    const wx = p.x - a.x, wy = p.y - a.y;
    const c1 = vx * wx + vy * wy;
    if (c1 <= 0) return Math.hypot(p.x - a.x, p.y - a.y);
    const c2 = vx * vx + vy * vy;
    if (c2 <= c1) return Math.hypot(p.x - b.x, p.y - b.y);
    const t = c1 / c2;
    const proj = { x: a.x + t * vx, y: a.y + t * vy };
    return Math.hypot(p.x - proj.x, p.y - proj.y);
  };

  // On drop, if both handles of a node overlap the same edge, split that edge inserting the node
  const tryInsertNodeIntoEdgeOnDrop = useCallback((nodeId) => {
    const rf = reactFlowInstance;
    const nodesRF = rf?.getNodes?.() || [];
    const edgesRF = rf?.getEdges?.() || [];
    const moved = normalizeNodeForGeometry(nodesRF.find((n) => n.id === nodeId));
    if (!moved) return false;
    const handles = enumerateHandles(moved);
    if (!handles || handles.length === 0) return false;
    const byRole = handles.reduce((acc, h) => {
      acc[h.role] = acc[h.role] || [];
      acc[h.role].push(h);
      return acc;
    }, {});
    const sourceList = byRole['source'] || [];
    const targetList = byRole['target'] || [];
    if (sourceList.length === 0 || targetList.length === 0) return false;
    const baseGap = Math.max(5, Math.min(200, Number(gridGapPx) || 25));
    const epsTight = Math.max(6, Math.floor(baseGap / 2));
    const epsLoose = Math.max(epsTight + 4, Math.floor(baseGap * 1.5));
    const nodesById = new Map(nodesRF.map((n) => [n.id, normalizeNodeForGeometry(n)]));
    let chosen = null; // { edge, srcHandle, tgtHandle }
    for (const e of edgesRF) {
      if (e.source === nodeId || e.target === nodeId) continue;
      const poly = getEdgePolyline(e, nodesById);
      if (!poly || poly.length < 2) continue;
      const segmentDetails = (pt, a, b) => {
        const vx = b.x - a.x;
        const vy = b.y - a.y;
        const wx = pt.x - a.x;
        const wy = pt.y - a.y;
        const c1 = vx * wx + vy * wy;
        if (c1 <= 0) {
          return { distance: Math.hypot(pt.x - a.x, pt.y - a.y) };
        }
        const c2 = vx * vx + vy * vy;
        if (c2 <= c1) {
          return { distance: Math.hypot(pt.x - b.x, pt.y - b.y) };
        }
        const t = c1 / c2;
        const projX = a.x + t * vx;
        const projY = a.y + t * vy;
        return { distance: Math.hypot(pt.x - projX, pt.y - projY) };
      };
      const analyzeHandle = (handle) => {
        if (!handle) return null;
        let bestDist = Infinity;
        let bestSeg = -1;
        for (let i = 0; i < poly.length - 1; i++) {
          const info = segmentDetails(handle, poly[i], poly[i + 1]);
          if (info.distance < bestDist) {
            bestDist = info.distance;
            bestSeg = i;
          }
        }
        if (!Number.isFinite(bestDist)) return null;
        return { handle, distance: bestDist, segment: bestSeg };
      };
      const srcCandidates = sourceList.map(analyzeHandle).filter(Boolean);
      const tgtCandidates = targetList.map(analyzeHandle).filter(Boolean);
      if (srcCandidates.length === 0 || tgtCandidates.length === 0) continue;
      const combos = [];
      for (const s of srcCandidates) {
        for (const t of tgtCandidates) {
          const maxDist = Math.max(s.distance, t.distance);
          if (!Number.isFinite(maxDist) || maxDist > epsLoose) continue;
          const sameSegment = s.segment === t.segment && s.segment !== -1;
          const tightMatch = s.distance <= epsTight && t.distance <= epsTight;
          const segmentPenalty = sameSegment ? 0 : epsTight;
          const tightPenalty = tightMatch ? 0 : epsTight;
          const score = maxDist + segmentPenalty + tightPenalty;
          combos.push({ s, t, score, sameSegment, tightMatch });
        }
      }
      if (combos.length === 0) continue;
      combos.sort((a, b) => {
        if (a.sameSegment !== b.sameSegment) return a.sameSegment ? -1 : 1;
        if (a.tightMatch !== b.tightMatch) return a.tightMatch ? -1 : 1;
        return a.score - b.score;
      });
      const best = combos[0];
      if (best) {
        chosen = { edge: e, srcHandle: best.s.handle, tgtHandle: best.t.handle };
        break;
      }
    }
    if (!chosen) return false;
    const srcHandleIdRaw = chosen.srcHandle?.id;
    const tgtHandleIdRaw = chosen.tgtHandle?.id;
    const normalizedSrcId = srcHandleIdRaw && srcHandleIdRaw !== 'source' ? srcHandleIdRaw : undefined;
    const normalizedTgtId = tgtHandleIdRaw && tgtHandleIdRaw !== 'target' ? tgtHandleIdRaw : undefined;
    const e = chosen.edge;
    // Split: e.source -> node (target handle), node -> e.target (source handle)
    setEdges((eds) => {
      const next = Array.isArray(eds) ? eds.filter((x) => x.id !== e.id) : eds;
      const mk = (src, srcH, tgt, tgtH) => ({
        id: `edge_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
        type: e.type || 'orthogonalEdge',
        className: e.className,
        source: src,
        target: tgt,
        sourceHandle: srcH || undefined,
        targetHandle: tgtH || undefined,
        data: { ...(e.data || {}), waypoints: [] },
      });
      const e1 = mk(e.source, e.sourceHandle, nodeId, normalizedTgtId);
      const e2 = mk(nodeId, normalizedSrcId, e.target, e.targetHandle);
      return [...next, e1, e2];
    });
    return true;
  }, [enumerateHandles, reactFlowInstance, setEdges, gridGapPx, getEdgePolyline, normalizeNodeForGeometry]);

  // Center viewport on first tile when editor is empty.
  // On first empty mount, also set zoom so the first tile fits the viewport.
  useEffect(() => {
    try {
      const isEmpty = (currentNodes?.length || 0) === 0 && (currentEdges?.length || 0) === 0;
      const wasEmpty = prevEmptyRef.current;
      prevEmptyRef.current = isEmpty;
      const dims = PAGE_SIZE_DIMENSIONS[pageSize] || PAGE_SIZE_DIMENSIONS[PAGE_SIZE_A4];
      const cx = (dims?.width || 0) / 2;
      const cy = (dims?.height || 0) / 2;
      const wrapperW = reactFlowWrapper.current?.clientWidth || 0;
      const wrapperH = reactFlowWrapper.current?.clientHeight || 0;

      if (isEmpty && !initialViewSetRef.current && wrapperW && wrapperH) {
        // First-time empty canvas: fit the first tile on screen (zoom out to see it)
        const fitZoom = Math.max(0.02, Math.min(2.5, Math.min(wrapperW / (dims.width || 1), wrapperH / (dims.height || 1)) * 0.9));
        const x = -(cx * fitZoom) + wrapperW / 2;
        const y = -(cy * fitZoom) + wrapperH / 2;
        if (typeof reactFlowInstance.setViewport === 'function') {
          reactFlowInstance.setViewport({ x, y, zoom: fitZoom });
          initialViewSetRef.current = true;
        }
        return;
      }

      if (isEmpty && !userMovedRef.current && wasEmpty !== true) {
        const dims = PAGE_SIZE_DIMENSIONS[pageSize] || PAGE_SIZE_DIMENSIONS[PAGE_SIZE_A4];
        const cx = (dims?.width || 0) / 2;
        const cy = (dims?.height || 0) / 2;
        // Keep current zoom and center instantly
        const z = viewTransform?.zoom || 1;
        const x = -(cx * z) + wrapperW / 2;
        const y = -(cy * z) + wrapperH / 2;
        if (typeof reactFlowInstance.setViewport === 'function') {
          reactFlowInstance.setViewport({ x, y, zoom: z });
        }
      }
    } catch (_) {}
  }, [currentNodes, currentEdges, pageSize, reactFlowInstance, viewTransform, reactFlowWrapper]);

  useEffect(() => {
    try {
      if (!reactFlowWrapper.current) return;
      const wrapperW = reactFlowWrapper.current?.clientWidth || 0;
      const wrapperH = reactFlowWrapper.current?.clientHeight || 0;
      if (!wrapperW || !wrapperH) return;

      const dims = PAGE_SIZE_DIMENSIONS[pageSize] || PAGE_SIZE_DIMENSIONS[PAGE_SIZE_A4];
      const tileWidth = dims?.width || 0;
      const tileHeight = dims?.height || 0;
      if (!tileWidth || !tileHeight) return;

      const baseZoom = Math.min(wrapperW / tileWidth, wrapperH / tileHeight);
      const fitZoom = Math.max(0.02, Math.min(2.5, baseZoom * 0.9));

      const cx = tileWidth / 2;
      const cy = tileHeight / 2;
      const x = -(cx * fitZoom) + wrapperW / 2;
      const y = -(cy * fitZoom) + wrapperH / 2;

      if (typeof reactFlowInstance?.setViewport === 'function') {
        reactFlowInstance.setViewport({ x, y, zoom: fitZoom });
        lastFitForPageRef.current = { pageSize, fitZoom };
      }
    } catch (_) {}
  }, [pageSize, reactFlowInstance]);

  // After a diagram finishes loading and nodes are present for the first time,
  // fit the view to all nodes (zoomed out a bit) without animation.
  useEffect(() => {
    try {
      if (diagramLoading) return;
      if (initialContentFitRef.current) return;
      if (userMovedRef.current) return;
      if (!reactFlowWrapper.current) return;
      const rfNodes = reactFlowInstance.getNodes ? reactFlowInstance.getNodes() : [];
      if (!rfNodes || rfNodes.length === 0) return;
      // Compute bounds from measured nodes
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      rfNodes.forEach((n) => {
        const x = n?.position?.x || 0;
        const y = n?.position?.y || 0;
        const w = n?.width || 250;
        const h = n?.height || 250;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + w);
        maxY = Math.max(maxY, y + h);
      });
      if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) return;
      const boundsW = Math.max(1, maxX - minX);
      const boundsH = Math.max(1, maxY - minY);
      const wrapperW = reactFlowWrapper.current.clientWidth || 1;
      const wrapperH = reactFlowWrapper.current.clientHeight || 1;
      // Fit to bounds and zoom out slightly to give breathing room
      const fit = Math.min(wrapperW / boundsW, wrapperH / boundsH);
      const targetZoom = Math.max(0.02, Math.min(2.5, fit * 0.9));
      const cx = minX + boundsW / 2;
      const cy = minY + boundsH / 2;
      const x = -(cx * targetZoom) + wrapperW / 2;
      const y = -(cy * targetZoom) + wrapperH / 2;
      if (typeof reactFlowInstance.setViewport === 'function') {
        reactFlowInstance.setViewport({ x, y, zoom: targetZoom });
        initialContentFitRef.current = true;
      }
    } catch (_) {}
  }, [diagramLoading, currentNodes, currentEdges, reactFlowInstance]);

  // Legacy onConnectEnd replaced by enhanced one above

  // Autosave on changes
  useEffect(() => {
    if (!historyApplying && !isDraggingRef.current) {
      debouncedSaveHistory();
    }
  }, [currentNodes, currentEdges, debouncedSaveHistory, historyApplying]);

// Auto Layout Function placing nodes left-to-right based on dependency depth
const handleAutoLayout = () => {
  if (!currentNodes || !currentEdges) return;

  const incoming = {};
  currentEdges.forEach((e) => {
    if (!incoming[e.target]) incoming[e.target] = [];
    incoming[e.target].push(e.source);
  });

  const levelMap = {};
  const queue = [];
  currentNodes.forEach((n) => {
    if (!incoming[n.id]) {
      levelMap[n.id] = 0;
      queue.push(n.id);
    }
  });

  while (queue.length) {
    const id = queue.shift();
    const level = levelMap[id];
    currentEdges.forEach((e) => {
      if (e.source === id) {
        const next = level + 1;
        if (levelMap[e.target] === undefined || next > levelMap[e.target]) {
          levelMap[e.target] = next;
          queue.push(e.target);
        }
      }
    });
  }

  const byLevel = {};
  currentNodes.forEach((n) => {
    const lvl = levelMap[n.id] || 0;
    if (!byLevel[lvl]) byLevel[lvl] = [];
    byLevel[lvl].push(n);
  });

  const hSpacing = 300;
  const vSpacing = 200;
  const layoutedNodes = currentNodes.map((node) => {
    const lvl = levelMap[node.id] || 0;
    const index = byLevel[lvl].indexOf(node);
    return {
      ...node,
      position: { x: lvl * hSpacing, y: index * vSpacing },
    };
  });

  dispatch(setNodesActionCreator([...layoutedNodes]));
};

  const actionButtons = useMemo(() => {
    if (!hasNodeSelection) return [];
    if (overlayIsMulti) {
      const actions = [
        {
          key: 'align-column',
          label: 'Alinear columna',
          onClick: handleAlignVertical,
          icon: AlignHorizontalIcon,
          tone: 'subtle',
          tooltip: 'Alinear columna',
        },
        {
          key: 'align-row',
          label: 'Alinear fila',
          onClick: handleAlignHorizontal,
          icon: AlignVerticalIcon,
          tone: 'subtle',
          tooltip: 'Alinear fila',
        },
      ];
      if (canRotateSelection) {
        actions.push({
          key: 'rotate',
          label: 'Rotar',
          onClick: rotateSelectedNodes,
          iconSrc: ROTATE_ICON_SRC,
          tone: 'subtle',
          tooltip: 'Rotar selección',
          tintIcon: true,
        });
      }
      actions.push({
        key: 'out-of-service',
        label: selectedNodesOutOfService ? 'Poner en servicio' : 'Fuera de servicio',
        onClick: handleToggleOutOfService,
        iconSrc: OUT_SERVICE_ICON_SRC,
        tone: 'subtle',
        tooltip: selectedNodesOutOfService ? 'Poner en servicio' : 'Fuera de servicio',
        tintIcon: true,
        isActive: selectedNodesOutOfService,
      });
      actions.push({
        key: 'clone',
        label: 'Clonar',
        onClick: cloneSelection,
        iconSrc: CLONE_ICON_SRC,
        tone: 'subtle',
        tooltip: 'Clonar selección',
        tintIcon: true,
      });
      actions.push({
        key: 'delete',
        label: 'Eliminar',
        onClick: handleDeleteSelected,
        iconSrc: '/assets/ico-eliminar.png',
        tone: 'outline',
        isDestructive: true,
        tooltip: 'Eliminar',
      });
      return actions;
    }

    const actions = [];
    if (canRotateToolbarNode) {
      actions.push({
        key: 'rotate',
        label: 'Rotar',
        onClick: rotateSingleNode,
        iconSrc: ROTATE_ICON_SRC,
        tone: 'subtle',
        tooltip: 'Rotar',
        tintIcon: true,
      });
    }
    if (canStartTextPositionEdit) {
      actions.push({
        key: 'text-position',
        label: 'Mover texto',
        onClick: handleStartTextPositionEdit,
        iconSrc: MOVE_TEXT_ICON_SRC,
        tone: 'subtle',
        tooltip: 'Mover texto',
        tintIcon: true,
        isActive: isTextPositionEditing,
      });
    }
    if (!isBoxToolbar) {
      actions.push({
        key: 'out-of-service',
        label: selectedNodesOutOfService ? 'Poner en servicio' : 'Fuera de servicio',
        onClick: handleToggleOutOfService,
        iconSrc: OUT_SERVICE_ICON_SRC,
        tone: 'subtle',
        tooltip: selectedNodesOutOfService ? 'Poner en servicio' : 'Fuera de servicio',
        tintIcon: true,
        isActive: selectedNodesOutOfService,
      });
    }
    if (isBoxToolbar) {
      actions.push({
        key: 'lock-box',
        label: isBoxLocked ? 'Desfijar caja' : 'Fijar caja',
        onClick: handleToggleBoxLocked,
        icon: () => <LockIcon locked={isBoxLocked} />,
        tone: 'subtle',
        tooltip: isBoxLocked ? 'Desfijar caja' : 'Fijar caja',
        isActive: isBoxLocked,
      });
    }
    actions.push({
      key: 'clone',
      label: 'Clonar',
      onClick: cloneSelection,
      iconSrc: CLONE_ICON_SRC,
      tone: 'subtle',
      tooltip: 'Clonar',
      tintIcon: true,
    });
    actions.push({
      key: 'delete',
      label: 'Eliminar',
      onClick: handleDeleteSelected,
      iconSrc: '/assets/ico-eliminar.png',
      tone: 'outline',
      isDestructive: true,
      tooltip: 'Eliminar',
    });
    return actions;
  }, [
    hasNodeSelection,
    overlayIsMulti,
    handleAlignVertical,
    handleAlignHorizontal,
    rotateSelectedNodes,
    cloneSelection,
    handleDeleteSelected,
    canRotateSelection,
    canRotateToolbarNode,
    isBoxToolbar,
    isBoxLocked,
    canStartTextPositionEdit,
    handleStartTextPositionEdit,
    handleToggleBoxLocked,
    handleToggleOutOfService,
    rotateSingleNode,
    isTextPositionEditing,
    selectedNodesOutOfService,
  ]);

  const edgeActionButtons = useMemo(() => {
    if (!hasEdgeSelection) return [];
    return [
      {
        key: 'delete-edge',
        label: 'Eliminar',
        onClick: handleDeleteSelected,
        iconSrc: '/assets/ico-eliminar.png',
        tone: 'outline',
        isDestructive: true,
        tooltip: 'Eliminar',
      },
    ];
  }, [hasEdgeSelection, handleDeleteSelected]);

  const spawnPreviewPosition = useMemo(() => {
    if (!spawnPreview || !spawnPreview.data) return null;
    if (!lastMouseFlow) return null;
    const { screenX, screenY } = lastMouseFlow;
    if (typeof screenX !== 'number' || typeof screenY !== 'number') return null;
    return {
      left: screenX + 12,
      top: screenY + 12,
    };
  }, [spawnPreview, lastMouseFlow]);

  const boxPreview = useMemo(() => {
    if (!boxTool?.active) {
      return null;
    }
    if (!boxTool.anchor) {
      if (!lastMouseFlow || typeof lastMouseFlow.screenX !== 'number' || typeof lastMouseFlow.screenY !== 'number') {
        return null;
      }
      if (boxTool?.preset?.table) {
        const placement = resolveBoxPresetPlacement(
          { x: lastMouseFlow.x, y: lastMouseFlow.y },
          boxTool.preset,
        );
        if (!placement) {
          return null;
        }
        const wrapperRect = reactFlowWrapper.current?.getBoundingClientRect();
        const { x: tx = 0, y: ty = 0, zoom = 1 } = viewTransform || {};
        const left = placement.position.x * zoom + tx;
        const top = placement.position.y * zoom + ty;
        return {
          mode: 'preset',
          left: wrapperRect ? wrapperRect.left + left : left,
          top: wrapperRect ? wrapperRect.top + top : top,
          width: placement.size.width * zoom,
          height: placement.size.height * zoom,
        };
      }
      return {
        mode: 'cursor',
        left: lastMouseFlow.screenX + 14,
        top: lastMouseFlow.screenY + 14,
        size: 28,
      };
    }
    const pointerFlow = lastMouseFlow
      ? { x: lastMouseFlow.x, y: lastMouseFlow.y }
      : boxTool.anchor;
    const placement = resolveBoxPlacement(boxTool.anchor, pointerFlow, isBoxShiftPressed);
    if (!placement) {
      return null;
    }
    const wrapperRect = reactFlowWrapper.current?.getBoundingClientRect();
    const { x: tx = 0, y: ty = 0, zoom = 1 } = viewTransform || {};
    const toScreen = (point) => {
      const baseX = point.x * zoom + tx;
      const baseY = point.y * zoom + ty;
      if (wrapperRect) {
        return {
          x: wrapperRect.left + baseX,
          y: wrapperRect.top + baseY,
        };
      }
      return {
        x: baseX,
        y: baseY,
      };
    };
    const topLeft = toScreen({ x: placement.position.x, y: placement.position.y });
    const bottomRight = toScreen({
      x: placement.position.x + placement.size.width,
      y: placement.position.y + placement.size.height,
    });
    return {
      mode: 'marquee',
      left: Math.min(topLeft.x, bottomRight.x),
      top: Math.min(topLeft.y, bottomRight.y),
      width: Math.abs(bottomRight.x - topLeft.x),
      height: Math.abs(bottomRight.y - topLeft.y),
    };
  }, [boxTool, lastMouseFlow, resolveBoxPlacement, resolveBoxPresetPlacement, isBoxShiftPressed, viewTransform]);

  const straightLinePreview = useMemo(() => {
    if (!straightLineTool?.active) {
      return null;
    }
    if (!straightLineTool.anchor) {
      if (!lastMouseFlow || typeof lastMouseFlow.screenX !== 'number' || typeof lastMouseFlow.screenY !== 'number') {
        return null;
      }
      return {
        mode: 'cursor',
        left: lastMouseFlow.screenX + 14,
        top: lastMouseFlow.screenY + 14,
        color: straightLineTool.color || STRAIGHT_LINE_DEFAULT_COLOR,
        strokeWidth: Number(straightLineTool.strokeWidth) || STRAIGHT_LINE_DEFAULT_STROKE,
      };
    }
    const pointerFlow = lastMouseFlow
      ? { x: lastMouseFlow.x, y: lastMouseFlow.y }
      : straightLineTool.anchor;
    const placement = resolveStraightLinePlacement(straightLineTool.anchor, pointerFlow, isBoxShiftPressed);
    if (!placement) {
      return null;
    }
    const wrapperRect = reactFlowWrapper.current?.getBoundingClientRect();
    const { x: tx = 0, y: ty = 0, zoom = 1 } = viewTransform || {};
    const toScreen = (point) => {
      const baseX = point.x * zoom + tx;
      const baseY = point.y * zoom + ty;
      if (wrapperRect) {
        return {
          x: wrapperRect.left + baseX,
          y: wrapperRect.top + baseY,
        };
      }
      return {
        x: baseX,
        y: baseY,
      };
    };
    const start = toScreen(placement.anchor);
    const end = toScreen(placement.end);
    return {
      mode: 'line',
      left: Math.min(start.x, end.x) - STRAIGHT_LINE_PADDING,
      top: Math.min(start.y, end.y) - STRAIGHT_LINE_PADDING,
      width: Math.max(1, Math.abs(end.x - start.x) + STRAIGHT_LINE_PADDING * 2),
      height: Math.max(1, Math.abs(end.y - start.y) + STRAIGHT_LINE_PADDING * 2),
      start: {
        x: start.x - Math.min(start.x, end.x) + STRAIGHT_LINE_PADDING,
        y: start.y - Math.min(start.y, end.y) + STRAIGHT_LINE_PADDING,
      },
      end: {
        x: end.x - Math.min(start.x, end.x) + STRAIGHT_LINE_PADDING,
        y: end.y - Math.min(start.y, end.y) + STRAIGHT_LINE_PADDING,
      },
      color: straightLineTool.color || STRAIGHT_LINE_DEFAULT_COLOR,
      strokeWidth: Number(straightLineTool.strokeWidth) || STRAIGHT_LINE_DEFAULT_STROKE,
    };
  }, [straightLineTool, lastMouseFlow, resolveStraightLinePlacement, isBoxShiftPressed, viewTransform]);

  const symbolDrawerPreview = useMemo(() => {
    if (!symbolDrawerTool?.active) {
      return null;
    }
    if (!lastMouseFlow || typeof lastMouseFlow.screenX !== 'number' || typeof lastMouseFlow.screenY !== 'number') {
      return null;
    }
    return {
      left: lastMouseFlow.screenX + 14,
      top: lastMouseFlow.screenY + 14,
      width: 150,
      height: 92,
    };
  }, [symbolDrawerTool?.active, lastMouseFlow]);

  const nodeToolbarPortal = useMemo(() => {
    if (!showNodeToolbar) return null;
    if (isBoxToolbar || isStraightLineToolbar || isSymbolDrawerToolbar || hasInspectorlessDesignSelection) return null;
    const mount = typeof document !== 'undefined' ? document.getElementById('menu-bar-overlay-root') : null;
    if (!mount) return null;
    const overlayClassName = overlaySymbolSlug
      ? `node-selection-overlay node-selection-overlay--symbol-${overlaySymbolSlug}`
      : 'node-selection-overlay';

    const renderTextBoxColorControls = () => {
      if (!isTextBoxToolbar) return null;
      return (
        <div className="node-selection-overlay__section node-selection-overlay__section--colors node-selection-overlay__section--textbox-colors">
          
          <div className="node-selection-overlay__textbox-color-field">
            <div className="node-selection-overlay__textbox-color-label">Texto</div>
            <input
              type="color"
              className="node-selection-overlay__textbox-color-input"
              value={textBoxTextHex}
              onChange={(e) => handleTextBoxColorInput('textColor', e.target.value)}
              aria-label="Color del texto"
            />
          </div>

          <div className="node-selection-overlay__textbox-color-field">
            <div className="node-selection-overlay__textbox-color-label">Fondo</div>
            <input
              type="color"
              className="node-selection-overlay__textbox-color-input"
              value={textBoxBackgroundHex}
              onChange={(e) => handleTextBoxColorInput('backgroundColor', e.target.value)}
              aria-label="Color de fondo"
            />
          </div>
         


          <div className="node-selection-overlay__textbox-alpha">
            <button
              type="button"
              className={`node-selection-overlay__textbox-alpha-toggle${isTextBoxBackgroundTransparent ? ' is-inverse' : ''}`}
              onClick={handleToggleTextBoxBackground}
              aria-pressed={isTextBoxBackgroundTransparent}
            >
              {isTextBoxBackgroundTransparent ? 'Con fondo' : 'Sin fondo'}
            </button>
          </div>
        </div>
      );
    };

    const renderTextControls = () => {
      if (!supportsTextSize) return null;
      const AlignIcon = ({ variant }) => {
        switch (variant) {
          case 'center':
            return (
              <svg className="node-selection-overlay__text-align-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <line x1="4" y1="5" x2="16" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="7" y1="10" x2="13" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="5" y1="15" x2="15" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            );
          case 'right':
            return (
              <svg className="node-selection-overlay__text-align-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <line x1="6" y1="5" x2="16" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="9" y1="10" x2="16" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="8" y1="15" x2="16" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            );
          case 'left':
          default:
            return (
              <svg className="node-selection-overlay__text-align-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                <line x1="4" y1="5" x2="14" y2="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="4" y1="10" x2="11" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="4" y1="15" x2="14" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            );
        }
      };

      const textSizeSection = (
        <div className="node-selection-overlay__section node-selection-overlay__section--text">
          {TEXT_SIZE_PRESETS.map((preset) => {
            const isActive = activeTextSizeKey === preset.key;
            return (
              <button
                key={preset.key}
                type="button"
                className={`node-selection-overlay__circle-btn node-selection-overlay__circle-btn--text${isActive ? ' is-active' : ''}`}
                onClick={() => handleApplyTextSizePreset(preset.key)}
                aria-pressed={isActive}
                aria-label={preset.label}
                data-tooltip={preset.label}
                data-tooltip-position="bottom"
              >
                <span className="node-selection-overlay__text-letter" style={{ fontSize: preset.sizeIcon }}>
                  A
                </span>
              </button>
            );
          })}
        </div>
      );

      if (!isTextBoxToolbar) {
        return textSizeSection;
      }

      return (
        <>
          {textSizeSection}
          <div className="node-selection-overlay__section node-selection-overlay__section--text-bold">
            <button
              type="button"
              className={`node-selection-overlay__circle-btn node-selection-overlay__circle-btn--text${textBoxBold ? ' is-active' : ''}`}
              onClick={handleToggleTextBoxBold}
              aria-pressed={textBoxBold}
              aria-label="Bold"
              data-tooltip="Bold"
              data-tooltip-position="bottom"
            >
              <BoldIcon />
            </button>
          </div>
          <div className="node-selection-overlay__section node-selection-overlay__section--text-align">
            <div className="node-selection-overlay__align-group" role="group" aria-label="Alineación de texto">
              {[
                { key: 'left', label: 'Izquierda' },
                { key: 'center', label: 'Centro' },
                { key: 'right', label: 'Derecha' },
              ].map((option) => {
                const isActive = textBoxAlign === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    className={`node-selection-overlay__circle-btn node-selection-overlay__circle-btn--text${isActive ? ' is-active' : ''}`}
                    onClick={() => handleTextBoxAlignChange(option.key)}
                    aria-pressed={isActive}
                    aria-label={`Alinear ${option.label}`}
                    data-tooltip={`Alinear ${option.label}`}
                    data-tooltip-position="bottom"
                  >
                    <AlignIcon variant={option.key} />
                  </button>
                );
              })}
            </div>
          </div>
        </>
      );
    };

    const renderColorControls = () => {
      if (isTextBoxToolbar) {
        return renderTextBoxColorControls();
      }
      const hasTargets = selectedNodes.length > 0 || selectedEdges.length > 0;
      if (!hasTargets) return null;
      const edgeActionsAvailable = edgeActionButtons.length > 0;
      const colorsClass = `node-selection-overlay__section node-selection-overlay__section--colors${hasEdgeSelectionOnly && !edgeActionsAvailable ? ' is-solo' : ''}`;
      return (
        <div className={colorsClass}>
          <div className="node-selection-overlay__color-group" role="group" aria-label="Colores">
            {COLOR_PRESETS.map((preset) => {
              const isActive = activeColorHex === preset.normalized;
              const chipBorder = preset.isLight ? 'rgba(17, 24, 39, 0.32)' : 'rgba(255, 255, 255, 0.8)';
              return (
                <button
                  key={preset.key}
                  type="button"
                  className={`node-selection-overlay__circle-btn node-selection-overlay__circle-btn--color${preset.isLight ? ' is-light' : ''}${isActive ? ' is-active' : ''}`}
                  onClick={() => handleApplyAccentColor(preset.hex)}
                  aria-pressed={isActive}
                  aria-label={`Color ${preset.label}`}
                  data-tooltip={`Color ${preset.label}`}
                  data-tooltip-position="bottom"
                >
                  <span
                    className="node-selection-overlay__color-chip"
                    style={{ backgroundColor: preset.hex, borderColor: chipBorder }}
                  />
                  {isActive ? (
                    <span className="node-selection-overlay__color-check" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M5 10.5l3 3 7-7"
                          stroke={preset.isLight ? '#111827' : '#ffffff'}
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      );
    };

    const renderActions = () => {
      if (!hasNodeSelection || !actionButtons.length) return null;
      return (
        <div className="node-selection-overlay__section node-selection-overlay__section--actions">
          {actionButtons.map((action) => {
            const toneClass = action.tone ? ` node-selection-overlay__circle-btn--${action.tone}` : ' node-selection-overlay__circle-btn--solid';
            const destructiveClass = action.isDestructive ? ' is-destructive' : '';
            const IconComponent = action.icon;
            const activeClass = action.isActive ? ' is-active' : '';
            return (
              <button
                key={action.key}
                type="button"
                className={`node-selection-overlay__circle-btn node-selection-overlay__circle-btn--action${toneClass}${destructiveClass}${activeClass}`}
                onClick={action.onClick}
                title={action.label}
                aria-label={action.label}
                disabled={action.disabled}
                aria-pressed={typeof action.isActive === 'boolean' ? action.isActive : undefined}
                data-tooltip={action.tooltip || undefined}
                data-tooltip-position={action.tooltip ? 'bottom' : action.tooltipPosition || undefined}
              >
                {action.iconSrc ? (
                  <ActionIconImage src={action.iconSrc} tint={action.tintIcon} />
                ) : IconComponent ? (
                  <IconComponent />
                ) : null}
              </button>
            );
          })}
        </div>
      );
    };

    const renderEdgeActions = () => {
      if (!hasEdgeSelectionOnly || !edgeActionButtons.length) return null;
      return (
        <div className="node-selection-overlay__section node-selection-overlay__section--actions">
          {edgeActionButtons.map((action) => {
            const toneClass = action.tone ? ` node-selection-overlay__circle-btn--${action.tone}` : ' node-selection-overlay__circle-btn--solid';
            const destructiveClass = action.isDestructive ? ' is-destructive' : '';
            const IconComponent = action.icon;
            return (
              <button
                key={action.key}
                type="button"
                className={`node-selection-overlay__circle-btn node-selection-overlay__circle-btn--action${toneClass}${destructiveClass}`}
                onClick={action.onClick}
                title={action.label}
                aria-label={action.label}
                disabled={action.disabled}
                data-tooltip={action.tooltip || undefined}
                data-tooltip-position={action.tooltip ? 'bottom' : action.tooltipPosition || undefined}
              >
                {action.iconSrc ? (
                  <ActionIconImage src={action.iconSrc} tint={action.tintIcon} />
                ) : IconComponent ? (
                  <IconComponent />
                ) : null}
              </button>
            );
          })}
        </div>
      );
    };

    const renderOverlayBody = () => {
      if (isTextPositionEditing) {
        return (
          <div className="node-selection-overlay__section node-selection-overlay__section--hint">
            <div className="node-selection-overlay__hint-text">Presione enter para dejar de editar la posición</div>
          </div>
        );
      }

      if (hasEdgeSelectionOnly) {
        return (
          <>
            {renderColorControls()}
            {renderEdgeActions()}
          </>
        );
      }

      return (
        <>
          {renderTextControls()}
          {renderColorControls()}
          {renderActions()}
        </>
      );
    };

    return createPortal(
      <div className={overlayClassName} data-selection-symbol={overlaySymbolLabel || undefined}>
        <div className="node-selection-overlay__section node-selection-overlay__section--label">
          <div className="node-selection-overlay__label-group">
            <button
              type="button"
              className="node-selection-overlay__dismiss"
              onClick={clearAllSelection}
            >
              Volver
            </button>
            <div className="node-selection-overlay__label">{overlayLabel}</div>

          </div>
        </div>
        {renderOverlayBody()}
      </div>,
      mount
    );
  }, [
    showNodeToolbar,
    overlayLabel,
    overlaySymbolLabel,
    overlaySymbolSlug,
    isTextBoxToolbar,
    isBoxToolbar,
    isStraightLineToolbar,
    hasInspectorlessDesignSelection,
    isBusToolbar,
    isTextPositionEditing,
    actionButtons,
    clearAllSelection,
    handleApplyAccentColor,
    activeColorHex,
    selectedNodes,
    selectedEdges,
    hasNodeSelection,
    hasEdgeSelectionOnly,
    textBoxBackgroundHex,
    textBoxTextHex,
    textBoxAlphaPercent,
    isTextBoxBackgroundTransparent,
    handleTextBoxColorInput,
    handleTextBoxAlphaChange,
    handleToggleTextBoxBackground,
    handleToggleTextBoxBold,
    textBoxBold,
    textBoxAlign,
    handleTextBoxAlignChange,
    edgeActionButtons,
    supportsTextSize,
    activeTextSizeKey,
    handleApplyTextSizePreset,
    isSymbolDrawerToolbar,
  ]);

  const boxPreviewPortal =
    boxPreview
      ? createPortal(
          boxPreview.mode === 'cursor' ? (
            <div
              className="box-tool-preview box-tool-preview--cursor"
              style={{
                position: 'fixed',
                left: boxPreview.left,
                top: boxPreview.top,
                width: boxPreview.size,
                height: boxPreview.size,
              }}
            >
              <img
                className="box-tool-preview__icon"
                src="/assets/ico-createsquare.svg"
                alt=""
                draggable="false"
              />
            </div>
          ) : (
            <div
              className={`box-tool-preview box-tool-preview--${boxPreview.mode === 'preset' ? 'preset' : 'marquee'}`}
              style={{
                position: 'fixed',
                left: boxPreview.left,
                top: boxPreview.top,
                width: Math.max(BOX_MIN_SIZE, boxPreview.width),
                height: Math.max(BOX_MIN_SIZE, boxPreview.height),
              }}
            />
          ),
          document.body
        )
      : null;

  const straightLinePreviewPortal =
    straightLinePreview
      ? createPortal(
          straightLinePreview.mode === 'cursor' ? (
            <div
              className="straight-line-tool-preview straight-line-tool-preview--cursor"
              style={{
                position: 'fixed',
                left: straightLinePreview.left,
                top: straightLinePreview.top,
                color: straightLinePreview.color,
              }}
            >
              <span className="straight-line-tool-preview__cursor-line" />
            </div>
          ) : (
            <svg
              className="straight-line-tool-preview straight-line-tool-preview--line"
              style={{
                position: 'fixed',
                left: straightLinePreview.left,
                top: straightLinePreview.top,
                width: straightLinePreview.width,
                height: straightLinePreview.height,
              }}
              width={straightLinePreview.width}
              height={straightLinePreview.height}
            >
              <line
                x1={straightLinePreview.start.x}
                y1={straightLinePreview.start.y}
                x2={straightLinePreview.end.x}
                y2={straightLinePreview.end.y}
                stroke={straightLinePreview.color}
                strokeWidth={straightLinePreview.strokeWidth}
                strokeLinecap="round"
                strokeDasharray="8 6"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          ),
          document.body
        )
      : null;

  const symbolDrawerPreviewPortal =
    symbolDrawerPreview
      ? createPortal(
          <div
            className="symbol-drawer-tool-preview"
            style={{
              position: 'fixed',
              left: symbolDrawerPreview.left,
              top: symbolDrawerPreview.top,
              width: symbolDrawerPreview.width,
              height: symbolDrawerPreview.height,
            }}
          >
            <div className="symbol-drawer-tool-preview__title" />
            <div className="symbol-drawer-tool-preview__grid">
              <span />
              <span />
              <span />
              <span />
            </div>
          </div>,
          document.body
        )
      : null;

  const spawnPreviewPortal =
    spawnPreview && spawnPreview.data && spawnPreviewPosition
      ? createPortal(
          <div
            className="spawn-preview-overlay"
            style={{
              position: 'fixed',
              left: spawnPreviewPosition.left,
              top: spawnPreviewPosition.top,
              pointerEvents: 'none',
              zIndex: 2000,
            }}
          >
            {spawnPreview.data.svgSrc ? (
              <img
                src={spawnPreview.data.svgSrc}
                alt=""
                draggable="false"
                style={{
                  width: 24,
                  height: 24,
                  objectFit: 'contain',
                  opacity: 0.85,
                  transform: spawnPreview.data.isRotable
                    ? `rotate(${spawnPreview.data.angle || 0}deg)`
                    : undefined,
                }}
              />
            ) : (
              <div
                style={{
                  width: 15,
                  height: 15,
                  borderRadius: '50%',
                  background: 'rgba(45, 152, 240, 0.2)',
                  border: '1px solid rgba(45, 152, 240, 0.65)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 8,
                  fontWeight: 700,
                  color: '#2563eb',
                }}
              >
                <span aria-hidden="true">T</span>
              </div>
            )}
          </div>,
          document.body
        )
      : null;

  const nodeContextMenuPortal =
    nodeContextMenu
      ? createPortal(
          <div
            className="diagram-node-context-menu"
            style={{
              position: 'fixed',
              left: nodeContextMenu.left,
              top: nodeContextMenu.top,
            }}
            role="menu"
            aria-label={nodeContextMenu.isMultiSelection ? 'Acciones de la selección' : 'Acciones del símbolo'}
          >
            {nodeContextMenu.actions.map((action) => (
              action.type === 'separator' ? (
                <div
                  key={action.key}
                  className="diagram-node-context-menu__separator"
                  role="separator"
                />
              ) : (
                <button
                  key={action.key}
                  type="button"
                  className="diagram-node-context-menu__item"
                  role="menuitem"
                  onClick={() => {
                    closeNodeContextMenu();
                    action.onClick();
                  }}
                >
                  {action.label}
                </button>
              )
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {nodeToolbarPortal}
      {boxPreviewPortal}
      {straightLinePreviewPortal}
      {symbolDrawerPreviewPortal}
      {spawnPreviewPortal}
      {nodeContextMenuPortal}
        <div
          ref={reactFlowWrapper}
          className={`diagram-canvas${handlesHidden ? ' handles-hidden' : ''}${isMarqueeSelecting ? ' is-marquee-selecting' : ''}`}
          style={{ width: '100%', height: '100%', position: 'relative' }}
          onClickCapture={handleCanvasClickCapture}
          onContextMenuCapture={handleCanvasContextMenuCapture}
        >
        {diagramLoading && (
          <div className="diagram-loading-overlay" role="status" aria-live="polite">
            <SoloLineLoader />
          </div>
        )}
        <ReactFlow
          nodes={renderedNodes}
          nodeTypes={nodeTypes}
          edges={currentEdges}
          edgeTypes={edgeTypes}
          deleteKeyCode={['Backspace', 'Delete']}
          // UX: left mouse selects/box-select; middle mouse pans; wheel zooms
          panOnDrag={[1]}
          selectionOnDrag
          selectionKeyCode={null}
          multiSelectionKeyCode={['Shift']}
          selectionMode="partial"
          panOnScroll={false}
          zoomOnScroll={true}
          zoomOnPinch={true}
          zoomOnDoubleClick={false}
          onNodesChange={handleNodesChange}
          onNodesDelete={onNodesDelete}
          onEdgesChange={handleEdgesChange}
          onEdgesDelete={onEdgesDelete}
          onEdgeClick={onEdgeClick}
          onConnect={onConnect}
          isValidConnection={isConnectionAllowed}
          connectionMode={ConnectionMode.Loose}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onPaneClick={onPaneClick}
          onPaneContextMenu={handlePaneContextMenu}
          onNodeClick={onNodeClick}
          onNodeContextMenu={handleNodeContextMenu}
          
          onSelectionStart={onSelectionStart}
          onSelectionDrag={onSelectionDrag}
          onSelectionDragStop={onSelectionDragStop}
          onSelectionEnd={onSelectionEnd}
          onNodeDragStart={onNodeDragStart}
          onNodeDrag={onNodeDrag}
          onSelectionChange={({ nodes: selNodes = [], edges: selEdges = [] }) => {
            const blockedNodeIds = isSpacePressedRef.current
              ? selNodes.filter((node) => node?.type === 'boxNode').map((node) => node.id)
              : [];
            const nodeIds = selNodes
              .filter((node) => !blockedNodeIds.includes(node.id))
              .map((n) => n.id);
            const edgeIds = selEdges.map((e) => e.id);
            const arrEq = (a = [], b = []) => {
              if (a.length !== b.length) return false;
              const sb = new Set(b);
              for (const id of a) if (!sb.has(id)) return false;
              return true;
            };

            // Skip selection changes during squelch window triggered by inspector edits
            if (Date.now() < selectionSquelchUntilRef.current || Date.now() < (edgeSquelchUntil || 0)) {
              return;
            }

            const activeEl = document.activeElement;
            const insideInspector = !!activeEl && activeEl.closest && activeEl.closest('.operation__sidebar, .ctn-inspector');
            if (insideInspector) {
              // Ignore RF selection updates while interacting inside inspector
              return;
            }
            if (skipSelectionOnceRef.current) {
              skipSelectionOnceRef.current = false;
              return;
            }
            if (blockedNodeIds.length > 0) {
              const allowedNodeSet = new Set(nodeIds);
              try {
                reactFlowInstance?.setNodes?.((nds) =>
                  Array.isArray(nds)
                    ? nds.map((node) => (
                        blockedNodeIds.includes(node.id) || node.selected !== allowedNodeSet.has(node.id)
                          ? { ...node, selected: allowedNodeSet.has(node.id) }
                          : node
                      ))
                    : nds
                );
              } catch {}
              setNodes((nds) =>
                Array.isArray(nds)
                  ? nds.map((node) => (
                      blockedNodeIds.includes(node.id) || node.selected !== allowedNodeSet.has(node.id)
                        ? { ...node, selected: allowedNodeSet.has(node.id) }
                        : node
                    ))
                  : nds
              );
            }
            const last = lastSelectionRef.current;
            const sameNodes = arrEq(nodeIds, last.nodeIds);
            const sameEdges = arrEq(edgeIds, last.edgeIds);

            if (!sameNodes || !sameEdges) {
              // Briefly suppress re-selection after pane clear without dispatching (avoid loops)
              if (Date.now() < ignoreSelectionUntilRef.current) {
                return;
              }
              // If the pane explicitly triggered a clear, just ignore this callback
              if (paneClearedRef.current) {
                paneClearedRef.current = false;
                return;
              }

              lastSelectionRef.current = { nodeIds, edgeIds };
              if (!sameNodes) {
                dispatch(setSelectedNodeIds(nodeIds));
              }
              if (!sameEdges) {
                //dispatch(setSelectedEdgeIds(edgeIds));
              }
              selectionSquelchUntilRef.current = Date.now() + 50;
            }
          }}
          onNodeDragStop={onNodeDragStop}
          snapToGrid={false}
          connectOnClick={connectOnClickEnabled}
          connectionLineType="step"
          connectionLineStyle={drawConn.active ? { opacity: 0 } : { stroke: '#52C41A', strokeWidth: 2 }}
          snapGrid={[GRID_GAP, GRID_GAP]}
          onMove={(evt, transform) => { userMovedRef.current = true; setViewTransform(transform); }}
          minZoom={0.02}      // allow zooming out further
          maxZoom={2}       // allow zooming in further
          // fitView is disabled to prevent unexpected re-centering on updates
        >
        <PageGridOverlay transform={viewTransform} />
        <TemplateAnchorOverlay
          transform={viewTransform}
          isSpacePressedRef={isSpacePressedRef}
          isSpacePressed={isSpacePressed}
          selectedBlockIds={selectedTemplateBlockIds}
          onTextContextMenu={handleTemplateTextContextMenu}
          onImageContextMenu={handleTemplateImageContextMenu}
        />
        <EdgeCrossPageOverlay transform={viewTransform} />
        {drawConn.active && (
          <DrawPreview src={drawConn.src} waypoints={drawConn.waypoints} mouse={drawConn.mouse} hover={drawConn.hover} />
        )}
        {/* Custom connection preview; React Flow click-to-connect is disabled to avoid stale handle state. */}

      {/*<button onClick={handleAutoLayout} style={{ position: 'absolute', top: 10, right: 10, zIndex: 10 }}>
        Auto Layout
      </button>*/}

              <Background
                  id="1"
                  gap={GRID_GAP}
                  color="#8484842f"
                  variant={BackgroundVariant.Lines}
                  
                />
          

        

      </ReactFlow>
        </div>
    </>
  );
}

export default Diagram;

function DrawPreview({ src, waypoints = [], mouse, hover }) {
  const [viewportEl, setViewportEl] = useState(null);
  useEffect(() => {
    setViewportEl(document.querySelector('.react-flow__viewport'));
  }, []);
  if (!src || !mouse || !viewportEl) return null;
  const pts = [src.base, ...waypoints, mouse];
  const srcRole = typeof src?.role === 'string' ? src.role.toLowerCase() : '';
  const hoverRole = typeof hover?.type === 'string' ? hover.type.toLowerCase() : '';
  const invalidCombo = srcRole && hoverRole && srcRole === hoverRole;
  const strokeColor = invalidCombo ? '#FF4D4F' : '#52C41A';
  const strokeWidthPx = 7;
  const toPath = (arr) => {
    if (!arr || arr.length === 0) return '';
    const [f, ...rest] = arr;
    const cmds = [`M ${f.x} ${f.y}`];
    rest.forEach((p) => cmds.push(`L ${p.x} ${p.y}`));
    return cmds.join(' ');
  };
  const overlay = (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
      <svg style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}>
        <path d={toPath(pts)} fill="none" stroke={strokeColor} strokeWidth={strokeWidthPx} strokeDasharray="6 6" />
        {pts.map((p, i) => (<circle key={i} cx={p.x} cy={p.y} r={3} fill={strokeColor} />))}
      </svg>
      {/* Hint tooltip near mouse */}
      <div
        style={{
          position: 'absolute',
          left: mouse.x + 40,
          top: mouse.y + 12,
          background: 'rgba(0, 0, 0, 0.73)',
          color: '#fff',
          fontSize: 20,
          padding: '4px 6px',
          borderRadius: 4,
          whiteSpace: 'nowrap'
        }}
      >
        𒊹(Esc) Cancelar   𒊹(Shift) Línea recta
      </div>
    </div>
  );
  return createPortal(overlay, viewportEl);
}

function SoloLineLoader() {
  return (
    <svg
      width="320"
      height="100"
      viewBox="0 0 320 100"
      role="img"
      aria-label="Loading SoloLine"
      xmlns="http://www.w3.org/2000/svg"
      className="diagram-loading-svg"
    >
      <style>{`
        :root {
          --brand: #ffffff;
          --bg-soft: #2C8ED611;
        }
        text {
          font: 700 20px/1 "Lato", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          fill: var(--brand);
          text-anchor: middle;
        }
        .loader-subtitle {
          font: 400 16px/1.3 "Lato", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          fill: #ffffffdd;
        }
        @keyframes spinLoader {
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .loader-ring { animation: none !important; }
        }
      `}</style>

      <g transform="translate(160,45)">
        <g transform="translate(0,-5)">
          <circle cx="0" cy="0" r="22" stroke="#ffffff33" strokeWidth="6" fill="none" />
          <circle
            className="loader-ring"
            cx="0"
            cy="0"
            r="22"
            stroke="#ffffff"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray="110"
            strokeDashoffset="80"
            fill="none"
            style={{ animation: 'spinLoader 1.1s linear infinite' }}
          />
        </g>
        <text x="0" y="40" fill="#ffffff">SoloLine</text>
        <g className="loader-messages" transform="translate(0,70)">
          <text className="loader-subtitle" x="0" y="0" textAnchor="middle">
            Revisa los atajos para crear rápidamente tus diagramas
            <animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.3;0.33;1" dur="9s" repeatCount="indefinite" />
            <animateTransform attributeName="transform" type="translate" values="0 0;0 -5;0 -5;0 0" keyTimes="0;0.3;0.33;1" dur="9s" repeatCount="indefinite" />
          </text>
          <text className="loader-subtitle" x="0" y="0" textAnchor="middle">
            SoloLine tiene canal de Youtube con tutoriales
            <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.33;0.34;0.63;0.66;1" dur="9s" repeatCount="indefinite" />
            <animateTransform attributeName="transform" type="translate" values="0 5;0 5;0 0;0 0;0 -5;0 -5" keyTimes="0;0.33;0.34;0.63;0.66;1" dur="9s" repeatCount="indefinite" />
          </text>
          <text className="loader-subtitle" x="0" y="0" textAnchor="middle">
            Doble enter para agregar rápidamente nuevos componentes
            <animate attributeName="opacity" values="0;0;0;1;1;0" keyTimes="0;0.66;0.67;0.84;0.95;1" dur="9s" repeatCount="indefinite" />
            <animateTransform attributeName="transform" type="translate" values="0 5;0 5;0 5;0 0;0 0;0 -5" keyTimes="0;0.66;0.67;0.84;0.95;1" dur="9s" repeatCount="indefinite" />
          </text>
        </g>
      </g>
    </svg>
  );
}
